import type { AcceptedDocument } from '../legal/editions';
import type { RtcProxyConfig } from './rtc/rtcProxy';
import {
  PracticeDirection,
  PracticeRecommendationsResponse,
  User,
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  ApiError,
  MasterProfile,
  MasterProfileUpdateRequest,
  PublicMasterProfile,
  ContentItem,
  UpcomingLive,
  PastLive,
  VideoPractice,
  VideoPracticeDetail,
  VideoPracticeWithMaster,
  SubscriptionState,
  FavoriteState,
  MasterLive,
  MasterVideo,
  CreateLiveRequest,
  UpdateLiveRequest,
  CreateVideoRequest,
  UpdateVideoRequest,
  MasterSubscriptionState,
  MasterSubscription,
  PushSubscriptionInfo,
  RegisterPushSubscriptionRequest,
  UserPreferencesRequest,
  UserPreferencesResponse,
  PreferenceOptionsResponse,
  StartingSoonResponse,
  DailyRhythmResponse,
  CurrentLiveResponse,
  LiveDetailResponse,
  LivePreviewTokenResponse,
  LivePreviewTokenInfo,
  AgoraTokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PasswordResetResponse,
  PaginatedResponse,
  UpcomingLiveItem,
  VideoPracticeItem,
  RecordingItem,
  MasterWithLiveNow,
  CalendarEventsResponse,
  FeaturedLive,
  RecordingStatusResponse,
  WatchedStateResponse,
  WatchedItemResponse,
  MasterPost,
  PostComment,
  PostReactionsResponse,
  CreatePostRequest,
  UpdatePostRequest,
  CreateCommentRequest,
  ManifestoVideo,
  ManifestoVideoRequest,
  AgapeVideo,
  AgapeVideoRequest,
  LikeResponse,
  MasterEvent,
  CreateEventRequest,
  UpdateEventRequest,
  UnifiedEvent,
  EventListSource,
  CalendarEventDto,
  EventRegistrationRequest,
  SendEventMessageRequest,
  NotificationSettings,
  DmConversationUserView,
  DmConversationMasterView,
  DmMessagesResponse,
  CreateConversationResponse,
  MastersOnlineResponse,
  SupportedLanguage,
  TranslationInfo,
  TranslationResponse,
  LiveTranslationInfo,
  LiveTranslationResponse,
  ChatContactsResponse,
  ChatAiCompanionDto,
  ChatMasterConfigDto,
  UserFriendDto,
  SubtitleResponse,
  CreateRoomRequest,
  RoomResponse,
  RoomRtcToken,
  RtcTokenIntent,
  RoomRosterSnapshotResponse,
  RoomScreenShareStartResponse,
  SpectateRoomResponse,
  RoomPlaybackState,
  RoomRecordingChange,
  RoomMediaChange,
  RoomBackgroundAudioSnapshot,
  RoomMediaType,
  SoundTrack,
  UnsplashImage,
  CalendarItem,
  CalendarBadgeResponse,
  CalendarDot,
  RoomStats,
  SessionEndingResponse,
  SessionEndingParticipantsResponse,
  SessionRatingResponse,
  SessionRatingValue,
  PublicUsersListResponse,
  PublicUserSummary,
  PublicUserProfile,
  PublicUserPostsPage,
  LiveStatsResponse,
  LiveModerator,
  PublicUserLivesResponse,
  RoomRecordingStatusResponse,
  RecordingDownloadResponse,
  WatchPartyVisibility,
  RoomMessageResponse,
  LiveChatAttachment,
  LiveChatAttachmentPolicy,
  DmConversationView,
  DmConversationSummary,
  FeedResponse,
  FeedItemKind,
  SavedListResponse,
  PracticeCompletionResponse,
  MemoryFactDto,
  CircleMemberDto,
  CircleBanDto,
  MasterCircleSummary,
  CommunityChatMessagePreview,
  ChatDiscussion,
  ChatDiscussionDetails,
  DiscussionGroup,
  DiscussionReadResult,
  ChatOnlineUsersResponse,
  ChatChannelMember,
  HomeFeedResponse,
  UpcomingSessionsResponse,
  HomeCollection,
  HomeCollectionItem,
  GamificationAchievementsResponse,
  GamificationProfile,
  PublicGamificationSummary,
  GamificationLevelState,
  GamificationXpEntry,
  GamificationEvents,
  GamificationShareCard,
  GamificationShareType,
  PublicGamificationShareCard,
  LevelBadge,
  InterestProfileResponse,
  LiveCapabilitiesResponse,
  SpectateLiveResponse,
  UpdateRoomRequest,
  RepostTarget,
} from '../types/auth';
import { storage } from '../utils/storage';
import { jwtNeedsRefresh } from '../utils/jwtExpiry';
import { markRefreshRejected, noteUnauthorized, SESSION_EXPIRY_PROMPT_ENABLED } from '../utils/sessionExpiry';
import { isCapabilityRefusal } from '../utils/capabilityRefusal';

/** CORE-1683 — страница истории канала чата с курсорами (`MessagePageDto`). */
export interface ChatMessagesPage {
  items: Record<string, any>[];
  /** Курсор страницы старше; null — старше ничего нет. */
  nextCursor: string | null;
  hasMore: boolean;
  newerCursor: string | null;
  hasNewer: boolean;
  /** Корень обсуждения, которому принадлежит страница (у реплики обсуждения). */
  threadRootId: string | null;
  /**
   * CORE-620 (возврат QA 30.10) — запрос не удался: нет связи, ответ не 2xx,
   * тело не читается. Страница пустая, как и раньше, но это «не знаем», а не
   * «истории больше нет»: листание повторяют, а не заканчивают.
   */
  failed?: boolean;
}
import { ChatMessage } from '../types/chat';
import type {
  ReferralIdentity, ReferralSummary, ReferralFunnel, ReferralAccrual, ReferralPayout, ReferralPoints,
  ReferralVisitResponse,
} from '../types/referral';
import type { PayoutDetailsTicket } from '../types/taxContour';
import { OnboardingTourDto } from '../types/onboardingTour';
import type {
  JournalEntry, JournalEntryDraft, JournalEntryUpdate, JournalPrivacy, JournalPrompts, ReflectionOffer,
} from '../types/auth';
import { API_BASE_URL as BASE_URL, ABSOLUTE_API_URL } from './apiUrl';
import { activeEnv, buildEnv, type EnvChoice } from '../config/envSwitch';
import { resolveApiUrl, ruFallbackUrl } from './apiFallback';
import { AUTH_CORRELATION_HEADER } from './authCorrelation';
import { retryOnGateway } from './gatewayRetry';
import { uploadFormWithProgress } from './formUploadWithProgress';
import { POST_IMAGE_TYPES } from './uploadLimits';
import { preparePostPhoto } from './imageConvert';
import { CHAT_HUB_BASE_URL } from '../constants/chat';
import { publishRewardReactions } from '../utils/rewardReactionsBus';
import type { ChatAttachmentDescriptor } from './chat/streamUpload';
import type { MentionCandidate } from '../utils/chatMentions';
import {
  parseAttachmentPolicy,
  type ChatAttachmentPolicy,
  type CommunityAttachmentUpload,
} from './chat/communityAttachments';
import {
  filterAvailableMasterProfileDirections,
  parseMasterProfileDirections,
  type MasterProfileDirection,
} from '../constants/masterProfileDirections';
import {
  normalizeMasterProfilePath,
  parseMasterProfilePath,
  type MasterProfilePathEntry,
} from '../constants/masterProfilePath';
import {
  parseMasterProfileSocialLinks,
  type MasterProfileSocialLink,
} from '../constants/masterProfileSocialLinks';
import { ChunkUploadError, UploadError, AuthRequiredError, PhotoFormatError } from './errorMessages';
import {
  trackUpload,
  trackUploadError,
  newUploadSessionId,
  type UploadContext,
} from './uploadTelemetry';
import {
  planMultipartParts,
  shouldUseMultipart,
} from './multipartUpload';
import {
  clearResumableUpload,
  expiredUploads,
  findResumable,
  loadResumableUploads,
  missingParts,
  partByteLength,
  saveResumableUpload,
  uploadFingerprint,
  type ResumableUpload,
} from './multipartResume';

// Web origin per environment, used to build share / deep links. Keyed like
// `S3_DIRECT_URL` below: which env is decided by the address rather than a lone
// `EXPO_PUBLIC_WEB_URL` that a build profile can forget and silently fall back
// to prod (CORE-926).
export const WEB_ORIGIN: Record<EnvChoice, string> = {
  prod: 'https://agapella.io',
  dev: 'https://rn-agape.vercel.app',
  // CORE-1507 — reached only through `activeEnv()` on a native build switched
  // to the stand; `buildEnv()` never answers 'staging'.
  staging: 'https://app.ag1.tmless.org',
};

// A build may still pin an explicit origin (staging → app.ag1.tmless.org, the
// e2e profile → rn-agape.vercel.app); honour it. Otherwise derive from the
// build's API env instead of hard-coding prod.
export const WEB_URL = process.env.EXPO_PUBLIC_WEB_URL || WEB_ORIGIN[buildEnv()];
// Proxy S3 files through Vercel rewrites to avoid CORS issues.
// On web, use /s3-files/ path (same origin). On native, use direct S3 URLs.
import { Platform } from 'react-native';
import { purchaseRequestBody } from '../screens/subscription/agapellaSubscription';
import { parseRetryAfterMs } from '../utils/rateLimitRetry';
import { RECORDING_NOTICE_VERSION } from '../screens/live-session/entry/recordingNotice';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from './idempotencyKey';
import type { ApiRequestInfo } from '../utils/apiErrorIdentity';

// CORE-1999 — every `fetch` in this module goes through this shadow, which only
// remembers which request produced a Response. `handleResponse` sees just the
// Response (no method), and GlitchTip needs «method + path + status» to group
// an HTTP error instead of opening a new issue per traceId. The global is
// looked up at call time, so tests that replace `global.fetch` keep working.
const requestOfResponse = new WeakMap<object, ApiRequestInfo>();
const fetch: typeof globalThis.fetch = async (input, init) => {
  const response = await globalThis.fetch(input, init);
  if (response && typeof response === 'object') {
    const req = input as { url?: string; method?: string };
    requestOfResponse.set(response, {
      method: (init?.method ?? (typeof input === 'object' ? req.method : undefined) ?? 'GET').toUpperCase(),
      url: typeof input === 'string' ? input : req.url ?? String(input),
    });
  }
  return response;
};

// Vercel preview URLs (`<project>-git-<branch>-<scope>.vercel.app`) sit behind
// deployment protection — unauthenticated visitors get blocked with a blank
// screen. Share links generated on a preview must therefore use the canonical
// brand domain so guests can actually open them. Localhost and the production
// host fall through to `window.location.origin` so internal dev/staging links
// keep working.
//
// CORE-534 — in prod a shared link is addressed to share.agapella.io, not to the
// site itself. agapella.io answers from Moscow, and Telegram's link-preview
// crawler cannot reach a Russian data centre (07.10.2026: zero crawler requests
// in the edge log, telegram.org times out from the box), so every link on the
// brand domain arrived in a chat as bare text. The share host lives outside
// Russia: a crawler gets the page with its OG tags, a person gets
// `302 -> agapella.io/<same path and query>`.
export const SHARE_ORIGIN_PROD = 'https://share.agapella.io';

export function toShareOrigin(origin: string): string {
  return /^https:\/\/([a-z0-9-]+\.)?agapella\.io$/i.test(origin) ? SHARE_ORIGIN_PROD : origin;
}

export function getShareOrigin(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (/-git-.*\.vercel\.app$/i.test(origin)) return toShareOrigin(WEB_URL);
    return toShareOrigin(origin);
  }
  // Native has no address bar, so pick the origin for the env actually in
  // effect — the build's baked env OR the runtime override from the env
  // switcher (CORE-413). A lone WEB_URL stayed on prod after a tester flipped a
  // dev build to the dev backend, so share links pointed at prod (CORE-926).
  return toShareOrigin(WEB_ORIGIN[activeEnv()]);
}
const S3_DIRECT_URL = ABSOLUTE_API_URL.includes('agapella.io')
  ? 'https://agape-files-prod.s3.us-east-1.amazonaws.com'
  : 'https://agape-files-dev.s3.us-east-1.amazonaws.com';
const S3_FILES_URL = Platform.OS === 'web' ? '/s3-files' : S3_DIRECT_URL;

/**
 * Convert a file path or URL to a direct S3 URL.
 * Handles: full URLs, relative paths, and object names.
 */
/**
 * CORE-1545 — a live-covers photo through our API. images.unsplash.com does not
 * answer from Russia (empty gallery tiles, a pick hanging 38 s then failing), our
 * API does — and `resolveApiUrl` sends an `<Image>` src to api-ru like any fetch.
 * Only for photos from `getUnsplashLiveCovers`: the backend serves nothing else.
 */
export function unsplashPhotoUrl(id: string, size: 'small' | 'regular'): string {
  return resolveApiUrl(`${BASE_URL}/api/Unsplash/photo/${encodeURIComponent(id)}?size=${size}`);
}

export function getFileUrl(pathOrUrl: string | null | undefined): string | null {
  if (!pathOrUrl) return null;
  // Defensive: a local `require()` asset resolves to a number on native (and a
  // module object on web). Those must be used as an <Image source> directly, so
  // never reach here — but if one slips through, bail instead of crashing on
  // `.startsWith`.
  if (typeof pathOrUrl !== 'string') return null;

  // Already a direct S3 URL — on web we normally proxy through Vercel
  // (`/s3-files/*` rewrite in vercel.json) so cookies/cache play nice with the
  // app domain, but the rewrite only exists on real Vercel hosts. On localhost
  // (Expo dev server) the path 404s, so return the raw S3 URL there.
  if (pathOrUrl.startsWith('https://agape-files-')) {
    if (Platform.OS === 'web') {
      const host = typeof window !== 'undefined' ? window.location?.hostname || '' : '';
      const isVercelHost = host.endsWith('vercel.app') || host.endsWith('agapella.io');
      if (isVercelHost) {
        const url = new URL(pathOrUrl);
        // CORE-1012 — `search` обязателен. Платные материалы выдаются
        // подписанной ссылкой со сроком жизни, а подпись живёт именно в
        // query-строке: собрать адрес из одного `pathname` — значит молча
        // выбросить её и получить 403 вместо файла.
        return `/s3-files${url.pathname}${url.search}`;
      }
      return pathOrUrl;
    }
    return pathOrUrl;
  }

  // URL pointing to our API files endpoint — extract object path and use S3
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    try {
      const url = new URL(pathOrUrl);
      // CORE-537 — a direct S3 URL on a bucket other than agape-files- (e.g.
      // agape-recordings-prod, which holds the 480p/1080p transcodes) must NOT
      // be routed through the `/s3-files` proxy: that rewrite only maps to
      // agape-files, so it would 403 on the wrong bucket. agape-files- URLs are
      // already handled above; serve any other S3 object directly.
      //
      // CORE-805 — same hazard on Yandex Object Storage, which now serves the
      // newest practice masters (…/agape-storage/prod/practice-videos/…). The
      // knownFolders rewrite below matches `practice-videos`, rebuilds the URL
      // against the agape-files bucket AND drops the `prod/` env prefix, so the
      // native player asked S3 for an object that does not exist → 403 → a dead
      // «плей» and 00:00/00:00. Google Play flagged exactly that.
      if (url.hostname.endsWith('.amazonaws.com') || url.hostname.endsWith('.yandexcloud.net')) {
        return pathOrUrl;
      }
      const filesIndex = url.pathname.indexOf('/api/files/');
      if (filesIndex !== -1) {
        const objectPath = url.pathname.substring(filesIndex + '/api/files/'.length);
        return `${S3_FILES_URL}/${objectPath}`;
      }
      // Extract known folder prefixes (master-photos, practice-videos, etc.)
      const knownFolders = ['master-photos', 'practice-videos', 'manifesto-videos', 'user-avatars', 'soundtracks'];
      const pathParts = url.pathname.split('/');
      const folderIndex = pathParts.findIndex(p => knownFolders.includes(p));
      if (folderIndex !== -1) {
        const objectPath = pathParts.slice(folderIndex).join('/');
        return `${S3_FILES_URL}/${objectPath}`;
      }
      return pathOrUrl;
    } catch {
      return pathOrUrl;
    }
  }

  // Relative path or object name (e.g., "master-photos/xxx.jpg")
  const cleanPath = pathOrUrl.startsWith('/') ? pathOrUrl.slice(1) : pathOrUrl;
  // CORE-715 — the value may ALREADY be a proxied path. Several screens resolve
  // an avatar once and hand the result down as a prop whose consumer resolves it
  // again (`masterPhotoUrl` did exactly that), and blindly prepending the prefix
  // a second time produces `/s3-files/s3-files/…`, which the Vercel rewrite does
  // not map — the object is there, the request comes back 403. Strip the prefix
  // and re-attach the platform's own: idempotent on web, and on native (where
  // S3_FILES_URL is the bucket host) it turns the web path into a direct S3 URL
  // instead of a nonsense `https://…amazonaws.com/s3-files/…`.
  const objectPath = cleanPath.startsWith('s3-files/') ? cleanPath.slice('s3-files/'.length) : cleanPath;
  return `${S3_FILES_URL}/${objectPath}`;
}

/**
 * CORE-721 — query string for the two live access grants. `preview` is the
 * co-host token (publisher rights, one holder); `invite` is the audience code
 * that «Только по приглашению» hands out. They're independent: a co-host
 * following an audience link should still be able to present their token.
 */
export function buildLiveAccessQuery(
  previewToken?: string | null,
  inviteCode?: string | null,
): string {
  const parts: string[] = [];
  if (previewToken) parts.push(`preview=${encodeURIComponent(previewToken)}`);
  if (inviteCode) parts.push(`invite=${encodeURIComponent(inviteCode)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Every playable URL for this item, best first.
 *
 * CORE-918 — the preference order below is a *preference*, not a promise that
 * the preferred file exists. When the transcode bucket went dark (S3 answering
 * `403 AllAccessDisabled` for the whole account), `getVideoUrl` kept handing the
 * player a dead 480p URL while a perfectly live original sat unused on another
 * host — cover art loaded, the player stayed at 00:00. Returning the whole list
 * lets the player walk to the next candidate on a media error instead of
 * bottoming out on the first one.
 *
 * The list is ordered, de-duplicated and free of empties, so callers can treat
 * position 0 as «what we'd pick» and the tail as «what to try next».
 *
 * CORE-919 — `playbackUrlMirror` closes the list: the same object on the other
 * cloud, so losing a whole provider costs playback only where no second copy
 * exists. It comes from the API rather than being derived here on purpose. The
 * client cannot know whether a twin was ever written — the confirmation lives in
 * the database — and the derivation is not even uniform: originals mirror to
 * Yandex under the same key, while 480p transcodes sit in a different bucket
 * with no counterpart at all. Guessing would append a guaranteed 404 and spend
 * the player's retry on it.
 *
 * It goes last because it is the survivability option, not the fast one: while
 * both clouds are healthy the preferred transcode still wins.
 */
export function getVideoUrlCandidates(
  item: {
    playbackUrl?: string | null;
    playbackUrl480p?: string | null;
    playbackUrlMirror?: string | null;
  },
): string[] {
  const original = item.playbackUrl;
  const p480 = item.playbackUrl480p;
  // HLS is adaptive — it already picks its own rendition, so it leads and the
  // fixed-size transcode is not an alternative to it.
  const ordered = original && (original.includes('.m3u8') || original.includes('/hls/'))
    ? [original]
    : [p480, original];
  return [...ordered, item.playbackUrlMirror]
    .filter((u): u is string => typeof u === 'string' && u.length > 0)
    .filter((u, i, all) => all.indexOf(u) === i);
}

/**
 * CORE-1033 — which of the candidates is this, in words.
 *
 * Playback telemetry is close to useless without it: «практика не игралась» reads
 * completely differently depending on whether the player was on the preferred
 * 480p rendition, had already fallen back to the master original, or was on the
 * other cloud's copy. The player itself only ever sees a URL.
 *
 * Classify the RAW candidate, never the one `getFileUrl` returned — that one is
 * rewritten per platform and host (`/s3-files/…` on Vercel, RU edge elsewhere),
 * so it stops matching the fields it came from.
 */
export type VideoCandidateKind = 'hls' | 'p480' | 'original' | 'mirror' | 'unknown';

export function videoCandidateKind(
  url: string | null | undefined,
  item: {
    playbackUrl?: string | null;
    playbackUrl480p?: string | null;
    playbackUrlMirror?: string | null;
  },
): VideoCandidateKind {
  if (!url) return 'unknown';
  if (url === item.playbackUrl480p) return 'p480';
  if (url === item.playbackUrl) {
    return url.includes('.m3u8') || url.includes('/hls/') ? 'hls' : 'original';
  }
  // Checked last: when the mirror URL equals one of the two above, the candidate
  // list has already de-duplicated it, and the copy we are playing is better
  // described by what it is than by where it also exists.
  if (url === item.playbackUrlMirror) return 'mirror';
  return 'unknown';
}

/**
 * The best candidate that hasn't already failed to load.
 *
 * CORE-918 — the player owns the list of URLs that threw a media error and asks
 * this for what to try next; `null` means every candidate is spent and the
 * failure is real, which the UI must surface rather than sit at 00:00 forever.
 */
export function resolveActiveCandidate(candidates: string[], failed: string[]): string | null {
  return candidates.find(url => !failed.includes(url)) ?? null;
}

/**
 * Get the best available video URL, preferring 480p when available.
 *
 * This is `getVideoUrlCandidates(item)[0]` — unchanged behaviour for callers
 * that can only hold one URL. Anything rendering a real player should take the
 * full list instead, so a dead preferred file can fall back (CORE-918).
 */
export function getVideoUrl(item: {
  playbackUrl?: string | null;
  playbackUrl480p?: string | null;
  playbackUrlMirror?: string | null;
}): string | null {
  return getVideoUrlCandidates(item)[0] ?? null;
}

/**
 * Progress payload for `chunkedUpload.onProgress`. `bytesUploaded` /
 * `bytesTotal` come from the BE's PUT response (single source of truth);
 * percent / throughput / ETA are derived in `chunkedUpload`.
 *
 * `throughputBytesPerSec` and `etaSeconds` are `null` until the first
 * 5-second pacer window closes. UI should hide ETA while it's `null`
 * so we don't render "осталось NaN".
 */
export interface ChunkedUploadProgress {
  bytesUploaded: number;
  bytesTotal: number;
  percent: number;
  throughputBytesPerSec: number | null;
  etaSeconds: number | null;
}

// CORE-486 — client-side video upload size limit. Matches the backend
// chunked-upload cap; gating here means an oversize file is rejected
// instantly with clear copy instead of after a long doomed upload that would
// only 413 at the very end. Exported so it's tunable/testable.
// CORE-898 — raised 4 GB → 8 GB so an hour-long practice (the reported case
// was 5.19 GB) uploads without quality-destroying re-compression, with
// headroom for longer sessions. Keep this in lock-step with the backend cap
// in MasterChunkedUploadController.InitUpload — a mismatch either rejects a
// valid file late (BE lower) or lets it die at the end of a long upload (FE
// higher). NB: raising the cap raises the peak temp-disk footprint of
// AssembleFileAsync on the API host — see the CORE-898 report.
export const MAX_VIDEO_UPLOAD_BYTES = 8 * 1024 * 1024 * 1024;
const VIDEO_TOO_LARGE_MESSAGE = 'Файл слишком большой. Максимальный размер видео — 8 ГБ.';

/**
 * Throw a friendly, telemetry-logged error when a video exceeds the size limit.
 * Called before `upload_started` fires, so a rejected file logs only
 * `upload_size_rejected` (not started/failed). No-op when the size is unknown
 * (native {uri} before the blob is read) — the backend still enforces the cap.
 */
function assertVideoWithinSizeLimit(sizeBytes: number | null | undefined, ctx: UploadContext): void {
  if (typeof sizeBytes === 'number' && sizeBytes > MAX_VIDEO_UPLOAD_BYTES) {
    trackUpload('upload_size_rejected', ctx, { limitBytes: MAX_VIDEO_UPLOAD_BYTES });
    // Typed + status 413 so friendlyApiError surfaces the (Russian) message.
    throw new UploadError(VIDEO_TOO_LARGE_MESSAGE, 413);
  }
}

/**
 * Pure pacer state — feeds `decideNextConcurrency` after every closed
 * 5-second window. Lives in its own type so the algorithm is testable
 * without the network. See AMB-225 FE doc for the ramp ladder.
 */
export interface PacerState {
  /** Current parallelism, clamped to [PACER_FLOOR, PACER_CEIL]. */
  concurrency: number;
  /** Throughput measured on the single-thread baseline window. */
  baseline: number | null;
  /** Throughput from the previous window. Used for ramp-up + cliff detection. */
  prevThroughput: number | null;
}

export const PACER_FLOOR = 1;
export const PACER_CEIL = 4;
const PACER_WINDOW_MS = 5_000;

/**
 * Decide the next pacer concurrency from a closed throughput window.
 * Pure function — no I/O, no timer — so the ramp ladder is unit-tested
 * in `api.pacer.test.ts` without spinning up the network.
 *
 * Ladder (from `docs/tech-tasks/chunked-upload-adaptive-progress.md`):
 *   - conc 1 + first window done → bump to 2, snapshot baseline.
 *   - conc 2 + throughput > baseline × 1.3 → bump to 3.
 *   - conc 3 + throughput > prev × 1.2 → bump to 4 (ceiling).
 *   - throughput ≤ prev × 1.05 on any step → freeze.
 *   - throughput < prev × 0.7 → drop by 1 (down to floor).
 */
export function decideNextConcurrency(state: PacerState, throughput: number): PacerState {
  const { concurrency, baseline, prevThroughput } = state;
  // Cliff first — a degrading channel beats the ramp logic.
  if (prevThroughput !== null && throughput < prevThroughput * 0.7) {
    return {
      concurrency: Math.max(PACER_FLOOR, concurrency - 1),
      baseline,
      prevThroughput: throughput,
    };
  }
  // First window — measure single-thread baseline and step to 2.
  if (concurrency === 1 && baseline === null) {
    return {
      concurrency: Math.min(PACER_CEIL, 2),
      baseline: throughput,
      prevThroughput: throughput,
    };
  }
  // 2 → 3: must clear the baseline ×1.3 bar (not just the previous window).
  if (concurrency === 2 && baseline !== null && throughput > baseline * 1.3) {
    return {
      concurrency: 3,
      baseline,
      prevThroughput: throughput,
    };
  }
  // 3 → 4: linear-growth check against the previous window.
  if (concurrency === 3 && prevThroughput !== null && throughput > prevThroughput * 1.2) {
    return {
      concurrency: PACER_CEIL,
      baseline,
      prevThroughput: throughput,
    };
  }
  // No-op gain → freeze. The doc says ≤1.05× prev = saturation.
  // (Pacer ladder above already short-circuits the +x cases; falling
  // through here means we keep `concurrency` and just refresh prev.)
  return { concurrency, baseline, prevThroughput: throughput };
}

/**
 * CORE-415 — resolve the JWT for the standalone upload helpers, which run
 * outside `ApiService` and so bypass its `authedRequest` silent-refresh
 * wrapper. If the token is missing (commonly because a mid-session 401 on a
 * bare-fetch dashboard call already cleared it — the "ghost authenticated"
 * state that hit Veronika, prod REACT-NATIVE-19), make one refresh attempt
 * before giving up. When there's still no token, throw the typed
 * `AuthRequiredError` (status 401) instead of a status-less
 * `Error('Not authenticated')` so the UI shows «Сессия истекла…» and can
 * route the user to re-login rather than offering a dead-end retry.
 */
async function requireToken(): Promise<string> {
  let token = await storage.getToken();
  if (!token) {
    token = await api.refreshJwt();
  }
  if (!token) throw new AuthRequiredError();
  return token;
}

/**
 * CORE-486 — discard a chunked-upload session server-side, deleting every
 * chunk already in S3.
 *
 * Call this only when the user genuinely gives up on an upload (declining the
 * «продолжить загрузку?» prompt, discarding after an error). Do NOT call it
 * when the modal simply closes mid-upload: that path deliberately keeps the
 * session alive so the upload can be resumed within the BE's 24h TTL.
 *
 * Best-effort by design — the user has already moved on, and the worker's
 * cleanup sweep will collect the session anyway if this request never lands.
 * Never throws.
 */
export async function cancelChunkedUpload(sessionId: string): Promise<void> {
  if (!sessionId) return;
  try {
    const token = await requireToken();
    await fetch(`${BASE_URL}/api/master/files/chunked/${sessionId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch {
    // Offline, expired token, session already gone — nothing the user can act on.
  }
}

/**
 * Chunked upload for large video files. Uploads chunks through an
 * adaptive worker pool (concurrency 1..4) and reports byte-accurate
 * progress sourced from the BE's PUT response.
 *
 * `onProgress` shape changed in AMB-225 from `(percent: number)` to
 * the `ChunkedUploadProgress` info object. Callers should migrate to
 * the named fields rather than reading `info.percent` only — that
 * keeps the UI honest about ETA / throughput visibility.
 */
export async function chunkedUpload(opts: {
  file: Blob;
  fileName: string;
  contentType: string;
  onProgress?: (info: ChunkedUploadProgress) => void;
  /**
   * Status callback for the post-100% finalize step (`/complete`). Lets the
   * UI show "Финализирую…" / "Жду сервер Xс" instead of a blank stuck
   * progress bar. `'finalizing'` fires while the initial POST is in flight,
   * `'pending'` fires on each second of the poll interval as we wait on
   * the BE-side assembly to finish.
   */
  onFinalize?: (status:
    | { phase: 'finalizing' }
    | { phase: 'pending'; secondsRemaining: number }
  ) => void;
  signal?: AbortSignal;

  /**
   * Resume an in-flight session instead of starting a new one. When
   * provided, skips `POST /init` and hits `GET /status` to learn which
   * chunks the BE has already accepted, then uploads only the missing
   * ones. Caller is responsible for restoring the same `file` /
   * `fileName` / `contentType` from local storage — the BE doesn't
   * track the original Blob, so passing a different file will produce
   * a corrupt assembly on `/complete`.
   *
   * Cleared after a successful `/complete` is the caller's
   * responsibility (e.g. via `uploadResume.clear(userId)`).
   */
  resumeFromSessionId?: {
    sessionId: string;
    chunkSize: number;
    totalChunks: number;
  };

  /**
   * Lifecycle hook fired when `POST /init` first returns a sessionId.
   * Lets the caller persist the resume entry to localStorage /
   * AsyncStorage so a page reload can pick up where we left off.
   * Not fired on the resume path (the caller already has the
   * sessionId).
   */
  onSessionStart?: (info: { sessionId: string; chunkSize: number; totalChunks: number }) => void;

  /** CORE-486 — what's being uploaded, for upload telemetry grouping.
   *  Defaults to 'practice' (the master-video chunked path). */
  telemetryKind?: string;
}): Promise<string> {
  const {
    file, fileName, contentType,
    onProgress, onFinalize, signal,
    resumeFromSessionId, onSessionStart,
    telemetryKind,
  } = opts;

  // CORE-486 — upload telemetry (account-attributed). A client-generated
  // correlation id ties every event of this attempt together even if `POST
  // /init` fails before the BE hands out a sessionId; the BE session id is
  // added to properties (beSessionId) once known.
  const uploadSessionId = newUploadSessionId();
  const telemetryCtx: UploadContext = {
    uploadSessionId,
    kind: telemetryKind ?? 'practice',
    transport: 'chunked',
    fileName,
    fileSizeBytes: file.size,
    contentType,
  };
  const telemetryStartedAt = Date.now();

  // Reject oversize files up front (before any upload_started) — logs
  // upload_size_rejected and throws a friendly 413.
  assertVideoWithinSizeLimit(file.size, telemetryCtx);

  try {
  const token = await requireToken();

  // 1a. Resume path — skip `POST /init`, hit `GET /status` to learn
  // which chunks are already in S3, narrow the worker pool's queue
  // to the missing ones.
  let sessionId: string;
  let chunkSize: number;
  let totalChunks: number;
  let initialBytesUploaded = 0;
  let chunksToUpload: number[];

  if (resumeFromSessionId) {
    sessionId = resumeFromSessionId.sessionId;
    chunkSize = resumeFromSessionId.chunkSize;
    totalChunks = resumeFromSessionId.totalChunks;
    const statusRes = await fetch(
      `${BASE_URL}/api/master/files/chunked/${sessionId}/status`,
      { headers: { 'Authorization': `Bearer ${token}` }, signal },
    );
    if (!statusRes.ok) {
      throw new ChunkUploadError(await readErrorMessage(statusRes, 'Resume status failed'), statusRes.status);
    }
    const statusBody = await statusRes.json();
    const uploadedChunks: number[] = Array.isArray(statusBody?.uploadedChunks)
      ? statusBody.uploadedChunks
      : [];
    initialBytesUploaded = typeof statusBody?.bytesUploaded === 'number'
      ? statusBody.bytesUploaded
      : uploadedChunks.length * chunkSize;
    const uploadedSet = new Set(uploadedChunks);
    chunksToUpload = [];
    for (let i = 0; i < totalChunks; i++) if (!uploadedSet.has(i)) chunksToUpload.push(i);
    trackUpload('upload_resumed', telemetryCtx, {
      beSessionId: sessionId,
      totalChunks,
      remainingChunks: chunksToUpload.length,
      bytesUploaded: initialBytesUploaded,
    });
  } else {
    // 1b. Fresh session — same `POST /init` as before.
    const initRes = await fetch(`${BASE_URL}/api/master/files/chunked/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ fileName, fileSize: file.size, contentType }),
      signal,
    });
    if (!initRes.ok) {
      throw new ChunkUploadError(await readErrorMessage(initRes, 'Upload init failed'), initRes.status);
    }
    ({ sessionId, chunkSize, totalChunks } = await initRes.json());
    onSessionStart?.({ sessionId, chunkSize, totalChunks });
    trackUpload('upload_started', telemetryCtx, { beSessionId: sessionId, totalChunks });
    chunksToUpload = Array.from({ length: totalChunks }, (_, i) => i);
  }

  // 2. Upload chunks through an adaptive worker pool.
  //
  // The bytesUploaded counter is BE-sourced (PUT response's
  // `bytesUploaded` / `bytesTotal`) and monotonic via Math.max so a
  // late-completing chunk from an earlier window doesn't yank the
  // progress bar backwards. Pacer state lives across worker
  // completions; `decideNextConcurrency` (pure helper above) makes
  // the ramp logic unit-testable.
  // Seeded from `GET /status` on the resume path, 0 on a fresh session.
  // Subsequent PUT responses overwrite via Math.max so a stale local
  // counter can't drag the bar backwards.
  let bytesUploaded = initialBytesUploaded;
  const bytesTotal = file.size;
  let windowStart = Date.now();
  let windowBytes = 0;
  // Resume always starts the pacer at floor — the channel may have
  // changed between the original upload and the resume, so re-measure
  // before ramping up.
  let pacer: PacerState = { concurrency: 1, baseline: null, prevThroughput: null };
  let lastThroughput: number | null = null;

  const emitProgress = () => {
    const percent = bytesTotal > 0 ? Math.min(100, (bytesUploaded / bytesTotal) * 100) : 0;
    const etaSeconds = lastThroughput && lastThroughput > 0
      ? Math.max(0, Math.round((bytesTotal - bytesUploaded) / lastThroughput))
      : null;
    onProgress?.({
      bytesUploaded,
      bytesTotal,
      percent,
      throughputBytesPerSec: lastThroughput,
      etaSeconds,
    });
  };

  // PUT one chunk with up to 3 retries on transient errors. Aborts
  // are surfaced as `AbortError` immediately — no retry on cancel.
  async function putChunk(idx: number): Promise<void> {
    const start = idx * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    const chunkBytes = end - start;

    let retries = 0;
    while (true) {
      try {
        const chunkRes = await fetch(
          `${BASE_URL}/api/master/files/chunked/${sessionId}/chunks/${idx}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/octet-stream', 'Authorization': `Bearer ${token}` },
            body: chunk,
            signal,
          }
        );
        // CORE-205 — carry the HTTP status as a structured field so the UI
        // boundary (friendlyApiError) can map it (413 → «Файл слишком
        // большой…») instead of showing this technical string.
        if (!chunkRes.ok) throw new ChunkUploadError(`Chunk ${idx} failed: ${chunkRes.status}`, chunkRes.status);
        // Prefer BE's bytesUploaded — it's the running total across all
        // chunks the server has accepted, so concurrent PUTs can't double-
        // count. Fall back to local accounting if the BE response is
        // missing the field (mock/legacy).
        const body = await chunkRes.json().catch(() => null as any);
        const beBytesUploaded = typeof body?.bytesUploaded === 'number' ? body.bytesUploaded : null;
        if (beBytesUploaded !== null) {
          bytesUploaded = Math.max(bytesUploaded, beBytesUploaded);
        } else {
          bytesUploaded += chunkBytes;
        }
        windowBytes += chunkBytes;
        // Close the 5-second window — measure + ramp + reset.
        const now = Date.now();
        const windowElapsedMs = now - windowStart;
        if (windowElapsedMs >= PACER_WINDOW_MS) {
          const throughput = (windowBytes * 1000) / windowElapsedMs;
          lastThroughput = throughput;
          pacer = decideNextConcurrency(pacer, throughput);
          windowStart = now;
          windowBytes = 0;
          // One telemetry breadcrumb per closed pacer window (~5s) — matches
          // the presigned path's sampling cadence.
          trackUpload('upload_progress', telemetryCtx, {
            beSessionId: sessionId,
            bytesUploaded,
            percent: bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0,
            throughputBytesPerSec: throughput,
            concurrency: pacer.concurrency,
          });
        }
        emitProgress();
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') throw err;
        retries++;
        // Breadcrumb every retry — a burst of these on one session is the
        // signature of a flaky link mid-upload.
        trackUpload('upload_chunk_retry', telemetryCtx, {
          beSessionId: sessionId,
          chunkIndex: idx,
          attempt: retries,
          httpStatus: err instanceof ChunkUploadError ? err.status ?? null : null,
        });
        if (retries >= 3) {
          // Preserve the HTTP status from the failing PUT (when there was
          // one) so the UI can still map e.g. 413 after retries are spent;
          // a network/transport failure leaves status undefined and is
          // classified via `cause` in friendlyApiError.
          const status = err instanceof ChunkUploadError ? err.status : undefined;
          throw new ChunkUploadError(`Chunk ${idx} failed after 3 retries: ${err?.message}`, status, { cause: err });
        }
        await new Promise(r => setTimeout(r, 1000 * retries));
      }
    }
  }

  // Emit the seeded progress before the first PUT lands — on the resume
  // path the bar should appear at the saved offset immediately instead
  // of jumping from 0% on the first chunk completion.
  if (initialBytesUploaded > 0) emitProgress();

  // Worker pool — schedules up to `pacer.concurrency` PUTs at a time.
  // The queue is `chunksToUpload`, which on a fresh session is
  // [0..totalChunks-1] and on resume is only the chunks the BE hasn't
  // accepted yet (per `GET /status`).
  let cursor = 0;
  const inFlight = new Set<Promise<void>>();

  while (cursor < chunksToUpload.length || inFlight.size > 0) {
    while (inFlight.size < pacer.concurrency && cursor < chunksToUpload.length) {
      const idx = chunksToUpload[cursor++];
      const p = putChunk(idx);
      const tracked = p.finally(() => { inFlight.delete(tracked); });
      inFlight.add(tracked);
    }
    if (inFlight.size === 0) break;
    // Wait for ANY worker to settle. We use the actual Promise (which can
    // reject) rather than the tracked .finally wrapper so an upload error
    // surfaces here and aborts the loop.
    await Promise.race(inFlight);
  }

  // 3. Complete — POST /complete now kicks off the BE-side assembly + S3
  // upload as a background task and returns 202 with a status URL when the
  // work is still pending, or 200 with the final URL when it's already
  // done (cached or fast). We poll the status endpoint instead of
  // re-issuing /complete: that avoids the old failure mode where a
  // synchronous /complete would race a still-in-progress assembly under
  // proxy timeouts and burn all retries before the BE finished its first
  // attempt.
  onFinalize?.({ phase: 'finalizing' });
  const initialRes = await fetch(
    `${BASE_URL}/api/master/files/chunked/${sessionId}/complete`,
    { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, signal }
  );
  const initialBody = await initialRes.json().catch(() => ({} as any));

  let completeUrl: string | null = null;
  let statusUrl: string | null = null;

  if (initialRes.status >= 400 && initialRes.status !== 202) {
    throw new Error(initialBody?.error || `Upload completion failed (${initialRes.status})`);
  }
  if (initialBody?.url) {
    completeUrl = initialBody.url as string;
  } else if (initialBody?.statusUrl) {
    statusUrl = String(initialBody.statusUrl).startsWith('http')
      ? initialBody.statusUrl
      : `${BASE_URL}${initialBody.statusUrl}`;
  } else {
    statusUrl = `${BASE_URL}/api/master/files/chunked/${sessionId}/complete/status`;
  }

  if (!completeUrl) {
    // Poll the status endpoint until Done or Failed. 5s tick × up to 30 min
    // overall — assembly of multi-hundred-MB practices typically finishes in
    // 1-3 min, but the BE-side job is durable across deploys + worker
    // restarts so it's safe to wait longer than the old in-memory window.
    const pollIntervalSec = 5;
    const overallTimeoutMs = 30 * 60 * 1000;
    const deadline = Date.now() + overallTimeoutMs;
    while (!completeUrl) {
      if (Date.now() > deadline) {
        throw new Error('Upload completion timed out (30 min). Try again.');
      }
      for (let s = pollIntervalSec; s > 0; s--) {
        if (signal?.aborted) {
          const e: any = new Error('Aborted');
          e.name = 'AbortError';
          throw e;
        }
        onFinalize?.({ phase: 'pending', secondsRemaining: s });
        await new Promise(r => setTimeout(r, 1000));
      }
      const pollRes = await fetch(statusUrl!, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal,
      });
      const pollBody = await pollRes.json().catch(() => ({} as any));
      if (pollBody?.url) {
        completeUrl = pollBody.url as string;
        break;
      }
      if (pollRes.status >= 500 || pollBody?.phase === 'failed') {
        throw new Error(pollBody?.error || `Upload completion failed (${pollRes.status})`);
      }
      // 202 / phase: 'pending' / phase: 'not_started' — keep polling.
    }
  }
  trackUpload('upload_completed', telemetryCtx, {
    beSessionId: sessionId,
    durationMs: Date.now() - telemetryStartedAt,
    bytesUploaded: bytesTotal,
  });
  return completeUrl!;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      trackUpload('upload_cancelled', telemetryCtx, { durationMs: Date.now() - telemetryStartedAt });
    } else {
      trackUploadError('upload_failed', telemetryCtx, err, { durationMs: Date.now() - telemetryStartedAt });
    }
    throw err;
  }
}

// Backend error responses are `{"error":"..."}` JSON. Surface that message
// when present; fall back to raw text, then to the supplied default. Keeps
// "File too large. Maximum size: 8GB." readable in alerts instead of dumping
// the JSON blob at the user.
async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  try {
    const json = JSON.parse(text);
    if (typeof json?.error === 'string' && json.error) return json.error;
    if (typeof json?.message === 'string' && json.message) return json.message;
  } catch { /* not JSON — use raw text */ }
  return text;
}

/**
 * Wraps `fetch` with a hard timeout via AbortController. Used by the
 * multipart save-profile endpoints so a stalled upload (RU edge shaping,
 * dropped connection mid-PUT, backend pause) surfaces a clear error to
 * the user instead of an infinite spinner.
 *
 * The default 60 s is generous enough for a normal avatar upload over a
 * 1–2 Mbps link but short enough to break out of a truly hung connection.
 * Honours any caller-supplied `init.signal` — combined with our timeout
 * via `AbortController.abort(reason)` semantics.
 */
/**
 * CORE-400 — chat-connect token fetches use this tight ceiling instead of the
 * 60s default: they're tiny requests, so anything slower means the network is
 * blocked/shaped and we want the api-ru fallback to engage in seconds, not after
 * the browser's ~120s TCP timeout.
 */
const STREAM_TOKEN_TIMEOUT_MS = 10_000;

/**
 * CORE-717 — per-attempt ceiling for the profile feed. Generous next to a
 * healthy ~0.5 s response (and to the slower RU edge) so it never cuts off a
 * legitimately slow load; short enough that a stalled attempt is replaced by a
 * fresh one long before the user gives up on the page.
 */
const POSTS_ATTEMPT_TIMEOUT_MS = 8000;

/**
 * Ceiling for provisioning an AI chat channel. Idempotent and normally sub-
 * second, so 10 s is already generous — and it is the first thing that runs
 * after the consent gate, where a stall is indistinguishable from a broken
 * product: the chat sits on an endless spinner and the message the person just
 * typed goes nowhere.
 */
const CHANNEL_PROVISION_TIMEOUT_MS = 10_000;

/**
 * CORE-1257 — `deleteRoomRecording` used a bare `fetch`, so a stalled
 * connection (dropped response, RU edge throttling) left the promise pending
 * forever: the «Удалить запись» loader never resolved into success OR error,
 * and there was nothing to retry. Matches the sibling `deleteLiveRecording`'s
 * ceiling.
 */
const DELETE_RECORDING_TIMEOUT_MS = 20_000;

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 60_000,
): Promise<Response> {
  const ctrl = new AbortController();
  let timedOut = false;
  const timeoutMessage = `Запрос занял больше ${Math.round(timeoutMs / 1000)} с. Проверьте интернет и попробуйте ещё раз.`;
  const onCallerAbort = () => ctrl.abort((init.signal as any)?.reason);
  if (init.signal) {
    if (init.signal.aborted) ctrl.abort((init.signal as any)?.reason);
    else init.signal.addEventListener('abort', onCallerAbort);
  }
  const timer = setTimeout(() => {
    // DOMException makes the abort show up as `name: 'TimeoutError'` in
    // catch blocks, so callers / Sentry can distinguish "network blip"
    // from "manual cancel" from "took too long".
    timedOut = true;
    ctrl.abort(new DOMException(timeoutMessage, 'TimeoutError'));
  }, timeoutMs);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } catch (err: any) {
    // Hermes (and some web engines) DROP the abort reason and reject an aborted
    // fetch with a generic `AbortError`, losing the `TimeoutError` name. That
    // made every 60 s timeout look like a user-cancel, so neither this call's
    // api-ru fallback nor installApiFallback's web patch (both keyed on
    // TimeoutError) ever retried a timed-out request. Re-tag deterministically
    // from our own flag so a timeout is always a TimeoutError, a real
    // caller-cancel stays AbortError. (CORE-450 — root of «всё равно отказ».)
    if (timedOut && err?.name === 'AbortError') {
      throw new DOMException(timeoutMessage, 'TimeoutError');
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (init.signal) init.signal.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * CORE-100 — upload a profile photo straight to S3 via a presigned URL, bypassing the
 * prod origin (RU users' bulk multipart uploads to the origin are bandwidth-shaped and
 * choke at ~128 KB; S3's IP range is not shaped). Returns the S3 object key to commit as
 * AvatarKey/CoverKey on the profile save. `file` is a web Blob or a native {uri,type}.
 */
async function presignAndPutPhoto(kind: 'avatar' | 'cover' | 'master', file: any, token: string): Promise<string> {
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  const contentType: string = file?.type && allowed.includes(file.type) ? file.type : 'image/jpeg';

  // 1. Mint the presigned PUT URL (tiny request — fast even on the shaped path).
  const presignRes = await fetchWithTimeout(`${BASE_URL}/api/user/profile/photo/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind, contentType }),
  });
  if (!presignRes.ok) throw new Error(await readErrorMessage(presignRes, 'Presign failed'));
  const { uploadUrl, key, contentType: signedType } = await presignRes.json();

  // 2. Resolve the bytes as a Blob — a web Blob passes through; a native {uri} is read.
  let blob: Blob;
  if (typeof Blob !== 'undefined' && file instanceof Blob) {
    blob = file;
  } else if (file?.uri) {
    blob = await (await fetch(file.uri)).blob();
  } else {
    throw new Error('Unsupported file for upload');
  }

  // 3. PUT straight to S3 (bypasses the shaped origin). No Authorization — the
  //    presigned URL carries its own credentials in the query string.
  const s3Res = await fetchWithTimeout(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': signedType || contentType },
    body: blob,
  }, 120_000);
  if (!s3Res.ok) throw new UploadError(`S3 upload failed: ${s3Res.status}`, s3Res.status);

  return key;
}

/**
 * CORE-232 — upload a master video (practice or manifesto) straight to S3 via a
 * presigned PUT, bypassing the prod origin. RU users' multi-MB uploads to the
 * origin choke at ~128 KB (origin-IP bandwidth shaping); S3's IP range is not
 * shaped (measured full multi-MB transfers from RU). Replaces the chunked
 * upload for the video binary; the caller still commits the returned public URL
 * as PlaybackUrl (practice) / VideoFileUrl (manifesto).
 *
 * Returns the public URL the object is served from. Reports byte progress via
 * XHR `upload.onprogress` (fetch gives none on a request body) so the existing
 * progress UI keeps working. Honours `signal` for cancel.
 *
 * Tradeoff vs chunkedUpload: a single PUT has no per-chunk resume — an
 * interrupted upload restarts from 0. Acceptable because the chunked path
 * doesn't complete at all from RU today; S3 does.
 */
export async function presignAndPutVideo(
  file: any,
  opts: {
    kind: 'practice' | 'manifesto' | 'agape' | 'post';
    fileName?: string;
    onProgress?: (info: ChunkedUploadProgress) => void;
    signal?: AbortSignal;
    /**
     * CORE-1267 — which endpoint mints the presigned PUT. The default is the
     * master one, closed behind the Master role; a regular user's Circle post
     * has its own (`/api/user/circle/posts/video/presign`) that returns the
     * same shape. Everything after the mint — the PUT, the fallback cloud, the
     * telemetry — is identical, which is why only the path is a parameter.
     */
    presignPath?: string;
  },
): Promise<string> {
  // CORE-486 — upload telemetry (account-attributed). One session id correlates
  // every event of this upload attempt; failures carry the HTTP status so the
  // admin/MCP view can group 413 (too large) / 401 (auth) / 5xx separately.
  const uploadSessionId = newUploadSessionId();
  const ctx: UploadContext = {
    uploadSessionId,
    kind: opts.kind,
    transport: 'presigned',
    fileName: opts.fileName ?? (file?.name ?? null),
    fileSizeBytes: typeof file?.size === 'number' ? file.size : null,
    contentType: typeof file?.type === 'string' ? file.type : null,
  };
  // Reject oversize files up front (web knows the size here; native learns it
  // after the blob is read, where the BE cap still applies).
  assertVideoWithinSizeLimit(ctx.fileSizeBytes, ctx);

  const startedAt = Date.now();
  trackUpload('upload_started', ctx);

  try {
    const token = await requireToken();

    // CORE-167 — include AVI (video/x-msvideo, some browsers video/avi) so the
    // presign asks for a .avi key instead of silently falling back to .mp4.
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska', 'video/x-msvideo', 'video/avi'];
    const contentType: string = file?.type && allowed.includes(file.type) ? file.type : 'video/mp4';
    ctx.contentType = contentType;

    // 1. Mint the presigned PUT URL (tiny request — fast even on the shaped path).
    const presignPath = opts.presignPath ?? '/api/master/content/videos/presign';
    const presignRes = await fetchWithTimeout(`${BASE_URL}${presignPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kind: opts.kind, contentType }),
      signal: opts.signal,
    });
    // CORE-415 — a 401 here means the JWT expired between requireToken() and now
    // (or was refreshed to a still-rejected token); surface it as an auth error
    // so the UI shows «Сессия истекла…» + re-login, not a raw «Presign failed».
    if (presignRes.status === 401) throw new AuthRequiredError();
    if (!presignRes.ok) throw new UploadError(await readErrorMessage(presignRes, 'Presign failed'), presignRes.status);
    const { uploadUrl, publicUrl, contentType: signedType } = await presignRes.json();

    // 2. Resolve the bytes as a Blob — a web Blob passes through; a native {uri} is read.
    let blob: Blob;
    if (typeof Blob !== 'undefined' && file instanceof Blob) {
      blob = file;
    } else if (file?.uri) {
      blob = await (await fetch(file.uri)).blob();
    } else {
      throw new UploadError('Unsupported file for upload');
    }
    ctx.fileSizeBytes = blob.size;

    // 3. PUT straight to S3 via XHR (for byte progress) — no Authorization header,
    //    the presigned URL carries its own credentials in the query string.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', signedType || contentType);
      const total = blob.size;
      const putStartedAt = Date.now();
      // Sample progress into telemetry at most once every 5s (the UI callback
      // still fires on every tick) so a long upload leaves a breadcrumb trail
      // without flooding the stream.
      let lastTelemetryEmit = 0;
      // No timeout on this PUT: once all bytes are sent, a lost/hung S3 response
      // (onload never fires) leaves the post frozen at «Загрузка 100%» forever
      // (tester, prod). Bound the wait for the ack — not the upload itself.
      let respTimer: ReturnType<typeof setTimeout> | null = null;
      const armAckTimeout = () => {
        if (respTimer !== null) return;
        respTimer = setTimeout(() => {
          try { xhr.abort(); } catch { /* ignore */ }
          reject(new UploadError('S3 не подтвердил загрузку — попробуйте ещё раз.'));
        }, 45000);
      };
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const elapsed = (Date.now() - putStartedAt) / 1000;
        const throughput = elapsed > 0 ? e.loaded / elapsed : null;
        const etaSeconds = throughput && throughput > 0
          ? Math.max(0, Math.round((total - e.loaded) / throughput))
          : null;
        opts.onProgress?.({
          bytesUploaded: e.loaded,
          bytesTotal: total,
          percent: total > 0 ? Math.min(100, (e.loaded / total) * 100) : 0,
          throughputBytesPerSec: throughput,
          etaSeconds,
        });
        // Fallback arm for platforms whose XHR upload doesn't emit onloadend
        // (React Native). Idempotent — the web path arms via onloadend below.
        if (e.loaded >= total) armAckTimeout();
        const now = Date.now();
        if (now - lastTelemetryEmit >= 5000) {
          lastTelemetryEmit = now;
          trackUpload('upload_progress', ctx, {
            bytesUploaded: e.loaded,
            percent: total > 0 ? Math.round((e.loaded / total) * 100) : 0,
            throughputBytesPerSec: throughput,
            etaSeconds,
          });
        }
      };
      // CORE-338 — arm the ack-wait the instant the request body finishes, not
      // from an `e.loaded >= total` check inside onprogress. Browsers don't
      // guarantee a final progress tick where loaded === total (and the composer
      // rounds 99.5%→100%, so the UI shows the «finalising» spinner before that
      // tick would fire anyway), so arming in onprogress could silently never
      // happen — leaving a hung S3 response with no bound, i.e. the infinite
      // loader QA hit on an iPhone .mov. `upload.onloadend` fires exactly once
      // when the body is fully sent, whatever the progress granularity.
      xhr.upload.onloadend = () => armAckTimeout();
      xhr.onload = () => {
        if (respTimer) clearTimeout(respTimer);
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        // CORE-232 — typed so friendlyApiError shows RU copy, not «S3 upload failed: N».
        else reject(new UploadError(`S3 upload failed: ${xhr.status}`, xhr.status));
      };
      xhr.onerror = () => { if (respTimer) clearTimeout(respTimer); reject(new UploadError('S3 upload network error')); };
      xhr.onabort = () => { if (respTimer) clearTimeout(respTimer); const e: any = new Error('Aborted'); e.name = 'AbortError'; reject(e); };
      if (opts.signal) {
        if (opts.signal.aborted) { xhr.abort(); return; }
        opts.signal.addEventListener('abort', () => xhr.abort());
      }
      xhr.send(blob);
    });

    trackUpload('upload_completed', ctx, {
      durationMs: Date.now() - startedAt,
      bytesUploaded: ctx.fileSizeBytes,
    });
    return publicUrl;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      trackUpload('upload_cancelled', ctx, { durationMs: Date.now() - startedAt });
    } else {
      trackUploadError('upload_failed', ctx, err, { durationMs: Date.now() - startedAt });
    }
    throw err;
  }
}

/**
 * CORE-1267 — то же самое для видео в публикации ОБЫЧНОГО пользователя.
 *
 * Отдельная ручка presign нужна потому, что мастерская закрыта ролью целиком;
 * дальше путь общий — байты уходят в хранилище напрямую, мимо API, и в пост
 * попадает только ссылка (`createCirclePost({ videoUrl })`).
 */
export function presignAndPutCircleVideo(
  file: any,
  opts: {
    fileName?: string;
    onProgress?: (info: ChunkedUploadProgress) => void;
    signal?: AbortSignal;
  } = {},
): Promise<string> {
  return presignAndPutVideo(file, {
    ...opts,
    kind: 'post',
    presignPath: '/api/user/circle/posts/video/presign',
  });
}

/** CORE-904 — the handle every multipart follow-up call takes. */
type MultipartHandle = { key: string; uploadId: string; cloud: string };

/**
 * CORE-904 — presigned PUT URLs for the given parts of an open upload. Used on
 * resume (the create response's URLs are long gone) and to replace a URL that
 * expired mid-upload — S3 signs them for an hour, and a multi-gigabyte practice
 * on a weak channel outlives that.
 */
async function mintMultipartPartUrls(
  handle: MultipartHandle,
  partNumbers: number[],
  token: string,
  signal?: AbortSignal,
): Promise<Map<number, string>> {
  const res = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/multipart/parts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ key: handle.key, uploadId: handle.uploadId, cloud: handle.cloud, partNumbers }),
    signal,
  });
  if (!res.ok) throw new UploadError(await readErrorMessage(res, 'Could not sign upload parts'), res.status);
  const body = await res.json();
  return new Map<number, string>((body.parts ?? []).map((p: any) => [p.partNumber as number, p.url as string]));
}

/**
 * CORE-904 — the parts S3 already holds for an open upload, or `null` when it
 * no longer knows the upload at all (completed, aborted, or expired by a bucket
 * lifecycle rule). `null` means "start clean", not "fail".
 */
async function listMultipartParts(
  handle: MultipartHandle,
  token: string,
  signal?: AbortSignal,
): Promise<{ partNumber: number; eTag: string }[] | null> {
  try {
    const q = `key=${encodeURIComponent(handle.key)}&uploadId=${encodeURIComponent(handle.uploadId)}&cloud=${encodeURIComponent(handle.cloud)}`;
    const res = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/multipart/status?${q}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    const body = await res.json();
    return (body.parts ?? [])
      .filter((p: any) => typeof p.partNumber === 'number' && typeof p.eTag === 'string')
      .map((p: any) => ({ partNumber: p.partNumber as number, eTag: p.eTag as string }));
  } catch {
    // A resume that can't be confirmed is not a resume — re-uploading is always
    // correct, whereas completing on a guess assembles a corrupt object.
    return null;
  }
}

/** CORE-904 — abort an open upload, best effort: it must never mask a real error. */
async function abortMultipartQuietly(handle: MultipartHandle, token: string): Promise<void> {
  try {
    await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/multipart/abort`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ key: handle.key, uploadId: handle.uploadId, cloud: handle.cloud }),
    });
  } catch { /* the parts fall to the bucket lifecycle rule; nothing else to do */ }
}

/**
 * CORE-904 — get an open multipart upload for this file: either the one a
 * previous attempt left behind, or a fresh one.
 *
 * The stored record is only a handle; what has actually landed comes from S3
 * (`ListParts`), so a resume can never complete on a stale local belief.
 *
 * This is also where records that aged out are aborted — the next upload is the
 * only moment the client gets to reap parts nobody will ever finish. Only the
 * *expired* ones: a fresh record for another file may well be an upload running
 * in another tab, and aborting that would kill it.
 */
async function openOrResumeMultipart(args: {
  kind: string;
  contentType: string;
  fingerprint: string;
  fileSizeBytes: number;
  partSize: number;
  partCount: number;
  token: string;
  signal?: AbortSignal;
}): Promise<{
  handle: MultipartHandle;
  publicUrl: string;
  urlFor: Map<number, string>;
  alreadyUploaded: { partNumber: number; eTag: string }[];
}> {
  const { kind, contentType, fingerprint, fileSizeBytes, partSize, partCount, token, signal } = args;

  const now = Date.now();
  const stored = await loadResumableUploads();

  for (const stale of expiredUploads(stored, now)) {
    await abortMultipartQuietly({ key: stale.key, uploadId: stale.uploadId, cloud: stale.cloud }, token);
    await clearResumableUpload(stale.fingerprint);
  }

  const mine = findResumable(stored, { fingerprint, partSize, partCount, fileSizeBytes }, now);
  if (mine) {
    const handle: MultipartHandle = { key: mine.key, uploadId: mine.uploadId, cloud: mine.cloud };
    const held = await listMultipartParts(handle, token, signal);
    if (held) {
      const missing = missingParts(partCount, held.map((p) => p.partNumber));
      const urlFor = missing.length
        ? await mintMultipartPartUrls(handle, missing, token, signal)
        : new Map<number, string>();
      return { handle, publicUrl: mine.publicUrl, urlFor, alreadyUploaded: held };
    }
    // S3 doesn't know it any more — nothing to abort, just start clean.
    await clearResumableUpload(mine.fingerprint);
  }

  const createRes = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/multipart/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind, contentType, partCount, fileSizeBytes }),
    signal,
  });
  if (createRes.status === 401) throw new AuthRequiredError();
  if (!createRes.ok) throw new UploadError(await readErrorMessage(createRes, 'Multipart init failed'), createRes.status);
  const createBody = await createRes.json();
  const handle: MultipartHandle = {
    key: createBody.key,
    uploadId: createBody.uploadId,
    cloud: createBody.primaryCloud ?? 'aws',
  };
  const record: ResumableUpload = {
    fingerprint,
    ...handle,
    publicUrl: createBody.publicUrl,
    partSize,
    partCount,
    fileSizeBytes,
    createdAt: Date.now(),
  };
  await saveResumableUpload(record);
  return {
    handle,
    publicUrl: createBody.publicUrl,
    urlFor: new Map<number, string>(
      (createBody.parts ?? []).map((p: any) => [p.partNumber as number, p.url as string]),
    ),
    alreadyUploaded: [],
  };
}

/**
 * CORE-904 — upload a large master video via an S3 multipart upload. A single
 * presigned PUT caps at 5 GiB, so a practice between 5 GiB and the 8 GB product
 * cap (CORE-898) 400s with EntityTooLarge at the very end. Multipart slices the
 * file into parts (grid from the shared `multipartUpload` policy), PUTs each
 * part straight to S3 via a presigned per-part URL — bytes still bypass the
 * shaped origin — and completes by ETag list. Parts upload through the same
 * adaptive worker pool as the chunked path; a transient part failure retries in
 * place (and re-mints the presigned URL in case it expired) instead of
 * restarting the whole upload.
 *
 * An upload that dies harder than that — connection gone, tab closed, app
 * killed — is resumed rather than restarted: the handle survives in storage
 * (`multipartResume`), and the next attempt at the same file asks S3 which
 * parts it already holds and uploads only the rest. A user cancel is the one
 * failure that aborts the S3 upload outright, so parts nobody will finish don't
 * accrue storage charges.
 *
 * Returns the public URL to commit as PlaybackUrl (practice) / VideoFileUrl
 * (manifesto). Prefer the `uploadMasterVideo` dispatcher, which routes small
 * files to the single PUT and large ones here.
 */
export async function presignAndMultipartVideo(
  file: any,
  opts: {
    kind: 'practice' | 'manifesto' | 'agape' | 'post';
    fileName?: string;
    onProgress?: (info: ChunkedUploadProgress) => void;
    signal?: AbortSignal;
  },
): Promise<string> {
  const uploadSessionId = newUploadSessionId();
  const ctx: UploadContext = {
    uploadSessionId,
    kind: opts.kind,
    transport: 'presigned-multipart',
    fileName: opts.fileName ?? (file?.name ?? null),
    fileSizeBytes: typeof file?.size === 'number' ? file.size : null,
    contentType: typeof file?.type === 'string' ? file.type : null,
  };

  const startedAt = Date.now();
  trackUpload('upload_started', ctx);

  // Held outside the try so the catch can abort the S3 upload and the auth
  // header is available for complete/abort.
  let token = '';
  let created: { key: string; uploadId: string; cloud: string } | null = null;
  // Which resume record this attempt owns — the catch needs it to drop the
  // record on a user cancel.
  let fingerprint: string | null = null;

  try {
    token = await requireToken();

    const allowed = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska', 'video/x-msvideo', 'video/avi'];
    const contentType: string = file?.type && allowed.includes(file.type) ? file.type : 'video/mp4';
    ctx.contentType = contentType;

    // Resolve the bytes as a Blob — a web Blob passes through; a native {uri} is read.
    let blob: Blob;
    if (typeof Blob !== 'undefined' && file instanceof Blob) {
      blob = file;
    } else if (file?.uri) {
      blob = await (await fetch(file.uri)).blob();
    } else {
      throw new UploadError('Unsupported file for upload');
    }
    ctx.fileSizeBytes = blob.size;
    // The 8 GB product cap still applies (multipart can carry far more, but the
    // cap is a product decision, not a transport one).
    assertVideoWithinSizeLimit(blob.size, ctx);

    const { partSize, partCount } = planMultipartParts(blob.size);

    // 1. Open the multipart upload — or re-open the one an interrupted attempt
    //    at this same file left behind, so its parts aren't uploaded twice.
    fingerprint = uploadFingerprint(opts.kind, file, blob.size);
    const opened = await openOrResumeMultipart({
      kind: opts.kind,
      contentType,
      fingerprint,
      fileSizeBytes: blob.size,
      partSize,
      partCount,
      token,
      signal: opts.signal,
    });
    const { key, uploadId, cloud } = opened.handle;
    const publicUrl = opened.publicUrl;
    const urlFor = opened.urlFor;
    created = opened.handle;

    // 2. Upload parts through the adaptive worker pool. Progress is byte-
    //    accurate: each part's XHR upload feeds a per-part counter, summed
    //    across all parts so parallel PUTs can't double-count.
    const bytesTotal = blob.size;
    const partProgress = new Array<number>(partCount + 1).fill(0);
    const completed: { partNumber: number; eTag: string }[] = [];
    let pacer: PacerState = { concurrency: 1, baseline: null, prevThroughput: null };
    let lastThroughput: number | null = null;
    let windowStart = Date.now();
    let lastWindowBytes = 0;

    // Parts S3 already holds count as uploaded: the bar resumes at the
    // percentage the upload really reached, and they go into the ETag list the
    // completion is assembled from. `lastWindowBytes` starts there too, or the
    // first throughput window would read the resumed bytes as just-sent and
    // report a wildly inflated speed (and ETA).
    for (const p of opened.alreadyUploaded) {
      partProgress[p.partNumber] = partByteLength(p.partNumber, partSize, blob.size);
      completed.push({ partNumber: p.partNumber, eTag: p.eTag });
    }

    const sumBytes = () => {
      let s = 0;
      for (let i = 1; i <= partCount; i++) s += partProgress[i];
      return Math.min(bytesTotal, s);
    };
    lastWindowBytes = sumBytes();
    if (opened.alreadyUploaded.length > 0) {
      trackUpload('upload_resumed', ctx, {
        beSessionId: uploadId,
        bytesUploaded: lastWindowBytes,
        resumedPartCount: opened.alreadyUploaded.length,
        partCount,
      });
    }
    const emitProgress = () => {
      const bytesUploaded = sumBytes();
      const percent = bytesTotal > 0 ? Math.min(100, (bytesUploaded / bytesTotal) * 100) : 0;
      const etaSeconds = lastThroughput && lastThroughput > 0
        ? Math.max(0, Math.round((bytesTotal - bytesUploaded) / lastThroughput))
        : null;
      opts.onProgress?.({ bytesUploaded, bytesTotal, percent, throughputBytesPerSec: lastThroughput, etaSeconds });
    };
    const maybeCloseWindow = () => {
      const now = Date.now();
      const elapsed = now - windowStart;
      if (elapsed < PACER_WINDOW_MS) return;
      const b = sumBytes();
      lastThroughput = ((b - lastWindowBytes) * 1000) / elapsed;
      pacer = decideNextConcurrency(pacer, lastThroughput);
      windowStart = now;
      lastWindowBytes = b;
      trackUpload('upload_progress', ctx, {
        beSessionId: uploadId,
        bytesUploaded: b,
        percent: bytesTotal > 0 ? Math.round((b / bytesTotal) * 100) : 0,
        throughputBytesPerSec: lastThroughput,
        concurrency: pacer.concurrency,
      });
    };

    // Re-mint the presigned URL for one part — used after a failure, in case
    // the URL expired (403) mid-upload.
    const refreshPartUrl = async (n: number) => {
      try {
        const fresh = (await mintMultipartPartUrls(opened.handle, [n], token)).get(n);
        if (fresh) urlFor.set(n, fresh);
      } catch { /* keep the old URL and let the retry surface the real error */ }
    };

    // PUT one part with up to 3 retries. Aborts surface immediately (no retry).
    const putPart = async (n: number): Promise<void> => {
      const start = (n - 1) * partSize;
      const end = Math.min(start + partSize, blob.size);
      const chunk = blob.slice(start, end);
      let retries = 0;
      while (true) {
        // Array, not a `let` — TypeScript can't see an assignment made inside
        // the promise executor and narrows a nullable local to `never` by the
        // time `finally` runs.
        const cleanups: Array<() => void> = [];
        try {
          const eTag = await new Promise<string>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', urlFor.get(n)!);
            xhr.upload.onprogress = (e) => {
              if (!e.lengthComputable) return;
              partProgress[n] = e.loaded;
              maybeCloseWindow();
              emitProgress();
            };
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                // ETag is exposed to the browser by the bucket CORS
                // (ExposeHeaders: ETag). Without it we can't complete.
                const et = xhr.getResponseHeader('ETag');
                if (!et) { reject(new UploadError('S3 part response missing ETag (bucket CORS?)')); return; }
                partProgress[n] = end - start;
                emitProgress();
                resolve(et);
              } else {
                reject(new UploadError(`S3 part ${n} failed: ${xhr.status}`, xhr.status));
              }
            };
            xhr.onerror = () => reject(new UploadError('S3 part network error'));
            xhr.onabort = () => { const e: any = new Error('Aborted'); e.name = 'AbortError'; reject(e); };
            if (opts.signal) {
              // Reject, don't just return: `abort()` on an opened-but-unsent
              // XHR fires no `abort` event, so returning would leave this
              // promise pending forever. With hundreds of queued parts, a
              // cancel lands here for every part that hadn't started yet — the
              // pool would then never drain, the upload would never settle, and
              // the abort request below would never be sent (leaving the parts
              // in the bucket, which is exactly what this ticket set out to
              // avoid).
              if (opts.signal.aborted) {
                const e: any = new Error('Aborted');
                e.name = 'AbortError';
                reject(e);
                return;
              }
              // Removed on settle — an 8 GB upload runs ~512 parts through this
              // path, and with retries the listeners would pile up on one signal.
              const onAbort = () => xhr.abort();
              const signal = opts.signal;
              signal.addEventListener('abort', onAbort);
              cleanups.push(() => signal.removeEventListener('abort', onAbort));
            }
            xhr.send(chunk);
          });
          completed.push({ partNumber: n, eTag });
          return;
        } catch (err: any) {
          if (err?.name === 'AbortError') throw err;
          retries++;
          trackUpload('upload_chunk_retry', ctx, {
            beSessionId: uploadId,
            chunkIndex: n,
            attempt: retries,
            httpStatus: err instanceof UploadError ? err.status ?? null : null,
          });
          if (retries >= 3) {
            partProgress[n] = 0;
            const status = err instanceof UploadError ? err.status : undefined;
            throw new UploadError(`Part ${n} failed after 3 retries: ${err?.message}`, status, { cause: err });
          }
          partProgress[n] = 0;
          await refreshPartUrl(n);
          await new Promise((r) => setTimeout(r, 1000 * retries));
        } finally {
          while (cleanups.length) cleanups.pop()!();
        }
      }
    };

    // Worker pool — up to `pacer.concurrency` part PUTs at a time. Only the
    // parts S3 is still missing are queued; on a fresh upload that is all of them.
    const queue = missingParts(partCount, opened.alreadyUploaded.map((p) => p.partNumber));
    let cursor = 0;
    const inFlight = new Set<Promise<void>>();
    while (cursor < queue.length || inFlight.size > 0) {
      while (inFlight.size < pacer.concurrency && cursor < queue.length) {
        const n = queue[cursor++];
        const p = putPart(n);
        const tracked = p.finally(() => { inFlight.delete(tracked); });
        inFlight.add(tracked);
      }
      if (inFlight.size === 0) break;
      await Promise.race(inFlight);
    }

    // 3. Complete — assemble the object from the collected ETags.
    const completeRes = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/multipart/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        key,
        uploadId,
        cloud,
        parts: completed
          .sort((a, b) => a.partNumber - b.partNumber)
          .map((p) => ({ partNumber: p.partNumber, eTag: p.eTag })),
      }),
      signal: opts.signal,
    }, 120_000);
    if (!completeRes.ok) throw new UploadError(await readErrorMessage(completeRes, 'Multipart complete failed'), completeRes.status);
    const completeBody = await completeRes.json().catch(() => ({} as any));
    // The upload is committed — clear `created` so the catch doesn't abort it,
    // and drop the resume record so the next upload of the same file starts fresh.
    created = null;
    await clearResumableUpload(fingerprint);

    trackUpload('upload_completed', ctx, {
      beSessionId: uploadId,
      durationMs: Date.now() - startedAt,
      bytesUploaded: ctx.fileSizeBytes,
    });
    return completeBody.publicUrl ?? publicUrl;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      trackUpload('upload_cancelled', ctx, { durationMs: Date.now() - startedAt });
    } else {
      trackUploadError('upload_failed', ctx, err, { durationMs: Date.now() - startedAt });
    }
    // What happens to the parts already in S3 depends on WHY we're here.
    //
    //   - The user cancelled: they are not coming back to this upload, so abort
    //     it and drop the record — otherwise the parts sit in the bucket being
    //     billed until the TTL reaps them.
    //   - Anything else (connection dropped, a part gave up after its retries,
    //     complete failed): keep both. Those parts ARE the resume — aborting
    //     here is precisely what used to force «заливку с нуля» on the retry.
    //     If the user never retries, the next upload reaps this record (or the
    //     bucket's lifecycle rule does).
    if (err?.name === 'AbortError') {
      if (created && token) await abortMultipartQuietly(created, token);
      if (fingerprint) await clearResumableUpload(fingerprint);
    }
    throw err;
  }
}

/**
 * CORE-904 — upload a master video, routing small files through the proven
 * single presigned PUT (CORE-232) and large ones through S3 multipart
 * (resume-capable, no 5 GiB ceiling). The two call sites (web
 * PracticeUploadModal, native useUploadPractice) go through here so the routing
 * lives in one place.
 */
export async function uploadMasterVideo(
  file: any,
  opts: {
    kind: 'practice' | 'manifesto' | 'agape' | 'post';
    fileName?: string;
    onProgress?: (info: ChunkedUploadProgress) => void;
    signal?: AbortSignal;
  },
): Promise<string> {
  const knownSize = typeof file?.size === 'number' ? file.size : null;
  if (shouldUseMultipart(knownSize)) return presignAndMultipartVideo(file, opts);

  // Native {uri}: the size isn't known until the blob is read. Resolve it, then
  // route — so a large native practice still gets multipart.
  if (knownSize == null && file?.uri) {
    const blob = await (await fetch(file.uri)).blob();
    const routed = { ...opts, fileName: opts.fileName ?? file.name };
    return shouldUseMultipart(blob.size)
      ? presignAndMultipartVideo(blob, routed)
      : presignAndPutVideo(blob, routed);
  }

  return presignAndPutVideo(file, opts);
}

/**
 * CORE-315 — upload a practice cover image straight to S3 via a presigned PUT,
 * exactly like the video binary (CORE-232). The cover used to ride inside the
 * createVideo multipart POST; a native-res frame from an iPad clip is hundreds
 * of KB, which chokes the RU origin's ~128 KB upload shaping and hangs the
 * create. Pushing it to S3 (whose IP range isn't shaped) keeps the create POST
 * a few KB of text. Returns the public URL to commit as CoverImageUrl.
 */
export async function presignAndPutCover(
  blob: Blob,
  opts: { signal?: AbortSignal } = {},
): Promise<string> {
  const token = await requireToken();

  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  const contentType = blob.type && allowed.includes(blob.type) ? blob.type : 'image/jpeg';

  const presignRes = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind: 'cover', contentType }),
    signal: opts.signal,
  });
  // CORE-415 — see presignAndPutVideo: map a 401 to the typed auth error.
  if (presignRes.status === 401) throw new AuthRequiredError();
  if (!presignRes.ok) throw new Error(await readErrorMessage(presignRes, 'Cover presign failed'));
  const { uploadUrl, publicUrl, contentType: signedType } = await presignRes.json();

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', signedType || contentType);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UploadError(`Cover S3 upload failed: ${xhr.status}`, xhr.status));
    };
    xhr.onerror = () => reject(new UploadError('Cover S3 upload network error'));
    xhr.onabort = () => { const e: any = new Error('Aborted'); e.name = 'AbortError'; reject(e); };
    if (opts.signal) {
      if (opts.signal.aborted) { xhr.abort(); return; }
      opts.signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(blob);
  });

  return publicUrl;
}

/**
 * CORE-450 — upload a community-feed photo straight to S3 via a presigned PUT,
 * exactly like the practice cover (CORE-315) and video (CORE-232). Feed photos
 * used to ride the createPost multipart body, which the RU origin's ~128 KB
 * upload-body shaping stalls: measured on prod, a single feed photo hung 60 s on
 * api AND api-ru, while a 24 MB video presigned to S3 in ~5 s from the SAME
 * device. Pushing the photo bytes to S3 leaves createPost a few string fields.
 *
 * PUTs to the primary cloud (Yandex for RU per the presign's geo routing) and,
 * if that PUT fails, retries the AWS-S3 fallback URL the presign returns for the
 * same key. Returns the public URL to commit as one of the post's ImageUrls.
 */
export async function presignAndPutPostImage(
  file: any,
  opts: { signal?: AbortSignal; kind?: 'post-image' | 'event-cover' } = {},
): Promise<string> {
  // 1. Resolve the bytes first — a web Blob passes through; a native {uri} is
  //    read. Before the presign, because what these bytes ARE decides how the
  //    object gets named.
  let blob: Blob;
  if (typeof Blob !== 'undefined' && file instanceof Blob) {
    blob = file;
  } else if (file?.uri) {
    blob = await (await fetch(file.uri)).blob();
  } else {
    throw new UploadError('Unsupported file for upload');
  }

  // 2. CORE-338 — convert what the feed cannot show, instead of relabelling it.
  //    This line used to fall back to `image/jpeg` for ANY type outside the
  //    allow-list, so an iPhone HEIC was presigned, stored and served as a JPEG
  //    it never was: the upload succeeds, the post publishes, and the tile is
  //    blank in every browser but Safari — no error anywhere. iOS now hands us
  //    JPEG (the picker asks for the compatible representation), and on web we
  //    re-encode whatever the browser can decode. What is left — HEIC in Chrome
  //    or Firefox — needs the server-side convert (CORE-677); refuse it until
  //    then rather than publish a blank tile.
  //    CORE-722 — the decision itself now lives in `preparePostPhoto`, so the
  //    composer can ask the same question at pick time and refuse the file
  //    while the author is still looking at it. A refusal is a PhotoFormatError:
  //    createPost must let it out rather than treat it as a presign hiccup.
  let contentType: string =
    file?.type && POST_IMAGE_TYPES.includes(file.type) ? file.type : 'image/jpeg';
  const prepared = await preparePostPhoto(blob, file?.type);
  if (prepared !== blob) {
    blob = prepared;
    contentType = 'image/jpeg';
  }

  // 3. Only now ask for credentials. CORE-722 — the token used to be fetched
  //    first, so a file we were always going to refuse could fail on the
  //    session instead of on the format, and the author got «Сессия истекла»
  //    about a photo. Judge the file, then go to the network.
  const token = await requireToken();

  // 4. Mint the presigned PUT URL(s) — a tiny request, fast even on the shaped path.
  const presignRes = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind: opts.kind ?? 'post-image', contentType }),
    signal: opts.signal,
  });
  // CORE-415 — a 401 here means the JWT expired; surface the typed auth error.
  if (presignRes.status === 401) throw new AuthRequiredError();
  if (!presignRes.ok) throw new UploadError(await readErrorMessage(presignRes, 'Presign failed'), presignRes.status);
  const {
    uploadUrl, publicUrl, contentType: signedType,
    fallbackUploadUrl, fallbackPublicUrl,
  } = await presignRes.json();

  // 5. PUT straight to S3 (bypasses the shaped origin). Try the primary cloud
  //    (Yandex for RU); on failure fall back to AWS S3 for the same key. No
  //    Authorization header — the presigned URL carries its own credentials.
  const putTo = async (url: string): Promise<Response> =>
    fetchWithTimeout(url, {
      method: 'PUT',
      headers: { 'Content-Type': signedType || contentType },
      body: blob,
      signal: opts.signal,
    }, 120_000);

  try {
    const res = await putTo(uploadUrl);
    if (!res.ok) throw new UploadError(`S3 upload failed: ${res.status}`, res.status);
    return publicUrl;
  } catch (primaryErr: any) {
    // A caller-initiated cancel is intentional — never retry it onto the fallback.
    if (primaryErr?.name === 'AbortError') throw primaryErr;
    if (!fallbackUploadUrl || !fallbackPublicUrl) throw primaryErr;
    const res = await putTo(fallbackUploadUrl);
    if (!res.ok) throw new UploadError(`S3 fallback upload failed: ${res.status}`, res.status);
    return fallbackPublicUrl;
  }
}

/**
 * CORE-975 — an event cover used to ride the createEvent multipart body, which
 * the RU origin's ~128 KB upload-body shaping stalls. Measured from Moscow
 * against prod on 19.08.2026: a 10 KB body answers in 0.17 s, while 128 KB,
 * 512 KB and 2 MB ones all die at ~128 KB uploaded with no response at all —
 * exactly the report «без фото событие создаётся, с фото ERR_TIMED_OUT».
 * Same treatment as feed photos (CORE-450): the bytes go straight to S3 and the
 * event carries only the URL. Returns the public URL to commit as CoverImageUrl.
 */
export async function presignAndPutEventCover(
  file: any,
  opts: { signal?: AbortSignal } = {},
): Promise<string> {
  return presignAndPutPostImage(file, { ...opts, kind: 'event-cover' });
}

/**
 * CORE-975 — presign an event cover, or answer null when the bytes could not be
 * put in S3, so the caller can fall back to the legacy multipart field: a
 * presign outage must degrade to the old (shaped) behaviour, never block
 * publishing. A format verdict is NOT a transport failure — re-sending a file we
 * already refused would make the master wait out a second upload only to be told
 * «проверьте введённые данные» by the API's own 400 (the CORE-722 lesson), so
 * PhotoFormatError is let out.
 */
async function presignEventCoverOrNull(file: any): Promise<string | null> {
  try {
    return await presignAndPutEventCover(file);
  } catch (err: any) {
    if (err instanceof PhotoFormatError) throw err;
    return null;
  }
}

/**
 * CORE-1693 — `RepostOf.*` в multipart-форме создания публикации (BE CORE-1709).
 * Одни и те же поля у мастера и у обычного пользователя.
 */
function appendRepostOf(fd: FormData, target: RepostTarget | undefined): void {
  if (!target) return;
  fd.append('RepostOf.Kind', target.kind);
  fd.append('RepostOf.Id', String(target.id));
  if (target.source) fd.append('RepostOf.Source', target.source);
}

/** CORE-2092 — после неудачного обновления не пробовать снова на каждом запросе. */
const JWT_FRESHEN_RETRY_MS = 60 * 1000;

class ApiService {
  /**
   * `freshen: false` — только для самого `refreshJwt`: он шлёт текущий токен
   * как удостоверение и не должен ждать сам себя.
   */
  private async getHeaders(includeAuth = true, freshen = true): Promise<HeadersInit> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Timezone': timezone,
    };

    let attachedAuth = false;
    if (includeAuth) {
      const token = freshen ? await this.getAuthToken() : await storage.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        attachedAuth = true;
      }
    }

    // SCRUM-31/36 — for unauthenticated requests, send the persisted
    // anon-id (if any) so the BE can keep the same Agora UID + Stream
    // Chat user across page reloads. We don't send it together with
    // an Authorization header — the BE prefers JWT and ignores anon
    // for authed callers anyway.
    if (!attachedAuth) {
      const anonId = await storage.getAnonUserId();
      if (anonId) {
        headers['X-Anon-User-Id'] = anonId;
      }
    }

    return headers;
  }

  private async handleResponse<T>(response: Response, skipAuthRedirect = false): Promise<T> {
    if (!response.ok) {
      // 401 with a stored token = expired/revoked session → clear and reset to /login.
      // 401 without a token = guest hit an auth-only endpoint; the caller decides
      // (show CTA, render empty, etc.). Resetting nav for guests destroys the
      // back-stack and traps them on Home after closing /login.
      //
      // CORE-2098 — на вебе 401 с ИСТЁКШИМ токеном (или с токеном, обновить
      // который сервер отказался) не выкидывает на вход: `sessionExpiry`
      // поднимает спокойное «войдите снова», и вход открывается по кнопке с
      // возвратом сюда же. С `skipAuthRedirect` вызывающий 401 разбирает сам
      // (визард Live паркует черновик, CORE-1587) — туда окно не лезет.
      if (response.status === 401 && !skipAuthRedirect) {
        const currentToken = await storage.getToken();
        if (currentToken && !(SESSION_EXPIRY_PROMPT_ENABLED && noteUnauthorized(currentToken))) {
          await storage.removeToken();
          const { navigateToLogin } = require('../navigation/rootNavigation');
          navigateToLogin();
        }
      }

      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        // BE didn't return a parseable body — keep `status` on the
        // thrown error so callers (and `friendlyApiError` in
        // src/services/errorMessages.ts) can map it to friendly Russian
        // copy at the UI boundary. The technical English template used
        // to leak directly into user-facing Alerts.
        error = { message: 'Не удалось обработать ответ сервера.' };
      }
      error.status = response.status;
      // CORE-1999 — which request failed, for captureError's grouping.
      // Non-enumerable: stays out of spreads, JSON and `toEqual`.
      try {
        Object.defineProperty(error, 'apiRequest', {
          value: requestOfResponse.get(response) ?? { url: response.url },
          enumerable: false,
          configurable: true,
        });
      } catch {
        // frozen / odd body — grouping falls back to the flow tag
      }
      // CORE-1901 — сколько сервер просит подождать после 429. На вебе с
      // другого домена заголовок виден, только если бэкенд его выставил в
      // Access-Control-Expose-Headers; нет его — пауза берётся из лестницы.
      if (response.status === 429) {
        const retryAfterMs = parseRetryAfterMs(response.headers?.get?.('Retry-After'));
        if (retryAfterMs != null) error.retryAfterMs = retryAfterMs;
      }
      throw error;
    }

    // AMB-186 — capture the X-Anon-User-Id header BE returns on any
    // anonymous response. The dedicated /api/anon/identity endpoint
    // already does this, but anonymous users hitting room endpoints
    // first (Telegram WebView paste a /room/abc link) used to roll a
    // fresh anon-id on every reload because nothing here persisted it.
    // The stable id is what lets BE keep the same Agora UID + Stream
    // Chat user across reloads and treat the spectator as «the same
    // person» across hub reconnects. Fire-and-forget so the response
    // path stays sync-fast. `headers` is optional-chained because some
    // mocked Response objects in tests don't include it.
    const anonHeader = response.headers?.get?.('X-Anon-User-Id');
    if (anonHeader) {
      storage.getAnonUserId()
        .then((current) => current !== anonHeader ? storage.setAnonUserId(anonHeader) : undefined)
        .catch(() => { /* non-fatal */ });
    }

    // Handle empty responses (204 No Content, etc.)
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    return JSON.parse(text);
  }

  // ── AMB-198: silent refresh + retry wrapper ───────────────────────────
  //
  // Wraps a single authenticated request with one-shot 401-or-403 → refresh
  // → retry. Used by long-session API methods (room join/leave, heartbeat,
  // /me, dashboard listings) so a JWT that expired mid-session doesn't
  // kick the user out of a live эфир or practice room. Methods that opt
  // in by calling `authedRequest` instead of bare `fetch + handleResponse`.
  //
  // Why opt-in (not global): there are 200+ fetch callsites in this file
  // and many of them are auth-light (anon endpoints, login flows, public
  // browse) where 401-retry doesn't apply or would be wrong. Migrating
  // the hot paths only keeps the blast radius small.
  //
  // Why also retry on 403: BE `[Authorize(Roles="…")]` reads role claims
  // off the JWT, but the FE's `hasRole()` reads `user.roles` from /me —
  // which is the DB state. Admins promote/demote users out-of-band
  // (e.g. master-application approval), so a cached JWT can lack the
  // role even though the DB has it. `/Auth/refresh` re-reads roles from
  // DB, so one transparent refresh+retry resolves the mismatch without
  // forcing the user to log out + log in. If the role really is missing
  // in DB the retry returns the same 403 and we surface it normally.
  //
  // Flow on 401 or 403:
  //   1. Confirm there IS a token in storage (otherwise it's a guest hit
  //      on an authed endpoint — let the original status flow through).
  //   2. Call refreshJwt() (single-flight; concurrent retries share one
  //      network roundtrip).
  //   3. If a fresh token came back, retry the original request once with
  //      it. Build new headers from storage so Authorization gets the
  //      refreshed value automatically.
  //   4. If refresh failed (null), fall through to handleResponse which
  //      will clear the token and redirect to /login (on 401) or surface
  //      the 403 to the caller.
  private async retryWithRefresh(
    fetcher: () => Promise<Response>,
    response: Response,
  ): Promise<Response> {
    if (response.status !== 401 && response.status !== 403) return response;
    // CORE-1829 — a session capability refusal does not depend on the token.
    if (await isCapabilityRefusal(response)) return response;
    const currentToken = await storage.getToken();
    if (!currentToken) return response;
    const newToken = await this.refreshJwt();
    if (!newToken) return response;
    return fetcher();
  }

  private async authedRequest<T>(
    url: string,
    init: RequestInit = {},
    skipAuthRedirect = false,
  ): Promise<T> {
    const buildInit = async (): Promise<RequestInit> => {
      const headers = await this.getHeaders(true);
      // Preserve any caller-provided headers (Content-Type body overrides etc).
      return {
        ...init,
        headers: { ...headers, ...(init.headers as Record<string, string> | undefined ?? {}) },
      };
    };

    const fetcher = async () => fetch(url, await buildInit());
    // CORE-2113 — GET/HEAD, на который шлюз ответил 502/503/504 (окно
    // выкатки), повторяем на нативе; на вебе это делает apiFallback (CORE-604).
    const method = (init.method || 'GET').toUpperCase();
    const idempotent = method === 'GET' || method === 'HEAD';
    let response = idempotent && Platform.OS !== 'web'
      ? await retryOnGateway(fetcher)
      : await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<T>(response, skipAuthRedirect);
  }

  /**
   * CORE-1200 — `correlationId` едет заголовком, чтобы серверная запись села
   * рядом с клиентской. Он же короткий код, который видит человек: по одной
   * строке находится и попытка на клиенте, и её след на бэке — а если следа
   * нет, значит запрос до бэка не дошёл, и это ответ, а не пробел в данных.
   */
  async login(data: LoginRequest, correlationId?: string): Promise<LoginResponse> {
    const headers = await this.getHeaders(false);
    if (correlationId) (headers as Record<string, string>)[AUTH_CORRELATION_HEADER] = correlationId;
    // CORE-2113 — вход повторяем на 502/503/504 шлюза (окно выкатки API,
    // GlitchTip #176): запрос до бэкенда не дошёл, повторный вход ничего не создаёт.
    const response = await retryOnGateway(() => fetch(`${BASE_URL}/api/Auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    }));

    return this.handleResponse<LoginResponse>(response, true);
  }

  // ── Sliding JWT refresh (AMB-200) ─────────────────────────────────────
  //
  // BE issues a new JWT against the current one; we don't store a separate
  // refresh-token. Used by AuthContext on a proactive timer (~30 min before
  // exp) so long sessions — live эфир, длинная практика, телефон в кармане —
  // never see the user kicked to login. Single-flight: concurrent callers
  // (proactive timer firing at the same moment as a 401-retry) share one
  // network roundtrip. Errors return null — the caller decides whether to
  // sign out or just keep trying.

  private refreshInFlight: Promise<string | null> | null = null;
  private freshenFailedAt = 0;

  /**
   * CORE-2092 — токен для запроса, обновлённый заранее, если до истечения
   * осталось меньше `JWT_REFRESH_LEAD_MS`.
   *
   * Таймер `AuthContext` ставится на ~23,5 часа, и сон ноутбука его
   * откладывает: на проде 04.11 токен ведущей истёк посреди эфира, и загрузка
   * вложения в чат ушла с ним и получила 401, хотя за полчаса до этого были
   * десятки запросов, любой из которых мог его обновить. Проверка по часам на
   * стене перед запросом от сна не зависит.
   *
   * Обновление — общий single-flight `refreshJwt`. Не вышло — запрос идёт со
   * старым токеном (он ещё действует), а следующая попытка не раньше чем через
   * минуту, чтобы не удваивать каждый запрос.
   */
  async getAuthToken(): Promise<string | null> {
    const token = await storage.getToken();
    if (!token) return token;
    const now = Date.now();
    if (!jwtNeedsRefresh(token, now)) return token;
    if (now - this.freshenFailedAt < JWT_FRESHEN_RETRY_MS) return token;
    const fresh = await this.refreshJwt();
    if (fresh) return fresh;
    this.freshenFailedAt = Date.now();
    return token;
  }

  async refreshJwt(): Promise<string | null> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = (async () => {
      try {
        // CORE-522 — timeout the refresh. retryWithRefresh calls this when
        // createLive/updateLive return 401/403, so a stalled refresh would hang
        // the publish spinner forever with no error. On abort we fall through to
        // `return null`, and the caller surfaces the original 401 as a friendly
        // error the user can retry from — instead of an infinite step-3 loader.
        // CORE-2092 — `freshen: false`: this IS the refresh.
        const headers = await this.getHeaders(true, false);
        const sentAuth = (headers as Record<string, string>)['Authorization'];
        const response = await fetchWithTimeout(`${BASE_URL}/api/Auth/refresh`, {
          method: 'POST',
          // skipAuthRedirect=true so a 401 here doesn't trigger another
          // refresh attempt or navigate-to-login; the caller decides.
          headers,
        }, 20_000);
        if (!response.ok) {
          // CORE-2098 — сервер отказался обновить этот токен: сессии больше нет.
          if (response.status === 401 && sentAuth) markRefreshRejected(sentAuth.replace(/^Bearer /, ''));
          return null;
        }
        const data: LoginResponse = await response.json();
        if (!data?.token) return null;
        await storage.setToken(data.token);
        return data.token;
      } catch {
        return null;
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  // ── Passkey / WebAuthn ────────────────────────────────────────────────
  // Mirror the four-endpoint shape from WebAuthnController. /begin returns
  // the WebAuthn options JSON + a session id; the FE feeds the options to
  // navigator.credentials.* and ships the result back to /complete with
  // the same session id.

  async passkeyRegisterBegin(): Promise<import('../types/auth').PasskeyBeginResponse> {
    const response = await fetch(`${BASE_URL}/api/auth/passkey/register/begin`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });
    return this.handleResponse<import('../types/auth').PasskeyBeginResponse>(response);
  }

  async passkeyRegisterComplete(data: { sessionId: string; attestationResponse: any; deviceName?: string }): Promise<{ id: number; deviceName: string }> {
    const response = await fetch(`${BASE_URL}/api/auth/passkey/register/complete`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async passkeyLoginBegin(email?: string): Promise<import('../types/auth').PasskeyBeginResponse> {
    const response = await fetch(`${BASE_URL}/api/auth/passkey/login/begin`, {
      method: 'POST',
      headers: await this.getHeaders(false),
      body: JSON.stringify({ email: email ?? null }),
    });
    return this.handleResponse<import('../types/auth').PasskeyBeginResponse>(response);
  }

  async passkeyLoginComplete(data: { sessionId: string; assertionResponse: any }, correlationId?: string): Promise<LoginResponse> {
    const headers = await this.getHeaders(false);
    if (correlationId) (headers as Record<string, string>)[AUTH_CORRELATION_HEADER] = correlationId;
    const response = await fetch(`${BASE_URL}/api/auth/passkey/login/complete`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse<LoginResponse>(response, true);
  }

  // ── Social auth (CORE-108) ────────────────────────────────────────────
  // Discovery: which providers are configured + enabled on the server. The FE
  // hides buttons for providers that come back false.
  async getAuthProviders(): Promise<{ facebook: boolean; vk: boolean; google: boolean; apple: boolean }> {
    const response = await fetch(`${BASE_URL}/api/auth/providers`, {
      headers: await this.getHeaders(false),
    });
    return this.handleResponse(response, true);
  }

  // Sign in (or sign up) with a Google id_token obtained client-side.
  async loginWithGoogle(idToken: string, correlationId?: string): Promise<LoginResponse> {
    const headers = await this.getHeaders(false);
    if (correlationId) (headers as Record<string, string>)[AUTH_CORRELATION_HEADER] = correlationId;
    const response = await fetch(`${BASE_URL}/api/auth/google`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ idToken }),
    });
    return this.handleResponse<LoginResponse>(response, true);
  }

  // Sign in (or sign up) with an Apple identity token. `fullName` is only
  // present on the first authorization (Apple sends the name once).
  async loginWithApple(data: { idToken: string; fullName?: string }, correlationId?: string): Promise<LoginResponse> {
    const headers = await this.getHeaders(false);
    if (correlationId) (headers as Record<string, string>)[AUTH_CORRELATION_HEADER] = correlationId;
    const response = await fetch(`${BASE_URL}/api/auth/apple`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse<LoginResponse>(response, true);
  }

  async passkeyList(): Promise<import('../types/auth').PasskeySummary[]> {
    const response = await fetch(`${BASE_URL}/api/auth/passkey/list`, {
      headers: await this.getHeaders(),
    });
    return this.handleResponse<import('../types/auth').PasskeySummary[]>(response);
  }

  async passkeyDelete(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/auth/passkey/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    await this.handleResponse<void>(response);
  }

  // CORE-1806 — personal keys for the user MCP (own sessions, transcripts, chat).
  async mcpUserAccess(): Promise<import('../types/auth').McpUserAccess> {
    const response = await fetch(`${BASE_URL}/api/me/mcp/access`, {
      headers: await this.getHeaders(),
    });
    return this.handleResponse<import('../types/auth').McpUserAccess>(response);
  }

  async mcpUserKeys(): Promise<import('../types/auth').McpUserKey[]> {
    const response = await fetch(`${BASE_URL}/api/me/mcp/keys`, {
      headers: await this.getHeaders(),
    });
    return this.handleResponse<import('../types/auth').McpUserKey[]>(response);
  }

  async mcpUserMintKey(label?: string): Promise<import('../types/auth').McpUserKey> {
    const response = await fetch(`${BASE_URL}/api/me/mcp/keys`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify({ label: label || null }),
    });
    return this.handleResponse<import('../types/auth').McpUserKey>(response);
  }

  async mcpUserRevokeKey(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/me/mcp/keys/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    await this.handleResponse<void>(response);
  }

  async register(data: RegisterRequest, correlationId?: string): Promise<void> {
    // Default the language from the i18next instance if the caller didn't
    // pass one. Lazy-import to avoid pulling i18n into every api consumer
    // (notably tests) and circular-imports through the i18n module.
    let language = data.language;
    if (!language) {
      try {
        const { getCurrentLanguage } = await import('../i18n');
        language = getCurrentLanguage();
      } catch {
        // i18n unavailable (e.g. some unit-test environments) — let BE
        // fall back to English. Don't block registration on this.
      }
    }

    const headers = await this.getHeaders(false);
    if (correlationId) (headers as Record<string, string>)[AUTH_CORRELATION_HEADER] = correlationId;
    const response = await fetch(`${BASE_URL}/api/Auth/register`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...data, language }),
    });

    await this.handleResponse<void>(response);
  }

  async updateLanguage(language: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/user/language`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify({ language }),
    });
    await this.handleResponse<void>(response);
  }

  /**
   * CORE-1436 — reports the device time zone (IANA id). The server writes
   * notification times in it for users whose profile zone is «по системе».
   */
  async updateDeviceTimezone(timezone: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/user/device-timezone`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: JSON.stringify({ timezone }),
    });
    await this.handleResponse<void>(response);
  }

  /**
   * CORE-447 — requests deletion of the signed-in user's own account.
   *
   * Scoped to the caller by design: there is no id parameter, so this can't be
   * aimed at somebody else. The account is hidden and signed out straight away;
   * the data itself is destroyed once the grace window closes, which is what
   * `purgeAfterUtc` reports back so the UI doesn't hard-code the number.
   */
  async requestAccountDeletion(): Promise<{ purgeAfterUtc: string; graceWindowDays: number }> {
    const response = await fetch(`${BASE_URL}/api/account`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    return this.handleResponse<{ purgeAfterUtc: string; graceWindowDays: number }>(response);
  }

  /**
   * CORE-1931 — «Выйти» на экране согласия после входа через Google/Apple:
   * сервер удаляет только что созданный аккаунт, который согласия не дал, сразу и
   * без окна ожидания. Аккаунт с любым согласием или любыми действиями сервер не
   * трогает и отвечает 409 — вызывающему это безразлично, он всё равно выходит.
   */
  async deleteUnconsentedAccount(): Promise<void> {
    await this.authedRequest<unknown>(`${BASE_URL}/api/account/unconsented`, { method: 'DELETE' });
  }

  // ── CORE-1404 — согласия ──────────────────────────────────────────────────

  async grantConsent(input: {
    // CORE-1576 — `ai_data` отдельным видом: в разговоре с Агапеллой человек
    // рассказывает о самочувствии, а это специальная категория (ст.10 152-ФЗ),
    // и общего согласия при регистрации на неё не хватает.
    type: 'personal_data' | 'cookies' | 'distribution' | 'ai_data' | 'marketing';
    documentVersion: string;
    /** Для cookie: «analytics=yes;marketing=no» (CORE-1659) или прежние accepted / declined. */
    value?: string;
    /** CORE-1660 — принятые редакции: документ, версия, вариант и SHA-256 текста из реестра. */
    documents?: AcceptedDocument[];
    source: string;
  }): Promise<void> {
    await this.authedRequest<unknown>(`${BASE_URL}/api/consents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async revokeConsent(type: 'cookies' | 'distribution' | 'ai_data' | 'marketing'): Promise<void> {
    await this.authedRequest<unknown>(`${BASE_URL}/api/consents/${type}/revoke`, { method: 'POST' });
  }

  /**
   * CORE-1576 — действующие согласия с сервера.
   *
   * Нужен, потому что источник правды переехал: согласие на данные ИИ живёт на
   * сервере, и чат-сервис решает по нему, можно ли запоминать. Локальное
   * хранилище осталось кэшем — оно не знает ни про другое устройство, ни про
   * отзыв, сделанный не здесь.
   */
  async getActiveConsentTypes(): Promise<string[]> {
    const body = await this.authedRequest<{ active?: { type?: string }[] }>(
      `${BASE_URL}/api/consents`, { method: 'GET' });
    return (body?.active || []).map((c) => c?.type || '').filter(Boolean);
  }

  /** Возрастной выбор на экране согласия. `under14` — 403 AGE_NOT_ALLOWED, аккаунт удаляется. */
  /**
   * CORE-1401 — удаление всего, что ИИ знает о человеке.
   *
   * Через общий клиент, а не голым `fetch`: по замечанию ревью 02.10 своя
   * обёртка проходила мимо обновления токена и RU-пути, то есть у человека в
   * России необратимое действие могло просто не доехать — и он увидел бы
   * ошибку там, где на самом деле ничего не пробовали.
   *
   * `chatServiceReached` возвращается наверх как есть: по нему вызывающий
   * отличает «стёрто всё» от «стёрта только основная база» (CORE-1577).
   */
  async eraseAiData(): Promise<{ removedRows: number; chatServiceReached: boolean }> {
    const body = await this.authedRequest<{ removedRows?: number; chatServiceReached?: boolean }>(
      `${BASE_URL}/api/user/ai-data`, { method: 'DELETE' });
    return {
      removedRows: Number(body?.removedRows ?? 0),
      // Отсутствие поля — не «дошли». Старый бэкенд его не отдаёт, и считать
      // молчание успехом значило бы вернуть ровно тот отказ, выглядящий как
      // успех, ради которого поле и заводили.
      chatServiceReached: body?.chatServiceReached === true,
    };
  }

  async declareAge(ageGroup: 'adult' | 'teen' | 'under14'): Promise<void> {
    await this.authedRequest<unknown>(`${BASE_URL}/api/consents/age`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ageGroup }),
    });
  }

  async resendConfirmation(email: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/Auth/resend-confirmation`, {
      method: 'POST',
      headers: await this.getHeaders(false),
      body: JSON.stringify({ email }),
    });

    await this.handleResponse<void>(response);
  }

  /**
   * CORE-647 — возвращает код исхода, а не void: экран показывает четыре разных
   * состояния (подтвердили, уже подтверждён, ссылка устарела, ссылка битая), и
   * различать их по тексту сообщения — способ однажды показать не то.
   */
  async confirmEmail(
    userId: string,
    token: string,
  ): Promise<{ code?: string; message?: string }> {
    const params = new URLSearchParams({ userId, token });
    const response = await fetch(`${BASE_URL}/api/Auth/confirm-email?${params}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });

    return this.handleResponse<{ code?: string; message?: string }>(response);
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<PasswordResetResponse> {
    const response = await fetch(`${BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: await this.getHeaders(false),
      body: JSON.stringify(data),
    });

    return this.handleResponse<PasswordResetResponse>(response);
  }

  async resetPassword(data: ResetPasswordRequest): Promise<PasswordResetResponse> {
    const response = await fetch(`${BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: await this.getHeaders(false),
      body: JSON.stringify(data),
    });

    return this.handleResponse<PasswordResetResponse>(response);
  }

  async getMe(): Promise<User> {
    // AMB-198 — silent-refresh on 401 so a stale JWT mid-session is
    // rotated transparently instead of bouncing the user to /login.
    return this.authedRequest<User>(`${BASE_URL}/api/Auth/me`, {
      method: 'GET',
    });
  }

  /**
   * AMB-222 — JSON-body partial update for the current user. Today scoped
   * to `displayName`; if other editable fields land, extend `UpdateMeRequest`
   * on the BE and add them here. The BE also pushes the new name to Stream
   * Chat after a successful update, so callers that have an open Stream
   * client should `disconnectUser` + reconnect to pick up the fresh name
   * on existing channels (see AuthContext.refreshUser).
   */
  async updateMe(input: { displayName?: string }): Promise<User> {
    return this.authedRequest<User>(`${BASE_URL}/api/Auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * AMB-224 — anonymous region lookup for gating UI before login. The
   * authenticated path reads `user.region` off `/api/auth/me` directly;
   * this endpoint exists so guest viewers (browsing /masters, public
   * profiles, etc.) can be gated too. Returns `{ country: null }` when
   * GeoIP can't resolve the IP (dev/loopback, missing DB) — callers
   * treat null as «не РФ» and show the default UX.
   */
  async getRegion(): Promise<{ country: string | null }> {
    const response = await fetch(`${BASE_URL}/api/Region`, { method: 'GET' });
    return this.handleResponse<{ country: string | null }>(response);
  }

  /**
   * CORE-188 — public «О платформе» content. Available to guests and
   * authenticated users alike (no auth header needed), so it mirrors the
   * anonymous `getRegion` shape. `lang` selects the localized payload.
   */
  async getAboutPlatform(lang: string = 'ru'): Promise<import('../types/aboutPlatform').AboutPlatformResponse> {
    const response = await fetch(
      `${BASE_URL}/api/platform/about?lang=${encodeURIComponent(lang)}`,
      { method: 'GET' },
    );
    return this.handleResponse<import('../types/aboutPlatform').AboutPlatformResponse>(response);
  }

  async updateUserProfile(data: {
    displayName?: string;
    /** CORE-1684 — ник для упоминаний; пустая строка снимает его. */
    nickname?: string;
    timezone?: string;
    avatar?: any;
    removeAvatar?: boolean;
    coverImage?: any;
    coverImageUrl?: string | null;
    removeCoverImage?: boolean;
    coverPreset?: string;
    bio?: string;
    intention?: string;
    location?: string;
    profileVisibility?: 'public' | 'private';
    calendarPrivacy?: 'public' | 'subscribers' | 'private';
    feedPrivacy?: 'public' | 'subscribers' | 'private';
  }): Promise<User> {
    const token = await storage.getToken();

    // CORE-100 — upload avatar/cover straight to S3 via a presigned URL (the RU origin
    // path is bandwidth-shaped). On any failure, fall back to the legacy multipart field
    // so this keeps working against an older backend / if S3 CORS isn't ready yet.
    let avatarKey: string | undefined;
    let coverKey: string | undefined;
    if (data.avatar && token) {
      try { avatarKey = await presignAndPutPhoto('avatar', data.avatar, token); }
      catch (e) { console.warn('[profile] presigned avatar upload failed, falling back to multipart', e); }
    }
    if (data.coverImage && token) {
      try { coverKey = await presignAndPutPhoto('cover', data.coverImage, token); }
      catch (e) { console.warn('[profile] presigned cover upload failed, falling back to multipart', e); }
    }

    const formData = new FormData();
    if (data.displayName !== undefined) formData.append('DisplayName', data.displayName);
    if (data.nickname !== undefined) formData.append('Nickname', data.nickname);
    if (data.timezone !== undefined) formData.append('Timezone', data.timezone);
    if (avatarKey) formData.append('AvatarKey', avatarKey);
    else if (data.avatar) formData.append('Avatar', data.avatar);
    if (data.removeAvatar) formData.append('RemoveAvatar', 'true');
    if (coverKey) formData.append('CoverKey', coverKey);
    else if (data.coverImage) formData.append('CoverImage', data.coverImage);
    if (data.coverImageUrl) formData.append('CoverImageUrl', data.coverImageUrl);
    if (data.removeCoverImage) formData.append('RemoveCoverImage', 'true');
    if (data.coverPreset !== undefined) formData.append('CoverPreset', data.coverPreset);
    // Empty string is a meaningful value here — it means "clear the field" —
    // so check for `undefined` rather than truthiness.
    if (data.bio !== undefined) formData.append('Bio', data.bio);
    if (data.intention !== undefined) formData.append('Intention', data.intention);
    if (data.location !== undefined) formData.append('Location', data.location);
    if (data.profileVisibility !== undefined) formData.append('ProfileVisibility', data.profileVisibility);
    if (data.calendarPrivacy !== undefined) formData.append('CalendarPrivacy', data.calendarPrivacy);
    if (data.feedPrivacy !== undefined) formData.append('FeedPrivacy', data.feedPrivacy);

    // 60 s timeout — the «Save» button on the profile editor was reported
    // to hang forever (CORE feedback, 108bhramari@gmail.com, 04.06) when
    // the multipart PUT stalled mid-upload. Without a timeout, fetch
    // never resolves, so the useEditProfileForm catch block never fires
    // and the spinner runs indefinitely. With a timeout, the user gets a
    // clear error and can retry.
    const response = await fetchWithTimeout(`${BASE_URL}/api/user/profile`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    const saved = await this.handleResponse<User>(response);
    // CORE-1479 — свежее состояние наградных реакций уходит в AuthContext,
    // какой бы экран ни сохранял профиль; а если они открылись именно сейчас —
    // человеку об этом говорят тут же.
    if (saved?.rewardReactions) {
      publishRewardReactions({
        state: saved.rewardReactions,
        justUnlocked: !!saved.profileCompletion?.rewardReactionsUnlocked,
      });
    }
    return saved;
  }

  async getMasterProfile(): Promise<MasterProfile | null> {
    const token = await storage.getToken();
    const response = await fetch(`${BASE_URL}/api/MasterProfile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 404) {
      return null;
    }

    return this.handleResponse<MasterProfile>(response);
  }

  /** CORE-1833 — owner hides (`false`) or restores (`true`) their public master
   *  profile. Idempotent. 409 `nothing_to_hide` / `hidden_by_admin` carry the
   *  current `profile` in the body; 404 `no_profile`. */
  async setMasterProfileVisibility(visible: boolean): Promise<MasterProfile> {
    const response = await fetch(`${BASE_URL}/api/MasterProfile/visibility`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ visible }),
    });
    return this.handleResponse<MasterProfile>(response);
  }

  async updateMasterProfile(data: MasterProfileUpdateRequest): Promise<MasterProfile> {
    const token = await storage.getToken();

    // CORE-100 — upload the master photo straight to S3 via a presigned URL (the origin
    // multipart path is bandwidth-shaped for RU and hangs). Fall back to multipart on any
    // failure so this keeps working against an older backend / if S3 CORS isn't ready.
    let photoKey: string | undefined;
    if (data.photo && token) {
      try { photoKey = await presignAndPutPhoto('master', data.photo, token); }
      catch (e) { console.warn('[master] presigned photo upload failed, falling back to multipart', e); }
    }

    const formData = new FormData();

    formData.append('Name', data.name);
    if (data.description !== undefined) {
      formData.append('Description', data.description);
    }
    // CORE-183 — structured «О мастере» fields.
    if (data.experience !== undefined) formData.append('Experience', data.experience);
    if (data.education !== undefined) formData.append('Education', data.education);
    if (data.uniqueness !== undefined) formData.append('Uniqueness', data.uniqueness);
    if (photoKey) {
      formData.append('PhotoKey', photoKey);
    } else if (data.photo) {
      formData.append('Photo', data.photo);
    }

    // 60 s timeout — see updateUserProfile note. The master-profile sync
    // is wrapped in an inner try/catch by `useEditProfileForm` so its
    // failures don't surface; but without a timeout the inner await
    // would still block the outer save flow indefinitely. With a
    // timeout, the inner catch logs a warning and the outer flow moves
    // on to the next step.
    const response = await fetchWithTimeout(`${BASE_URL}/api/MasterProfile`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    return this.handleResponse<MasterProfile>(response);
  }

  // ─── Become-Master application (AMB-258) ────────────────────────
  // Two-step submission triggered from BecomeMasterModal:
  //   1) POST /api/MasterProfile (multipart/form-data) — creates the
  //      profile, returns it with `id`. JSON 400s with «Name field is
  //      required» because the controller binds via `[FromForm]`.
  //   2) PUT  /api/MasterProfile/{id}/social-links — replaces the link
  //      list with whatever the master typed (single URL in our form).
  // The third step (directions) goes through `updateMasterDirections`
  // below — the same method the existing Settings tab already uses.
  // Step 1 is the critical one. Step 2 + directions are best-effort —
  // if either fails the master profile still exists in PendingReview
  // and the master can fix social/direction later from settings.
  async submitMasterApplication(data: {
    name: string;
    phone?: string;
    description?: string;
    experience?: string;
    additionalInfo?: string;
  }): Promise<MasterProfile> {
    const token = await storage.getToken();
    // Controller binds via `[FromForm]` (same as existing
    // updateMasterProfile) — JSON body returns 400 «field is required».
    // Browser sets the multipart boundary automatically; don't pass a
    // Content-Type header manually.
    const formData = new FormData();
    formData.append('Name', data.name);
    if (data.phone) formData.append('Phone', data.phone);
    if (data.description) formData.append('Description', data.description);
    if (data.experience) formData.append('Experience', data.experience);
    if (data.additionalInfo) formData.append('AdditionalInfo', data.additionalInfo);

    const response = await fetch(`${BASE_URL}/api/MasterProfile`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    return this.handleResponse<MasterProfile>(response);
  }

  // PUT replaces the entire list — for our single-field form that means
  // sending a one-element array. `platform` is a free-form string; we
  // detect it from the URL hostname so the BE / future profile UI can
  // render the right icon.
  async setMasterSocialLinks(
    masterProfileId: number,
    links: { platform: string; url: string }[],
  ): Promise<void> {
    const token = await storage.getToken();
    const response = await fetch(`${BASE_URL}/api/MasterProfile/${masterProfileId}/social-links`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ socialLinks: links }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `social-links PUT failed: ${response.status}`);
    }
  }

  async updateMasterDirections(
    masterProfileId: number,
    directions: readonly string[],
    allowedDirections: readonly string[],
  ): Promise<MasterProfileDirection[]> {
    const token = await storage.getToken();
    // Filter (drop unknown) instead of normalize (which THROWS on any direction
    // not in the allowed list). A stale/renamed direction still stored on the
    // profile would otherwise abort the whole save — the master's edits «не
    // проходят». We save the valid subset; the dropped one is gone anyway.
    const normalized = filterAvailableMasterProfileDirections(directions, allowedDirections);
    const formData = new FormData();
    normalized.forEach((direction) => formData.append('directions[]', direction));

    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/directions`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    await this.handleResponse<void>(response);
    return normalized;
  }

  async getMasterDirections(masterProfileId: number): Promise<MasterProfileDirection[]> {
    const token = await storage.getToken();
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/directions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await this.handleResponse<unknown>(response);
    return parseMasterProfileDirections(data);
  }

  async getMasterPath(masterProfileId: number): Promise<MasterProfilePathEntry[]> {
    const token = await storage.getToken();
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/path`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return parseMasterProfilePath(await this.handleResponse<unknown>(response));
  }

  async updateMasterPath(
    masterProfileId: number,
    path: readonly MasterProfilePathEntry[],
  ): Promise<MasterProfilePathEntry[]> {
    const token = await storage.getToken();
    const normalized = normalizeMasterProfilePath(path);
    const formData = new FormData();
    normalized.forEach((entry, index) => {
      formData.append(`path[${index}].date`, entry.date);
      formData.append(`path[${index}].text`, entry.text);
    });

    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/path`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    const data = await this.handleResponse<unknown>(response);
    return Array.isArray(data) || (typeof data === 'object' && data !== null && 'path' in data)
      ? parseMasterProfilePath(data)
      : normalized;
  }

  async getMasterSocialLinks(masterProfileId: number): Promise<MasterProfileSocialLink[]> {
    const response = await fetch(`${BASE_URL}/api/MasterProfile/${masterProfileId}/social-links`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return parseMasterProfileSocialLinks(await this.handleResponse<unknown>(response, true));
  }

  async updateMasterSocialLinks(
    masterProfileId: number,
    socialLinks: readonly MasterProfileSocialLink[],
  ): Promise<MasterProfileSocialLink[]> {
    const token = await storage.getToken();
    const normalized = parseMasterProfileSocialLinks(socialLinks);
    const formData = new FormData();
    normalized.forEach((socialLink, index) => {
      formData.append(`socialLinks[${index}].platform`, socialLink.platform);
      formData.append(`socialLinks[${index}].url`, socialLink.url);
    });

    // 60 s timeout — see updateUserProfile note. Social-links is small
    // (kilobytes) and usually fast, but if it stalls behind the same
    // edge shaping it'd hang the save flow.
    const response = await fetchWithTimeout(`${BASE_URL}/api/MasterProfile/${masterProfileId}/social-links`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    const data = await this.handleResponse<unknown>(response);
    return Array.isArray(data) || (typeof data === 'object' && data !== null && 'socialLinks' in data)
      ? parseMasterProfileSocialLinks(data)
      : normalized;
  }

  async getPublicMasterProfiles(params?: { search?: string; specialty?: string; sort?: 'ranked' }): Promise<PublicMasterProfile[]> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.specialty) query.append('specialty', params.specialty);
    // CORE-322 — home carousels pass sort=ranked to get only masters with
    // published material, ordered by material count. The directory/search
    // screens omit it so they keep returning every master.
    if (params?.sort) query.append('sort', params.sort);
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/MasterProfile/public${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    // Public endpoint — guests must be able to read it. A 401 here means a
    // stale token, not "session expired", so don't bounce to /login.
    return this.handleResponse<PublicMasterProfile[]>(response, true);
  }

  async getPublicMasterProfile(id: number): Promise<PublicMasterProfile> {
    const response = await fetch(`${BASE_URL}/api/MasterProfile/public/${id}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<PublicMasterProfile>(response, true);
  }

  // Public User Profile Endpoints (no auth required)
  async getPublicUsers(params?: { limit?: number; cursor?: string; search?: string }): Promise<PublicUsersListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.search) qs.set('search', params.search);
    const url = `${BASE_URL}/api/user/public${qs.toString() ? `?${qs}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });

    return this.handleResponse<PublicUsersListResponse>(response, true);
  }

  async getPublicUserProfile(userId: string): Promise<PublicUserProfile> {
    // Auth header attached if available — backend uses it to populate `isSubscribed`
    // for the requesting viewer. Guest call still works (returns 200 with isSubscribed=null).
    const response = await fetch(`${BASE_URL}/api/user/public/${userId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<PublicUserProfile>(response, true);
  }

  // List the users following `userId` (their follower list).
  // Backend endpoint: pending — see Jira ticket "Followers/Following list endpoints".
  // The FE uses the same shape as /api/user/public so a single list component renders both.
  async getUserFollowers(userId: string, params?: { limit?: number; cursor?: string }): Promise<PublicUsersListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
    const url = `${BASE_URL}/api/user/public/${encodeURIComponent(userId)}/followers${qs.toString() ? `?${qs}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PublicUsersListResponse>(response, true);
  }

  // List the users that `userId` is subscribed to (their following list).
  async getUserFollowing(userId: string, params?: { limit?: number; cursor?: string }): Promise<PublicUsersListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
    const url = `${BASE_URL}/api/user/public/${encodeURIComponent(userId)}/following${qs.toString() ? `?${qs}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PublicUsersListResponse>(response, true);
  }

  async subscribeToUser(userId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/user/${encodeURIComponent(userId)}/subscribe`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async unsubscribeFromUser(userId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/user/${encodeURIComponent(userId)}/subscribe`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // Public user content — calendar/posts/practices visible to anyone
  // (respects the user's privacy settings on the backend).
  async getPublicUserLives(userId: string, params?: { date?: string }): Promise<PublicUserLivesResponse> {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/user/public/${encodeURIComponent(userId)}/lives${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PublicUserLivesResponse>(response, true);
  }

  /**
   * CORE-1692 — одна публикация по ссылке, с проверкой доступа на момент
   * открытия (CORE-1708). Неподписанному на пост «Для подписчиков» — 403
   * `subscription_required` с мастером (без текста и медиа), удалённому —
   * 404 `post_not_found`. Разбор отказа — `services/postAccess.ts`.
   */
  async getCommunityFeedPost(postId: number): Promise<MasterPost> {
    const response = await fetch(`${BASE_URL}/api/community-feed/posts/${postId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterPost>(response, true);
  }

  async getPublicUserPosts(userId: string): Promise<MasterPost[]> {
    const response = await fetch(`${BASE_URL}/api/user/public/${userId}/posts`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterPost[]>(response, true);
  }

  /**
   * CORE-1694 — те же публикации курсорной страницей (CORE-1245): «Лента»
   * чужого профиля обычного пользователя грузит их по мере прокрутки, а не
   * всё одним ответом. Правила видимости — серверные, общие с `getPublicUserPosts`.
   */
  async getPublicUserPostsPage(
    userId: string,
    params?: { limit?: number; cursor?: string | null },
  ): Promise<PublicUserPostsPage> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.cursor) qs.append('cursor', params.cursor);
    const tail = qs.toString();
    const response = await fetch(
      `${BASE_URL}/api/user/public/${encodeURIComponent(userId)}/posts/page${tail ? `?${tail}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<PublicUserPostsPage>(response, true);
  }

  async getPublicUserPractices(userId: string): Promise<VideoPracticeItem[]> {
    const response = await fetch(`${BASE_URL}/api/user/public/${userId}/practices`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<VideoPracticeItem[]>(response, true);
  }

  async getPublicUserFavorites(userId: string): Promise<ContentItem[]> {
    const response = await fetch(`${BASE_URL}/api/user/public/${userId}/favorites`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<ContentItem[]>(response, true);
  }

  // Circle posts authored by the current user. Storage moved off
  // AsyncStorage[circle_posts_*] to the BE in 2026-04-27 — keeps the
  // public profile and the owner's Dashboard reading the same source.
  async createCirclePost(opts: {
    content: string;
    image?: Blob;
    imageFileName?: string;
    /**
     * CORE-1226 — несколько фотографий одной публикацией.
     *
     * Сервер принимает их с CORE-1244 полем `Images`; клиент до сих пор слал
     * только одиночный `Image` и тем сам ограничивал публикацию одной
     * картинкой. `image` оставлен рядом: им пользуются другие вызывающие, и
     * ломать их ради этой задачи незачем.
     */
    images?: Blob[];
    /** Имена файлов в том же порядке, что `images`. Короче — недостающие
     *  получают запасное имя. */
    imageFileNames?: string[];
    /**
     * CORE-1267 — видео, уже лежащее в хранилище: URL из
     * `presignAndPutCircleVideo`. Байты идут в S3 мимо API, сюда приезжает
     * только ссылка. Бэкенд принимает объект лишь из префикса этого же
     * пользователя.
     */
    videoUrl?: string;
    visibility?: 'public' | 'subscribers' | 'private';
    mode?: 'intention' | 'reflection' | 'repost-practice' | 'repost-event';
    repostId?: number;
    /**
     * CORE-1693 — репост любого вида (пост, анонс, событие, практика,
     * запись) с собственным комментарием в `content`. Заменяет пару
     * `mode: 'repost-*'` + `repostId`: та оставлена для уже выпущенных
     * клиентов, сервер сводит обе к одной модели.
     */
    repostOf?: RepostTarget;
    /**
     * CORE-1872 — Idempotency-Key одного намерения «опубликовать» (сервер —
     * фильтр CORE-1910): повтор с тем же ключом отдаёт первый пост. Без ключа
     * запрос идёт как раньше.
     */
    idempotencyKey?: string;
    /**
     * CORE-1477 — ход отправки и возможность её прервать. Пока их не передали,
     * запрос идёт прежним `fetch`: веб-композеры ничего не просят и работают
     * ровно как раньше.
     */
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  }): Promise<MasterPost> {
    const fd = new FormData();
    fd.append('Content', opts.content);
    if (opts.image) fd.append('Image', opts.image, opts.imageFileName || 'image.jpg');
    // Порядок важен: сервер сохраняет фотографии в том же, в каком получил, и
    // именно в этом порядке их увидит читатель.
    (opts.images ?? []).forEach((img, i) => {
      fd.append('Images', img, opts.imageFileNames?.[i] || `image-${i + 1}.jpg`);
    });
    if (opts.videoUrl) fd.append('VideoUrl', opts.videoUrl);
    if (opts.visibility) fd.append('Visibility', opts.visibility);
    if (opts.mode) fd.append('Mode', opts.mode);
    if (opts.repostId != null) fd.append('RepostId', String(opts.repostId));
    appendRepostOf(fd, opts.repostOf);

    const token = await storage.getToken();
    const url = `${BASE_URL}/api/user/circle/posts`;
    const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` };
    if (opts.idempotencyKey) headers[IDEMPOTENCY_KEY_HEADER] = opts.idempotencyKey;
    const response = (opts.onProgress || opts.signal)
      ? await uploadFormWithProgress(url, fd, headers, {
          onProgress: opts.onProgress,
          signal: opts.signal,
        })
      : await fetch(url, { method: 'POST', headers, body: fd });
    return this.handleResponse<MasterPost>(response);
  }

  async deleteCirclePost(postId: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/user/circle/posts/${postId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // Master Profile Content Endpoints
  async getMasterContentAll(masterProfileId: number): Promise<ContentItem[]> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/content/all`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<ContentItem[]>(response);
  }

  async getMasterUpcomingLives(masterProfileId: number): Promise<UpcomingLive[]> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/content/upcoming`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<UpcomingLive[]>(response);
  }

  async getMasterPastLives(masterProfileId: number): Promise<PastLive[]> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/content/past`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<PastLive[]>(response);
  }

  async getMasterVideoPractices(masterProfileId: number): Promise<VideoPractice[]> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/content/videos`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<VideoPractice[]>(response);
  }

  async getAllVideoPractices(): Promise<VideoPracticeWithMaster[]> {
    // Fetch all masters first
    const masters = await this.getPublicMasterProfiles();

    // Fetch practices from each master in parallel
    const practicesPromises = masters.map(async (master) => {
      try {
        const practices = await this.getMasterVideoPractices(master.id);
        return practices.map((practice) => ({
          ...practice,
          masterName: master.name,
          masterProfileId: master.id,
        }));
      } catch {
        return [];
      }
    });

    const practicesArrays = await Promise.all(practicesPromises);
    const allPractices = practicesArrays.flat();

    // Sort by publishedAtUtc descending
    allPractices.sort((a, b) => {
      const dateA = a.publishedAtUtc ? new Date(a.publishedAtUtc).getTime() : 0;
      const dateB = b.publishedAtUtc ? new Date(b.publishedAtUtc).getTime() : 0;
      return dateB - dateA;
    });

    return allPractices;
  }

  // User Interaction Endpoints
  async subscribeLive(liveId: number): Promise<SubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/subscribe`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<SubscriptionState>(response);
  }

  async unsubscribeLive(liveId: number): Promise<SubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/subscribe`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<SubscriptionState>(response);
  }

  async enableLiveReminders(liveId: number): Promise<SubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/reminders`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<SubscriptionState>(response);
  }

  async disableLiveReminders(liveId: number): Promise<SubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/reminders`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<SubscriptionState>(response);
  }

  async addLiveToCalendar(liveId: number): Promise<SubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/calendar`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<SubscriptionState>(response);
  }

  getCalendarDownloadUrl(liveId: number): string {
    return `${BASE_URL}/api/lives/${liveId}/calendar.ics`;
  }

  async addLiveToFavorites(liveId: number): Promise<FavoriteState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/favorite`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<FavoriteState>(response);
  }

  async removeLiveFromFavorites(liveId: number): Promise<FavoriteState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/favorite`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<FavoriteState>(response);
  }

  async addVideoPracticeToFavorites(id: number): Promise<FavoriteState> {
    const response = await fetch(`${BASE_URL}/api/videopractices/${id}/favorite`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<FavoriteState>(response);
  }

  async removeVideoPracticeFromFavorites(id: number): Promise<FavoriteState> {
    const response = await fetch(`${BASE_URL}/api/videopractices/${id}/favorite`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<FavoriteState>(response);
  }

  // User Content (favorites and subscriptions)
  async getUserFavorites(type?: 'lives' | 'videos'): Promise<ContentItem[]> {
    const url = type
      ? `${BASE_URL}/api/user/favorites?type=${type}`
      : `${BASE_URL}/api/user/favorites`;
    // CORE-415 — authedRequest so an expired JWT on this dashboard-load GET is
    // refreshed + retried instead of cleared (ghost-auth → «Not authenticated»).
    return this.authedRequest<ContentItem[]>(url, { method: 'GET' });
  }

  async getUserSubscriptions(): Promise<UpcomingLive[]> {
    // CORE-415 — see getUserFavorites: refresh-retry, don't clear the token.
    return this.authedRequest<UpcomingLive[]>(`${BASE_URL}/api/user/subscriptions`, { method: 'GET' });
  }

  // Master Content Management (for Masters)

  // Lives
  //
  // AMB-203 — supports two response shapes off the same endpoint:
  //   no params  → MasterLive[] (legacy)
  //   limit/offset → PaginatedResponse<MasterLive> (paginated)
  // The split exists because we don't want one PR to migrate every
  // legacy caller; «Мои публикации» opts in by passing the params.
  //
  // Defensive normalization: if we asked for pagination but got a legacy
  // flat array back (BE not yet deployed, rollback, proxy returns the
  // un-cached older version), wrap it ourselves so the caller can read
  // .items / .totalCount without crashing. Treats the array length as
  // totalCount — best-effort, but better than throwing on undefined.
  async getMasterLives(): Promise<MasterLive[]>;
  async getMasterLives(
    params: { limit: number; offset?: number; orderBy?: 'startAsc'; dateFrom?: string },
  ): Promise<PaginatedResponse<MasterLive>>;
  async getMasterLives(
    params?: { limit: number; offset?: number; orderBy?: 'startAsc'; dateFrom?: string },
  ): Promise<MasterLive[] | PaginatedResponse<MasterLive>> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.append('limit', String(params.limit));
    if (params?.offset !== undefined) query.append('offset', String(params.offset));
    // CORE-1132 — сервер умел и то и другое с самого начала, просто никто не
    // просил: без них страница идёт от самых дальних эфиров к ближним, и
    // «предстоящие» собирались из того, что случайно попало в окно.
    if (params?.orderBy) query.append('orderBy', params.orderBy);
    if (params?.dateFrom) query.append('dateFrom', params.dateFrom);
    const qs = query.toString();
    const url = `${BASE_URL}/api/master/content/lives${qs ? `?${qs}` : ''}`;
    // CORE-415 — via authedRequest so an expired JWT on this dashboard-load
    // GET is silently refreshed + retried, not cleared. A bare 401 here used
    // to wipe the token mid-session (handleResponse) and strand the master in
    // a ghost-authenticated state where the upload modal threw «Not authenticated».
    const raw = await this.authedRequest<MasterLive[] | PaginatedResponse<MasterLive>>(url, { method: 'GET' });
    if (params && Array.isArray(raw)) {
      return {
        items: raw,
        totalCount: raw.length,
        limit: params.limit,
        offset: params.offset ?? 0,
      };
    }
    return raw;
  }

  async createLive(data: CreateLiveRequest): Promise<MasterLive> {
    const formData = new FormData();

    formData.append('Title', data.title);
    formData.append('StartDateTimeUtc', data.startDateTimeUtc);
    if (data.durationMinutes) {
      formData.append('DurationMinutes', String(data.durationMinutes));
    }
    if (data.previewUrl) {
      formData.append('PreviewUrl', data.previewUrl);
    }
    if (data.coverImage) {
      formData.append('CoverImage', data.coverImage);
    }
    if (data.isRegular !== undefined) {
      formData.append('IsRegular', String(data.isRegular));
    }
    if (data.description) {
      formData.append('Description', data.description);
    }
    // CORE-331 — master's personal feed invitation; BE uses it as the
    // announcement post body (falls back to description when absent).
    if (data.feedMessage) {
      formData.append('FeedMessage', data.feedMessage);
    }
    // April release renamed `Type` → `Kind` on the backend. The C# parser
    // accepts both English codes ("LiveStream"/"Offline"/"LivePractice") and
    // Russian labels ("Лайв-стрим"/"Офлайн"/"Лайв-практика"), so passing the
    // existing `data.type` string ('Офлайн' from OfflineEventModal) just works.
    // See https://agape-tech.atlassian.net/wiki/spaces/AM/pages/36012139
    if (data.type) {
      formData.append('Kind', data.type);
    }
    if (data.category) {
      formData.append('Category', data.category);
    }
    // New canonical multi-select shape — backend accepts `Categories[]` form-data.
    // Sent alongside the legacy `Category` CSV during the 2-release migration.
    if (Array.isArray(data.categories)) {
      for (const c of data.categories) {
        if (c && typeof c === 'string') formData.append('Categories', c);
      }
    }
    // Multi-speaker live: master-chosen guest cap (1..4). Omitted = solo.
    if (typeof data.coHostMaxSlots === 'number') {
      formData.append('CoHostMaxSlots', String(data.coHostMaxSlots));
    }
    // Offline-event location (april release). Backend stores all four fields
    // independently; sending only `Address` (plain text) is valid.
    if (data.address) formData.append('Address', data.address);
    if (typeof data.addressLatitude === 'number') formData.append('AddressLatitude', String(data.addressLatitude));
    if (typeof data.addressLongitude === 'number') formData.append('AddressLongitude', String(data.addressLongitude));
    if (data.addressPlaceId) formData.append('AddressPlaceId', data.addressPlaceId);
    // AMB-24 — attach a library practice to the live (auto-play during broadcast).
    if (typeof data.videoPracticeId === 'number') {
      formData.append('VideoPracticeId', String(data.videoPracticeId));
    }
    // CORE-505 — audience scope (all/subscribers/private), mirroring videos.
    // BE defaults to "all" when omitted. The native wizard sends the master's
    // «Кто увидит эфир» choice here; previously this was silently dropped.
    if (data.visibility) formData.append('Visibility', data.visibility);

    // FormData multipart: do NOT set Content-Type — the runtime computes
    // the boundary header itself. We can't reuse authedRequest (which
    // sets application/json via getHeaders), but the same role-mismatch
    // retry applies — admins approve master applications out-of-band,
    // so a fresh-master's cached JWT may still lack the "Master" claim.
    const fetcher = async () => {
      const t = await storage.getToken();
      // CORE-431 — timeout so a slow/stuck upload can't leave the «Запланировать
      // эфир» button spinning forever with no result («длинный лоадер, стало
      // снова кнопка без ошибок и публикации»). Aborts → friendly error instead.
      return fetchWithTimeout(`${BASE_URL}/api/master/content/lives`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${t}` },
        body: formData,
      });
    };
    let response = await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<MasterLive>(response);
  }

  async updateLive(id: number, data: UpdateLiveRequest): Promise<MasterLive> {
    const formData = new FormData();

    // Always send title and startDateTimeUtc to prevent backend from clearing them
    if (data.title !== undefined) formData.append('Title', data.title);
    if (data.startDateTimeUtc !== undefined) formData.append('StartDateTimeUtc', data.startDateTimeUtc);
    if (data.durationMinutes !== undefined) formData.append('DurationMinutes', String(data.durationMinutes));
    if (data.previewUrl !== undefined) formData.append('PreviewUrl', data.previewUrl);
    if (data.playbackUrl !== undefined) formData.append('PlaybackUrl', data.playbackUrl);
    if (data.coverImage) formData.append('CoverImage', data.coverImage);
    if (data.isRegular !== undefined) formData.append('IsRegular', String(data.isRegular));
    if (data.description !== undefined) formData.append('Description', data.description);
    // April release renamed `Type` → `Kind` (see createLive comment).
    if (data.type !== undefined) formData.append('Kind', data.type);
    if (data.category !== undefined) formData.append('Category', data.category);
    if (Array.isArray(data.categories)) {
      for (const c of data.categories) {
        if (c && typeof c === 'string') formData.append('Categories', c);
      }
    }
    // Multi-speaker live: master-chosen guest cap (1..4). Omitted = solo.
    if (typeof data.coHostMaxSlots === 'number') {
      formData.append('CoHostMaxSlots', String(data.coHostMaxSlots));
    }
    if (data.address !== undefined) formData.append('Address', data.address);
    if (typeof data.addressLatitude === 'number') formData.append('AddressLatitude', String(data.addressLatitude));
    if (typeof data.addressLongitude === 'number') formData.append('AddressLongitude', String(data.addressLongitude));
    if (data.addressPlaceId !== undefined) formData.append('AddressPlaceId', data.addressPlaceId);
    // AMB-24 — attach (or detach with sentinel `0`) a library practice. The
    // BE treats `VideoPracticeId == 0` on update as «clear the attachment».
    if (typeof data.videoPracticeId === 'number') {
      formData.append('VideoPracticeId', String(data.videoPracticeId));
    }
    // CORE-505 — omit to leave audience scope unchanged; send to update.
    if (data.visibility) formData.append('Visibility', data.visibility);

    // FormData multipart + role-mismatch retry — see createLive.
    const fetcher = async () => {
      const t = await storage.getToken();
      // CORE-522 — timeout like createLive (CORE-431): re-saving an edited Live
      // over a stalled mobile network must surface a friendly error instead of
      // spinning the «Запланировать эфир» loader forever with no result.
      return fetchWithTimeout(`${BASE_URL}/api/master/content/lives/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${t}` },
        body: formData,
      });
    };
    let response = await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<MasterLive>(response);
  }

  async deleteLive(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    await this.handleResponse<void>(response);
  }

  /**
   * SCRUM-49 — Cancel this live and every future iteration in the same
   * weekly series. Past iterations stay intact (recordings, chats,
   * stats preserved). Returns the number of cancelled rows so the FE
   * can render «Серия отменена, N встреч».
   */
  async deleteLiveSeries(id: number): Promise<{ cancelledCount: number; anchorSurvived: boolean }> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${id}/series`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ cancelledCount: number; anchorSurvived: boolean }>(response);
  }

  // === CORE-1132 — центр управления сериями ===
  //
  // Три аддитивные ручки поверх той механики серий, что есть с SCRUM-37/49:
  // шаблон с `isRegular`, дети с `parentLiveId`, материализация и отмена
  // «отсюда и далее». Раньше серия отдавалась плоским списком встреч, и
  // управлять ею можно было только по одной.

  /**
   * Серии мастера, по одной строке на серию.
   *
   * @param activeOnly `true` — только идущие, `false` — только приостановленные
   *   (правило снято, будущие встречи ещё есть), не передан — все. Это и есть
   *   фильтр «Все серии / Приостановленные».
   */
  async getMasterLiveSeries(activeOnly?: boolean):
    Promise<import('../types/liveSeries').LiveSeriesSummary[]> {
    const qs = activeOnly === undefined ? '' : `?activeOnly=${activeOnly}`;
    const response = await fetch(`${BASE_URL}/api/master/content/lives/series${qs}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/liveSeries').LiveSeriesSummary[]>(response);
  }

  /**
   * Встречи одной серии. Приходят и прошедшие тоже, помеченные `hasStarted`:
   * мастер должен видеть, что серия идёт давно, но менять их нельзя.
   */
  async getMasterLiveSeriesOccurrences(
    anchorId: number,
    // CORE-1132 — якорь ищется в той сущности, из которой пришла серия: по
    // таблице Live серия визарда не находится вовсе.
    source?: import('../types/liveSeries').LiveSeriesSource,
  ): Promise<import('../types/liveSeries').LiveSeriesOccurrence[]> {
    const qs = source ? `?source=${source}` : '';
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/series/${anchorId}/occurrences${qs}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/liveSeries').LiveSeriesOccurrence[]>(response);
  }

  /**
   * Изменить встречу, её и последующие, либо всю серию.
   *
   * Отдельная дверь от `updateLive`, а не флаг у него: одиночное обновление
   * тащит обложку, модерацию и рассылку по одной встрече, и прогонять это
   * пачкой значит получить пачку побочных эффектов там, где мастер просил
   * переименовать серию.
   *
   * Прошедшие встречи сервер не трогает ни при каком объёме и возвращает, сколько
   * их пропустил, — это надо показать: молчаливый пропуск выглядит как сбой.
   */
  async updateLiveSeries(
    liveId: number,
    input: import('../types/liveSeries').UpdateLiveSeriesInput,
  ): Promise<import('../types/liveSeries').LiveSeriesUpdateResult> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/master/content/lives/${liveId}/series`,
      {
        method: 'PUT',
        headers: await this.getHeaders(true),
        body: JSON.stringify(input),
      },
      20_000,
    );
    return this.handleResponse<import('../types/liveSeries').LiveSeriesUpdateResult>(response);
  }

  // === CORE-1131 / CORE-1134 — шаблоны Live-сессий ===
  //
  // Бэк готов с CORE-1147 (`/api/live-templates`), клиента у него не было ни
  // одного. CORE-1131 дал список, применение, копию и удаление; CORE-1134
  // добирает остальное: сохранить настройки как шаблон (в том числе из
  // существующей сессии), переписать шаблон и провести по нему сессию с
  // разовыми правками.

  async getLiveTemplates(): Promise<import('../types/liveTemplates').LiveTemplate[]> {
    const response = await fetch(`${BASE_URL}/api/live-templates`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/liveTemplates').LiveTemplate[]>(response);
  }

  /** Один шаблон целиком — им визард наполняет шаги при правке и применении. */
  async getLiveTemplate(templateId: number): Promise<import('../types/liveTemplates').LiveTemplateDetail> {
    const response = await fetch(`${BASE_URL}/api/live-templates/${templateId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/liveTemplates').LiveTemplateDetail>(response);
  }

  /** Сохранить набор настроек как новый шаблон. */
  async createLiveTemplate(
    data: import('../types/liveTemplates').SaveLiveSessionTemplateRequest,
  ): Promise<import('../types/liveTemplates').LiveTemplateDetail> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/live-templates`,
      { method: 'POST', headers: await this.getHeaders(true), body: JSON.stringify(data) },
      20_000,
    );
    return this.handleResponse<import('../types/liveTemplates').LiveTemplateDetail>(response);
  }

  /** Переписать шаблон. Контракт тот же, что у создания, — полная замена. */
  async updateLiveTemplate(
    templateId: number,
    data: import('../types/liveTemplates').SaveLiveSessionTemplateRequest,
  ): Promise<import('../types/liveTemplates').LiveTemplateDetail> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/live-templates/${templateId}`,
      { method: 'PUT', headers: await this.getHeaders(true), body: JSON.stringify(data) },
      20_000,
    );
    return this.handleResponse<import('../types/liveTemplates').LiveTemplateDetail>(response);
  }

  /**
   * Снять шаблон с существующей сессии — прошедшей или будущей, но своей
   * (чужая отвечает 404). Пустое `name` сервер заменит заголовком сессии.
   * Сама сессия не меняется.
   */
  async createLiveTemplateFromSession(
    input: { watchPartyId: number; name?: string },
  ): Promise<import('../types/liveTemplates').LiveTemplateDetail> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/live-templates/from-session`,
      { method: 'POST', headers: await this.getHeaders(true), body: JSON.stringify(input) },
      20_000,
    );
    return this.handleResponse<import('../types/liveTemplates').LiveTemplateDetail>(response);
  }

  /**
   * Создать Live по шаблону — то действие, ради которого шаблоны и заводят.
   * `overrides` — разовые правки этого проведения (CORE-1134): каждое
   * присланное поле перекрывает сохранённое только для создаваемой сессии,
   * сам шаблон не меняется. Без правок уходит пустое тело — сессия собирается
   * из сохранённых настроек целиком.
   */
  async createLiveFromTemplate(
    templateId: number,
    overrides?: Partial<CreateRoomRequest> & import('../types/liveTemplates').TemplateParticipantMessages,
    opts?: { skipAuthRedirect?: boolean; idempotencyKey?: string },
  ): Promise<RoomResponse> {
    // CORE-1910 — один ключ на вызов: им же уходят повтор после refresh и
    // транспортный повтор/фолбэк в apiFallback, и сервер не создаст вторую сессию.
    const idempotencyKey = opts?.idempotencyKey ?? newIdempotencyKey();
    // CORE-1587 — тот же refresh + повтор, что у `createRoom`.
    const fetcher = async () => fetchWithTimeout(
      `${BASE_URL}/api/live-templates/${templateId}/create-session`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true) as Record<string, string>), [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        body: JSON.stringify(overrides ?? {}),
      },
      20_000,
    );
    let response = await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<RoomResponse>(response, opts?.skipAuthRedirect);
  }

  /** Копия шаблона — быстрый способ завести похожий, не собирая его заново. */
  async duplicateLiveTemplate(templateId: number):
    Promise<import('../types/liveTemplates').LiveTemplate> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/live-templates/${templateId}/duplicate`,
      { method: 'POST', headers: await this.getHeaders(true) },
      20_000,
    );
    return this.handleResponse<import('../types/liveTemplates').LiveTemplate>(response);
  }

  async deleteLiveTemplate(templateId: number): Promise<void> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/live-templates/${templateId}`,
      { method: 'DELETE', headers: await this.getHeaders(true) },
      20_000,
    );
    // 204 — тела нет; `handleResponse` это переживает и вернёт undefined.
    await this.handleResponse<unknown>(response);
  }

  // === CORE-1133 — архив записей Live-сессий ===
  //
  // Контур записей есть с CORE-554, но у комнат практик. У Live не было ни
  // владельческого списка, ни публикации в «Медиа», ни удаления записи без
  // удаления самого эфира.

  /**
   * Записи мастера со сводкой — одним запросом, чтобы счётчики и строки не
   * разъезжались. В список попадают только эфиры, у которых запись есть или
   * ожидается: не записывали — карточки нет.
   */
  async getMasterLiveRecordings(params?: {
    filter?: import('../types/liveRecordings').LiveRecordingFilter;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<import('../types/liveRecordings').LiveRecordingList> {
    const qs = new URLSearchParams();
    if (params?.filter && params.filter !== 'all') qs.set('filter', params.filter);
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/recordings${qs.toString() ? `?${qs}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/liveRecordings').LiveRecordingList>(response);
  }

  /**
   * Опубликовать запись в «Медиа». Идемпотентно: связь держится по playback-URL,
   * поэтому повторное нажатие возвращает прежний элемент, а не создаёт второй.
   */
  async publishLiveRecording(liveId: number):
    Promise<import('../types/liveRecordings').LiveRecordingPublishResult> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/master/content/lives/${liveId}/recording/publish`,
      { method: 'POST', headers: await this.getHeaders(true) },
      20_000,
    );
    return this.handleResponse<import('../types/liveRecordings').LiveRecordingPublishResult>(response);
  }

  /** Убрать запись из «Медиа», не удаляя её. */
  async unpublishLiveRecording(liveId: number):
    Promise<import('../types/liveRecordings').LiveRecordingPublishResult> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/master/content/lives/${liveId}/recording/unpublish`,
      { method: 'POST', headers: await this.getHeaders(true) },
      20_000,
    );
    return this.handleResponse<import('../types/liveRecordings').LiveRecordingPublishResult>(response);
  }

  /**
   * Удалить запись, оставив сам эфир. Прежде запись убиралась только вместе с
   * ним (`deleteLive`), а с ним уходили чат, статистика и место в истории.
   */
  async deleteLiveRecording(liveId: number): Promise<void> {
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/master/content/lives/${liveId}/recording`,
      { method: 'DELETE', headers: await this.getHeaders(true) },
      20_000,
    );
    await this.handleResponse<unknown>(response);
  }

  async setLiveVisibility(id: number, isHidden: boolean): Promise<MasterLive> {
    // CORE-522 family — timeout like createLive/updateLive. handlePublish calls
    // this right after createLive (BE creates the live hidden; FE force-publishes
    // it), so a bare fetch stalling on a shaped RU connection hung the web
    // «Опубликовать» spinner forever with no error.
    const response = await fetchWithTimeout(`${BASE_URL}/api/master/content/lives/${id}/visibility`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ isHidden }),
    }, 20_000);
    return this.handleResponse<MasterLive>(response);
  }

  // Videos — same two-shape contract as getMasterLives (AMB-203).
  // Same defensive flat-array → PaginatedResponse normalization.
  async getMasterVideos(): Promise<MasterVideo[]>;
  async getMasterVideos(params: { limit: number; offset?: number }): Promise<PaginatedResponse<MasterVideo>>;
  async getMasterVideos(params?: { limit: number; offset?: number }): Promise<MasterVideo[] | PaginatedResponse<MasterVideo>> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.append('limit', String(params.limit));
    if (params?.offset !== undefined) query.append('offset', String(params.offset));
    const qs = query.toString();
    const url = `${BASE_URL}/api/master/content/videos${qs ? `?${qs}` : ''}`;
    // CORE-415 — via authedRequest (silent refresh + retry) so an expired JWT
    // on this dashboard-load GET doesn't get cleared into a ghost-auth state.
    const raw = await this.authedRequest<MasterVideo[] | PaginatedResponse<MasterVideo>>(url, { method: 'GET' });
    if (params && Array.isArray(raw)) {
      return {
        items: raw,
        totalCount: raw.length,
        limit: params.limit,
        offset: params.offset ?? 0,
      };
    }
    return raw;
  }

  async createVideo(data: CreateVideoRequest, onProgress?: (percent: number) => void, signal?: AbortSignal): Promise<MasterVideo> {
    const token = await storage.getToken();
    const formData = new FormData();

    formData.append('Title', data.title);
    formData.append('ContentType', data.contentType);
    if (data.description !== undefined) formData.append('Description', data.description);
    if (data.category) formData.append('Category', data.category);
    if (Array.isArray(data.categories)) {
      for (const c of data.categories) {
        if (c && typeof c === 'string') formData.append('Categories', c);
      }
    }
    if (data.publishedAtUtc) formData.append('PublishedAtUtc', data.publishedAtUtc);
    if (data.durationMinutes) formData.append('DurationMinutes', String(data.durationMinutes));
    if (data.playbackUrl) formData.append('PlaybackUrl', data.playbackUrl);
    // CORE-315 — prefer the presigned cover URL (tiny string) over inline bytes
    // so the create POST stays small enough to clear the RU origin shaping. Fall
    // back to inline CoverImage when no URL is present (native / presign failure).
    if (data.coverImageUrl) formData.append('CoverImageUrl', data.coverImageUrl);
    else if (data.coverImage) formData.append('CoverImage', data.coverImage);
    if (data.videoFile) formData.append('VideoFile', data.videoFile);
    // CORE-167 — audio practices ride here as a multipart AudioFile (no presign
    // path for audio); BE forces ContentType=audio when this part is present.
    if (data.audioFile) formData.append('AudioFile', data.audioFile);
    // CORE-72 — audience scope. BE defaults to "all" when omitted.
    if (data.visibility) formData.append('Visibility', data.visibility);

    if (onProgress && (data.videoFile || data.audioFile)) {
      return this.uploadWithProgress(`${BASE_URL}/api/master/content/videos`, 'POST', formData, token, onProgress);
    }

    // CORE-315 — bound this request. On the presigned-upload path the video is
    // already in S3, so this is a small metadata+cover POST that should return
    // quickly; it must never hang forever (the BE used to block on a synchronous
    // ffprobe of the .mov). A timeout + the caller's abort signal mean the modal
    // can leave the «processing» phase instead of spinning indefinitely.
    const response = await fetchWithTimeout(`${BASE_URL}/api/master/content/videos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
      signal,
    }, 120_000);

    return this.handleResponse<MasterVideo>(response);
  }

  async updateVideo(id: number, data: UpdateVideoRequest, onProgress?: (percent: number) => void): Promise<MasterVideo> {
    const token = await storage.getToken();
    const formData = new FormData();

    if (data.title) formData.append('Title', data.title);
    if (data.contentType) formData.append('ContentType', data.contentType);
    if (data.description !== undefined) formData.append('Description', data.description);
    if (data.category) formData.append('Category', data.category);
    if (Array.isArray(data.categories)) {
      for (const c of data.categories) {
        if (c && typeof c === 'string') formData.append('Categories', c);
      }
    }
    if (data.publishedAtUtc) formData.append('PublishedAtUtc', data.publishedAtUtc);
    if (data.durationMinutes) formData.append('DurationMinutes', String(data.durationMinutes));
    if (data.playbackUrl) formData.append('PlaybackUrl', data.playbackUrl);
    if (data.coverImage) formData.append('CoverImage', data.coverImage);
    if (data.videoFile) formData.append('VideoFile', data.videoFile);
    // CORE-72 — omit to leave unchanged; send any non-empty value to update.
    if (data.visibility) formData.append('Visibility', data.visibility);

    if (onProgress && data.videoFile) {
      return this.uploadWithProgress(`${BASE_URL}/api/master/content/videos/${id}`, 'PUT', formData, token, onProgress);
    }

    const response = await fetch(`${BASE_URL}/api/master/content/videos/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    return this.handleResponse<MasterVideo>(response);
  }

  private uploadWithProgress<T>(url: string, method: string, formData: FormData, token: string | null, onProgress: (percent: number) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid response')); }
        } else {
          // Carry the HTTP status (and prefer the BE's capital-M Message) on the
          // rejected error so the UI boundary can detect a 422 moderation block
          // (isModerationBlock) and map 413/etc via friendlyApiError — same shape
          // handleResponse throws on the non-progress path.
          let msg = `Upload failed (${xhr.status})`;
          try {
            const err = JSON.parse(xhr.responseText);
            msg = err.message || err.Message || err.title || msg;
          } catch { /* non-JSON body — keep the generic message */ }
          const e: any = new Error(msg);
          e.status = xhr.status;
          reject(e);
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
  }

  async deleteVideo(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/videos/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    await this.handleResponse<void>(response);
  }

  async setVideoVisibility(id: number, isHidden: boolean): Promise<MasterVideo> {
    const response = await fetch(`${BASE_URL}/api/master/content/videos/${id}/visibility`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ isHidden }),
    });
    return this.handleResponse<MasterVideo>(response);
  }

  // Push Notification Endpoints
  async getVapidPublicKey(): Promise<{ publicKey: string }> {
    const response = await fetch(`${BASE_URL}/api/push/vapid-public-key`, {
      method: 'GET',
    });

    return this.handleResponse<{ publicKey: string }>(response);
  }

  async registerPushSubscription(data: RegisterPushSubscriptionRequest): Promise<PushSubscriptionInfo> {
    const response = await fetch(`${BASE_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });

    return this.handleResponse<PushSubscriptionInfo>(response);
  }

  async unregisterPushSubscription(endpoint: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/push/subscribe`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ endpoint }),
    });

    await this.handleResponse<void>(response);
  }

  async getUserPushSubscriptions(): Promise<PushSubscriptionInfo[]> {
    const response = await fetch(`${BASE_URL}/api/push/subscriptions`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<PushSubscriptionInfo[]>(response);
  }

  /**
   * Register a native device for push (Expo push token). The backend (CORE-341)
   * dispatches by platform: web → Web Push (VAPID), native → Expo Push API.
   * Mirrors the web `registerPushSubscription` but for `Notifications.getExpoPushTokenAsync()`.
   */
  async registerDeviceToken(data: { token: string; platform: 'ios' | 'android'; deviceId?: string }): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/push/register-device`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });

    await this.handleResponse<void>(response);
  }

  async unregisterDeviceToken(token: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/push/register-device`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ token }),
    });

    await this.handleResponse<void>(response);
  }

  // Master Subscription Endpoints
  async subscribeToMaster(masterProfileId: number): Promise<MasterSubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/masters/${masterProfileId}/subscribe`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<MasterSubscriptionState>(response);
  }

  async unsubscribeFromMaster(masterProfileId: number): Promise<MasterSubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/masters/${masterProfileId}/subscribe`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<MasterSubscriptionState>(response);
  }

  async getMasterSubscriptionState(masterProfileId: number): Promise<MasterSubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/masters/${masterProfileId}/subscribe`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<MasterSubscriptionState>(response);
  }

  async updateMasterNotificationPreferences(masterProfileId: number, isPushNotificationsEnabled: boolean): Promise<MasterSubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/masters/${masterProfileId}/notifications`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ isPushNotificationsEnabled }),
    });

    return this.handleResponse<MasterSubscriptionState>(response);
  }

  async getUserMasterSubscriptions(): Promise<MasterSubscription[]> {
    const response = await fetch(`${BASE_URL}/api/user/master-subscriptions`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<MasterSubscription[]>(response);
  }

  /**
   * AMB-183 — public read of a master's circle summary. Returns the
   * deterministic Stream channel id (`master-circle-{id}`), plus name
   * and cover. `memberCount` is null in the MVP — Stream is the source
   * of truth, FE reads live counts off the channel state.
   *
   * The Stream channel may not be created server-side yet; the BE
   * synthesises the DTO from `MasterProfile` and creates the channel
   * lazily on first event post (see backend
   * `EventThreadService.EnsureMasterChannelAsync`). Anonymous-readable.
   *
   * CORE-1725 — токен прикладывается, когда он есть. `isMember` сервер считает
   * по вызывающему (владелец профиля или подписчик) и анонимному отвечает
   * `null`; без токена обсуждение события считало «не участником» каждого, и
   * владельцу предлагалось подписаться на самого себя. Чтение остаётся
   * публичным: гость уходит без токена, а невалидный токен сервер на этом
   * адресе не отвергает — 401 здесь не повод выкидывать человека на вход.
   */
  async getMasterCircle(masterProfileId: number): Promise<MasterCircleSummary> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/circle`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterCircleSummary>(response, true);
  }

  /**
   * CORE-96 — preview messages for the master's community chat. Used by
   * the non-member tab variant on the master profile (CORE-104): renders
   * the last `limit` messages as a teaser plus a «Подписаться чтобы
   * писать» CTA. Server-side filters out system / event-card / deleted
   * rows; FE renders straight without re-checking.
   *
   * `limit` is clamped server-side (max 20). Anonymous callers get the
   * same payload as authenticated ones — the endpoint doesn't gate on
   * membership, only on the master's profile being public.
   */
  async getCommunityChatPreview(
    masterProfileId: number,
    limit = 5,
  ): Promise<CommunityChatMessagePreview[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    const response = await fetch(
      `${BASE_URL}/api/masterprofile/${masterProfileId}/circle/preview-messages?${params}`,
      {
        method: 'GET',
        headers: await this.getHeaders(false),
      },
    );
    return this.handleResponse<CommunityChatMessagePreview[]>(response);
  }

  async getMasterChatDiscussions(masterProfileId: number): Promise<ChatDiscussion[]> {
    const response = await fetch(`${BASE_URL}/api/chat/masters/${masterProfileId}/discussions`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<ChatDiscussion[]>(response);
  }

  async getChatDiscussion(discussionId: string, masterProfileId?: number): Promise<ChatDiscussionDetails> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(`${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}${params.toString() ? `?${params}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<ChatDiscussionDetails>(response);
  }

  /**
   * CORE-1698 / CORE-1710 — закрыть обсуждение (тему или обсуждение события):
   * история остаётся, отправка отклоняется у всех кодом `discussion_closed`.
   * Мастер сообщества или админ, иначе 403; общий чат — 400
   * `general_not_closable`. Идемпотентно; ответ — карточка с `isClosed`.
   */
  async closeChatDiscussion(discussionId: string, masterProfileId?: number): Promise<ChatDiscussionDetails> {
    return this.setChatDiscussionClosed(discussionId, true, masterProfileId);
  }

  /** CORE-1698 — снова открыть закрытое обсуждение; переписка продолжается в том же чате. */
  async reopenChatDiscussion(discussionId: string, masterProfileId?: number): Promise<ChatDiscussionDetails> {
    return this.setChatDiscussionClosed(discussionId, false, masterProfileId);
  }

  private async setChatDiscussionClosed(
    discussionId: string,
    closed: boolean,
    masterProfileId?: number,
  ): Promise<ChatDiscussionDetails> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(
      `${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/close${params.toString() ? `?${params}` : ''}`,
      { method: closed ? 'POST' : 'DELETE', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<ChatDiscussionDetails>(response);
  }

  // CORE-698 — put a discussion back into the list as unread, from this message
  // onwards. Separate from the read endpoint on purpose: ordinary reading only
  // ever catches up, so rewinding needs its own door.
  async markMasterChatDiscussionUnread(
    discussionId: string,
    fromMessageAtUtc: string,
    masterProfileId?: number,
  ): Promise<DiscussionReadResult> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(
      `${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/unread${params.toString() ? `?${params}` : ''}`,
      {
        method: 'POST',
        headers: await this.getHeaders(true),
        body: JSON.stringify({ fromMessageAtUtc }),
      },
    );
    return this.handleResponse<DiscussionReadResult>(response);
  }

  // CORE-699 — silence a discussion for me: no push, no email, no row in the
  // notification feed. Reading it is unaffected, and the master's other
  // discussions keep notifying.
  async setMasterChatDiscussionMuted(
    discussionId: string,
    muted: boolean,
    masterProfileId?: number,
  ): Promise<void> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(
      `${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/mute${params.toString() ? `?${params}` : ''}`,
      { method: muted ? 'POST' : 'DELETE', headers: await this.getHeaders(true) },
    );
    if (!response.ok) await this.handleResponse<void>(response);
  }

  // CORE-697 — one active pin per discussion; pinning again replaces the previous
  // one. Owner-or-admin only (the backend forbids the rest), and the pin mirrors
  // into the chat channel so native clients see the same banner.
  async pinChatDiscussionMessage(
    discussionId: string,
    body: { messageId: string; text?: string | null; authorId?: string | null },
    masterProfileId?: number,
  ): Promise<ChatDiscussionDetails['pinnedMessage']> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(
      `${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/pin${params.toString() ? `?${params}` : ''}`,
      {
        method: 'POST',
        headers: await this.getHeaders(true),
        body: JSON.stringify(body),
      },
    );
    return this.handleResponse<ChatDiscussionDetails['pinnedMessage']>(response);
  }

  /** CORE-697 — clear the discussion's pin. Idempotent: 204 even when nothing was pinned. */
  async unpinChatDiscussionMessage(discussionId: string, masterProfileId?: number): Promise<void> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(
      `${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/pin${params.toString() ? `?${params}` : ''}`,
      { method: 'DELETE', headers: await this.getHeaders(true) },
    );
    if (!response.ok) await this.handleResponse<void>(response);
  }

  // CORE-1654 — ручные обсуждения («темы»), закреп обсуждения в списке и
  // группы (CORE-1688). Мастер заводит тему через «+» в списке обсуждений;
  // название и описание правятся тем же телом у любого обсуждения, в том
  // числе автосозданного для события. Удаляется тоже любое, кроме общего
  // чата (404); событие и его публикация при этом остаются.
  async createCommunityTopic(
    masterProfileId: number,
    body: { title: string; description?: string | null },
  ): Promise<ChatDiscussion> {
    const response = await fetch(`${BASE_URL}/api/chat/masters/${masterProfileId}/topics`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(body),
    });
    return this.handleResponse<ChatDiscussion>(response);
  }

  async updateCommunityTopic(
    discussionId: string,
    body: { title: string; description?: string | null },
  ): Promise<ChatDiscussion> {
    const response = await fetch(`${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/topic`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify(body),
    });
    return this.handleResponse<ChatDiscussion>(response);
  }

  async deleteCommunityTopic(discussionId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/topic`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  /** Закрепить обсуждение вверху списка сообщества (общее для всех участников). Идемпотентно. */
  async setDiscussionListPinned(masterProfileId: number, discussionId: string, pinned: boolean): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/chat/masters/${masterProfileId}/discussions/${encodeURIComponent(discussionId)}/list-pin`,
      { method: pinned ? 'POST' : 'DELETE', headers: await this.getHeaders(true) },
    );
    if (!response.ok) await this.handleResponse<void>(response);
  }

  /** CORE-1688 — группы обсуждений сообщества, по их порядку. */
  async getDiscussionGroups(masterProfileId: number): Promise<DiscussionGroup[]> {
    const response = await fetch(`${BASE_URL}/api/chat/masters/${masterProfileId}/discussion-groups`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<DiscussionGroup[]>(response);
  }

  async createDiscussionGroup(masterProfileId: number, title: string): Promise<DiscussionGroup> {
    const response = await fetch(`${BASE_URL}/api/chat/masters/${masterProfileId}/discussion-groups`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ title }),
    });
    return this.handleResponse<DiscussionGroup>(response);
  }

  /** Переименовать группу и/или переставить её (`order` — место в списке, с нуля). */
  async updateDiscussionGroup(
    groupId: number,
    body: { title?: string; order?: number },
  ): Promise<DiscussionGroup> {
    const response = await fetch(`${BASE_URL}/api/chat/discussion-groups/${groupId}`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify(body),
    });
    return this.handleResponse<DiscussionGroup>(response);
  }

  /** Убрать группу; её обсуждения возвращаются в общий список. */
  async deleteDiscussionGroup(groupId: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/chat/discussion-groups/${groupId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  /** Положить обсуждение в группу или вернуть в общий список (`null`). Идемпотентно. */
  async moveDiscussionToGroup(discussionId: string, groupId: number | null): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/group`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ groupId }),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  async getMasterChatOnlineUsers(masterProfileId: number): Promise<ChatOnlineUsersResponse> {
    const response = await fetch(`${BASE_URL}/api/chat/masters/${masterProfileId}/online-users`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<ChatOnlineUsersResponse>(response);
  }

  // CORE-162 — mark a community-chat discussion read (unread → 0). For the
  // general chat pass discussionId 'general' + masterProfileId; for an event
  // discussion the id is the event-card message id (master resolved from it).
  async markMasterChatDiscussionRead(
    discussionId: string,
    masterProfileId?: number,
    lastReadMessageId?: string,
  ): Promise<DiscussionReadResult> {
    const params = new URLSearchParams();
    if (masterProfileId !== undefined) params.set('masterProfileId', String(masterProfileId));
    const response = await fetch(
      `${BASE_URL}/api/chat/discussions/${encodeURIComponent(discussionId)}/read${params.toString() ? `?${params}` : ''}`,
      {
        method: 'POST',
        headers: await this.getHeaders(true),
        body: JSON.stringify(lastReadMessageId ? { lastReadMessageId } : {}),
      },
    );
    return this.handleResponse<DiscussionReadResult>(response);
  }

  // UC-06.04: Notification Settings
  // ─── CORE-1455: дневник рефлексии после практики ─────────────────────────

  /**
   * Наводящие вопросы и подсказки. Тексты живут на сервере намеренно: положи
   * их в приложение — и веб с мобильным со временем разойдутся, это здесь уже
   * случалось. Правка формулировки не требует выката приложений.
   */
  async getJournalPrompts(): Promise<JournalPrompts> {
    const response = await fetch(`${BASE_URL}/api/practice-journal/prompts`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<JournalPrompts>(response);
  }

  /**
   * Выбор приватности дневника. `aiMayRead === null` значит «человек ещё не
   * выбирал» — по нему и показывается окно с объяснением, один раз.
   */
  async getJournalPrivacy(): Promise<JournalPrivacy> {
    const response = await fetch(`${BASE_URL}/api/practice-journal/privacy`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<JournalPrivacy>(response);
  }

  async setJournalPrivacy(aiMayRead: boolean): Promise<JournalPrivacy> {
    const response = await fetch(`${BASE_URL}/api/practice-journal/privacy`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ aiMayRead }),
    });
    return this.handleResponse<JournalPrivacy>(response);
  }

  /**
   * Создать запись. Уровень приватности не передаём: сервер берёт выбор
   * человека. Передать его здесь значило бы дублировать правило на клиенте —
   * и однажды разойтись с ним.
   */
  async createJournalEntry(data: JournalEntryDraft): Promise<JournalEntry> {
    const response = await fetch(`${BASE_URL}/api/practice-journal`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<JournalEntry>(response);
  }

  /**
   * CORE-1751 — свои записи, новые первыми. Сервер отдаёт только записи
   * владельца токена и не больше двухсот за раз.
   */
  async getJournalEntries(take = 100): Promise<JournalEntry[]> {
    const response = await fetch(`${BASE_URL}/api/practice-journal?take=${take}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<JournalEntry[]>(response);
  }

  /**
   * Правка записи — текста или видимости. Тело собирает `entryUpdateBody`:
   * сервер переписывает все текстовые поля из присланного, и правка одной
   * видимости без текста стёрла бы запись.
   */
  async updateJournalEntry(id: number, data: JournalEntryUpdate): Promise<JournalEntry> {
    const response = await fetch(`${BASE_URL}/api/practice-journal/${id}`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<JournalEntry>(response);
  }

  /**
   * CORE-1751 — сразу после выхода из Live-сессии: предложить ли рефлексию по
   * ней. Запрос забирает предложение (сервер тут же отмечает его сделанным),
   * поэтому `null` — и «участие ниже порога», и «уже предлагали».
   */
  async claimRoomReflectionOffer(roomId: number): Promise<ReflectionOffer | null> {
    const response = await fetch(`${BASE_URL}/api/practice-journal/offers/rooms/${roomId}/claim`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    // 204 приходит пустым телом, а handleResponse отдаёт на него `{}`.
    const offer = await this.handleResponse<ReflectionOffer | Record<string, never>>(response);
    return typeof (offer as ReflectionOffer).completionId === 'number' ? (offer as ReflectionOffer) : null;
  }

  async deleteJournalEntry(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/practice-journal/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async getNotificationSettings(): Promise<NotificationSettings> {
    const response = await fetch(`${BASE_URL}/api/user/notification-settings`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<NotificationSettings>(response);
  }

  async updateNotificationSettings(data: {
    defaultNotificationOffsetMinutes?: number;
    autoNotifyForSubscriptions?: boolean;
    // CORE-71 — все необязательные: экран шлёт один тронутый переключатель,
    // а не форму целиком, и не затирает соседние настройки.
    notifyMessages?: boolean;
    notifyMasterEvents?: boolean;
    notifyPracticeStart?: boolean;
    notifyNewMedia?: boolean;
    notifySystem?: boolean;
    notifyPostEngagement?: boolean;
    // CORE-1453 — инициативные обращения Агапеллы. Тем же запросом, потому что
    // переключатель стоит в том же разделе; смысл у него другой, и пояснение на
    // экране это говорит.
    agapellaProactive?: boolean;
    // CORE-1482 — итоги недели от Агапеллы, своим ключом рядом.
    agapellaWeeklySummary?: boolean;
  }): Promise<NotificationSettings> {
    const response = await fetch(`${BASE_URL}/api/user/notification-settings`, {
      method: 'PUT',
      headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleResponse<NotificationSettings>(response);
  }

  // EP-01: User Preferences (Onboarding)
  async getPreferenceOptions(): Promise<PreferenceOptionsResponse> {
    // CORE-1010 — лэйблы приходят с бэка уже переведёнными, поэтому язык
    // интерфейса надо назвать. Именно язык интерфейса, а не системный: человек
    // с английским телефоном и русским приложением должен увидеть русский.
    // Ленивый импорт — как в register(): не тащить i18n в каждого потребителя
    // api и не замыкать круг импортов.
    let language: string | undefined;
    try {
      const { getCurrentLanguage } = await import('../i18n');
      language = getCurrentLanguage();
    } catch {
      // i18n недоступен (юнит-тесты) — бэк ответит на языке платформы.
    }

    const query = language ? `?lang=${encodeURIComponent(language)}` : '';
    const response = await fetch(`${BASE_URL}/api/preferences/options${query}`, {
      method: 'GET',
    });

    return this.handleResponse<PreferenceOptionsResponse>(response);
  }

  async getUserPreferences(): Promise<UserPreferencesResponse | null> {
    const response = await fetch(`${BASE_URL}/api/user/preferences`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    if (response.status === 204) {
      return null;
    }

    return this.handleResponse<UserPreferencesResponse>(response);
  }

  async saveUserPreferences(data: UserPreferencesRequest): Promise<UserPreferencesResponse> {
    const response = await fetch(`${BASE_URL}/api/user/preferences`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });

    return this.handleResponse<UserPreferencesResponse>(response);
  }

  // CORE-496 — home-page onboarding tour state. The tour UI is front-end; these
  // persist launch/progress/skip/close/complete/replay so it auto-launches once
  // and can be re-run from the Agapella chat.
  async getOnboardingTour(): Promise<OnboardingTourDto> {
    const response = await fetch(`${BASE_URL}/api/onboarding/tour`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<OnboardingTourDto>(response);
  }

  private async postOnboardingTour(path: string, body?: unknown): Promise<OnboardingTourDto> {
    const response = await fetch(`${BASE_URL}/api/onboarding/tour${path}`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return this.handleResponse<OnboardingTourDto>(response);
  }

  startOnboardingTour(): Promise<OnboardingTourDto> {
    return this.postOnboardingTour('/start');
  }

  replayOnboardingTour(): Promise<OnboardingTourDto> {
    return this.postOnboardingTour('/replay');
  }

  onboardingTourStep(step: number, skipped = false): Promise<OnboardingTourDto> {
    return this.postOnboardingTour('/step', { step, skipped });
  }

  completeOnboardingTour(): Promise<OnboardingTourDto> {
    return this.postOnboardingTour('/complete');
  }

  skipOnboardingTour(): Promise<OnboardingTourDto> {
    return this.postOnboardingTour('/skip');
  }

  closeOnboardingTour(step?: number): Promise<OnboardingTourDto> {
    return this.postOnboardingTour('/close', { step });
  }

  // EP-01: Home Vitrines
  async getStartingSoon(limit: number = 5): Promise<StartingSoonResponse> {
    const response = await fetch(`${BASE_URL}/api/home/starting-soon?limit=${limit}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<StartingSoonResponse>(response);
  }

  async getDailyRhythm(date?: string, timezone?: string, filters?: { masterId?: number; specialty?: string; timeOfDay?: string; directions?: string[]; includeLivePractices?: boolean }): Promise<DailyRhythmResponse> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (timezone) params.append('timezone', timezone);
    if (filters?.masterId) params.append('masterId', String(filters.masterId));
    if (filters?.specialty) params.append('specialty', filters.specialty);
    if (filters?.timeOfDay) params.append('timeOfDay', filters.timeOfDay);
    // April release: ?directions=A,B with OR-match against the canonical 8 directions.
    if (filters?.directions && filters.directions.length > 0) {
      params.append('directions', filters.directions.join(','));
    }
    // CORE-1712 — комнаты дня (kind `LivePractice`), видимые вызывающему, в том
    // числе «только подписчикам»; без флага сервер их не кладёт.
    if (filters?.includeLivePractices) params.append('includeLivePractices', 'true');

    const queryString = params.toString();
    const url = `${BASE_URL}/api/home/daily-rhythm${queryString ? `?${queryString}` : ''}`;

    // CORE-720 — identity matters here: the BE gates «только для подписчиков»
    // lives and rooms on the caller (LiveVisibilityFilters.WhereVisibleTo,
    // CORE-676). This call went out with NO headers at all, so every calendar
    // surface asked anonymously and a subscriber saw only public sessions —
    // the whole audience model was unreachable from the client. getHeaders
    // attaches the token only when there is one, so anonymous callers are
    // unaffected.
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    return this.handleResponse<DailyRhythmResponse>(response);
  }

  // Unified Lives endpoint (replaces N+1 per-master fetching)
  async getUpcomingLives(params?: { from?: string; to?: string; limit?: number; offset?: number; masterId?: number; specialty?: string; directions?: string[] }): Promise<PaginatedResponse<UpcomingLiveItem>> {
    const query = new URLSearchParams();
    if (params?.from) query.append('from', params.from);
    if (params?.to) query.append('to', params.to);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.masterId) query.append('masterId', String(params.masterId));
    if (params?.specialty) query.append('specialty', params.specialty);
    if (params?.directions && params.directions.length > 0) {
      query.append('directions', params.directions.join(','));
    }
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/lives/upcoming${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PaginatedResponse<UpcomingLiveItem>>(response);
  }

  // Unified Videos/Practices endpoint (replaces N+1 per-master fetching)
  async getVideos(params?: { limit?: number; offset?: number; sort?: string; contentType?: string; category?: string; directions?: string[]; duration?: string; masterProfileId?: number; excludeMasterProfileId?: number; search?: string }): Promise<PaginatedResponse<VideoPracticeItem>> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.sort) query.append('sort', params.sort);
    if (params?.contentType) query.append('contentType', params.contentType);
    if (params?.category) query.append('category', params.category);
    // CORE-1893 — several directions, comma-separated; the server keeps
    // practices tagged with any of them (union), before paging.
    if (params?.directions && params.directions.length > 0) query.append('directions', params.directions.join(','));
    if (params?.duration) query.append('duration', params.duration);
    if (params?.masterProfileId) query.append('masterProfileId', String(params.masterProfileId));
    // CORE-857 — block 2 of the master-direction view: same direction, every
    // master EXCEPT the source one. Disjoint from the masterProfileId query, so
    // the two blocks never overlap. Backend already binds this param.
    if (params?.excludeMasterProfileId) query.append('excludeMasterProfileId', String(params.excludeMasterProfileId));
    if (params?.search) query.append('search', params.search);
    const qs = query.toString();
    // CORE-1830 — public endpoint, but the token is sent (favourites, own and
    // subscribers-only items). Through authedRequest an expired token is
    // refreshed and retried instead of turning a catalogue read into a forced
    // logout; a guest sends no token and gets the plain request as before.
    return this.authedRequest<PaginatedResponse<VideoPracticeItem>>(
      `${BASE_URL}/api/content/videos${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  }

  // CORE-591 — unified redesigned home BFF. Public for guests, but sends
  // auth/anon headers when available so viewer-specific fields are populated.
  async getHomeFeed(params?: { locale?: string; timezone?: string }): Promise<HomeFeedResponse> {
    const query = new URLSearchParams();
    if (params?.locale) query.append('locale', params.locale);
    if (params?.timezone) query.append('timezone', params.timezone);
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/home${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<HomeFeedResponse>(response, true);
  }

  // CORE-1716 — личные ближайшие и идущие сессии (подписки, записи, свои),
  // включая «только подписчикам». Ответ на пользователя, поэтому с токеном;
  // `timezone` передавать явно — без него сервер считает в UTC. Без выброса на
  // вход по 401: это фоновый запрос рельса главной.
  async getMyUpcomingSessions(params?: { limit?: number; timezone?: string }): Promise<UpcomingSessionsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.timezone) query.append('timezone', params.timezone);
    const qs = query.toString();
    return this.authedRequest<UpcomingSessionsResponse>(
      `${BASE_URL}/api/user/me/upcoming-sessions${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true,
    );
  }

  // GET /api/home/collections — curated practice collections (BE MR !17).
  // Defensive field mapping: the BE contract is still settling, so we read a few
  // likely aliases (image/cover, count) and tolerate a wrapped `{ collections }`.
  async getHomeCollections(): Promise<HomeCollection[]> {
    const response = await fetch(`${BASE_URL}/api/home/collections`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    const data = await this.handleResponse<any>(response);
    const rows: any[] = Array.isArray(data) ? data : (data?.collections ?? data?.items ?? []);
    const mapItem = (it: any): HomeCollectionItem => ({
      id: it?.id ?? it?.entityId ?? it?.liveId ?? it?.videoId,
      kind: it?.kind ?? it?.type ?? null,
      title: it?.title ?? '',
      coverImageUrl: it?.coverImageUrl ?? it?.image ?? it?.imageUrl ?? null,
      durationMinutes: it?.durationMinutes ?? it?.duration ?? null,
      participantsCount: it?.participantsCount ?? null,
    });
    return rows.map((c: any) => ({
      id: c?.id ?? c?.slug ?? c?.title,
      title: c?.title ?? '',
      coverImageUrl: c?.coverImageUrl ?? c?.image ?? c?.imageUrl ?? null,
      practicesCount: c?.practicesCount ?? c?.count ?? (Array.isArray(c?.items) ? c.items.length : 0),
      items: Array.isArray(c?.items) ? c.items.map(mapItem) : [],
    }));
  }

  // Recordings (past lives for library)
  async getRecordings(params?: { limit?: number; offset?: number; sort?: string; masterProfileId?: number; duration?: string; search?: string }): Promise<PaginatedResponse<RecordingItem>> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    if (params?.sort) query.append('sort', params.sort);
    if (params?.masterProfileId) query.append('masterProfileId', String(params.masterProfileId));
    if (params?.duration) query.append('duration', params.duration);
    if (params?.search) query.append('search', params.search);
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/content/recordings${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PaginatedResponse<RecordingItem>>(response);
  }

  // Content categories
  async getContentCategories(): Promise<string[]> {
    const response = await fetch(`${BASE_URL}/api/content/categories`, {
      method: 'GET',
    });
    return this.handleResponse<string[]>(response);
  }

  // Canonical "direction" labels (Russian) accepted by the BE for lives,
  // practices, rooms, events. Use these as the source of truth for chip
  // selectors so we never POST a value the BE will 400 on.
  /**
   * CORE-913 — каталог направлений для блока на /practices.
   *
   * Отличается от getContentDirections тем, что несёт не только названия:
   * канонический порядок, локализованный заголовок и число практик. До этого
   * экран практик держал собственную копию списка в constants/directions.ts —
   * порядок приходилось синхронизировать руками, а счётчики взять было
   * неоткуда, хотя сервер их отдаёт.
   */
  /**
   * CORE-924 — персональная подборка практик для блока «Практики для вас».
   *
   * Ручка отвечает и гостю (стартовой подборкой), поэтому запрос сам по себе
   * не требует авторизации; решение, показывать ли блок неавторизованному,
   * принимает экран.
   */
  async getPracticeRecommendations(limit = 8): Promise<PracticeRecommendationsResponse> {
    const response = await this.authedRequest<PracticeRecommendationsResponse>(
      `${BASE_URL}/api/practices/recommendations?limit=${limit}`,
      { method: 'GET' },
    );
    return response;
  }

  async getPracticeDirectionCatalog(lang?: string): Promise<PracticeDirection[]> {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const response = await fetch(`${BASE_URL}/api/practices/directions${qs}`, {
      method: 'GET',
    });
    return this.handleResponse<PracticeDirection[]>(response);
  }

  async getContentDirections(): Promise<string[]> {
    const response = await fetch(`${BASE_URL}/api/content/directions`, {
      method: 'GET',
    });
    return this.handleResponse<string[]>(response);
  }

  // Masters currently streaming
  async getMastersWithLiveNow(): Promise<MasterWithLiveNow[]> {
    const response = await fetch(`${BASE_URL}/api/masters/with-live-now`, {
      method: 'GET',
    });
    if (response.status === 204) return [];
    return this.handleResponse<MasterWithLiveNow[]>(response);
  }

  // Calendar events (dates with scheduled lives)
  async getCalendarEvents(from: string, to: string, filters?: { masterId?: number; specialty?: string; directions?: string[]; includeRooms?: boolean }): Promise<CalendarEventsResponse> {
    const params = new URLSearchParams();
    params.append('from', from);
    params.append('to', to);
    if (filters?.masterId) params.append('masterId', String(filters.masterId));
    if (filters?.specialty) params.append('specialty', filters.specialty);
    if (filters?.directions && filters.directions.length > 0) {
      params.append('directions', filters.directions.join(','));
    }
    // CORE-1712 — дни с комнатами, видимыми вызывающему (в т.ч. «только
    // подписчикам»); без флага сервер отмечает только дни с лайвами.
    if (filters?.includeRooms) params.append('includeRooms', 'true');
    const qs = params.toString();
    // CORE-720 — identity matters here: the BE gates «только для подписчиков»
    // lives and rooms on the caller (LiveVisibilityFilters.WhereVisibleTo,
    // CORE-676). This call went out with NO headers at all, so every calendar
    // surface asked anonymously and a subscriber saw only public sessions —
    // the whole audience model was unreachable from the client. getHeaders
    // attaches the token only when there is one, so anonymous callers are
    // unaffected.
    const response = await fetch(`${BASE_URL}/api/home/calendar-events?${qs}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<CalendarEventsResponse>(response);
  }

  // Featured live (or null if 204)
  async getFeaturedLive(): Promise<FeaturedLive | null> {
    const response = await fetch(`${BASE_URL}/api/home/featured`, {
      method: 'GET',
    });
    if (response.status === 204) return null;
    return this.handleResponse<FeaturedLive>(response);
  }

  // Agora token for live viewer (public). `previewToken` = co-host invite,
  // `inviteCode` = CORE-721 audience invite (viewing only).
  async getViewerAgoraToken(
    liveId: number,
    previewToken?: string | null,
    inviteCode?: string | null,
  ): Promise<AgoraTokenResponse> {
    const qs = buildLiveAccessQuery(previewToken, inviteCode);
    // CORE-879 — кто спрашивает, здесь решает вход. Запрос уходил без
    // заголовков вовсе, сервер видел анонима и закрытую Live-сессию программы
    // не открывал даже купившему: 404 «Live not found», а у зрителя вечное
    // «Подключение к Live-сессии…». Тот же разрыв, что CORE-720 закрыл у
    // календаря. getHeaders прикладывает токен, только когда он есть, а
    // анониму — его X-Anon-User-Id (SCRUM-31), так что гость получает тот же
    // ответ, что и раньше, только со стабильным UID.
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/agora-token${qs}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<AgoraTokenResponse>(response);
  }

  // Agora token for master broadcaster (requires Master auth)
  // CORE-915 — `orientation` tells the BE how the master is holding the device
  // so the cloud-recording canvas matches the published track (a vertical phone
  // stream was being recorded on a 1280×720 landscape canvas and cropped top and
  // bottom). Optional: the BE defaults to landscape when it's absent/unknown.
  async getMasterAgoraToken(
    liveId: number,
    orientation?: 'portrait' | 'landscape',
  ): Promise<AgoraTokenResponse> {
    const qs = orientation ? `?orientation=${orientation}` : '';
    const response = await fetch(`${BASE_URL}/api/master/lives/${liveId}/agora-token${qs}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<AgoraTokenResponse>(response);
  }

  // CORE-1149 — the same call for the person ASSIGNED to conduct a live they
  // don't own (an эфир от имени сообщества). Different route because the master
  // routes sit behind the Master role, but the same server-side start sequence:
  // it stamps BroadcastStartedAt, so the host_no_show watchdog stops treating a
  // running practice as «ведущий не пришёл», and it hands back the master
  // profile's own uid so the audience sees the host in the main slot.
  async getHostAgoraToken(
    liveId: number,
    orientation?: 'portrait' | 'landscape',
  ): Promise<AgoraTokenResponse> {
    const qs = orientation ? `?orientation=${orientation}` : '';
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/host/token${qs}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<AgoraTokenResponse>(response);
  }

  /** CORE-1149 — assigned-host counterpart of {@link broadcastHeartbeat}. */
  async hostBroadcastHeartbeat(liveId: number): Promise<void> {
    try {
      await fetch(`${BASE_URL}/api/lives/${liveId}/host/heartbeat`, {
        method: 'POST',
        headers: await this.getHeaders(true),
      });
    } catch {
      // ignore — next interval tick will retry
    }
  }

  /** CORE-1149 — assigned-host counterpart of {@link stopRecording}: ends the эфир. */
  async hostEndBroadcast(liveId: number): Promise<RecordingStatusResponse | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/lives/${liveId}/host/end`, {
        method: 'POST',
        headers: await this.getHeaders(true),
      });
      if (!response.ok) {
        console.warn('Failed to end broadcast as host:', response.status);
        return null;
      }
      return response.json();
    } catch (e) {
      console.warn('Failed to end broadcast as host', e);
      return null;
    }
  }

  // CORE-528 — publisher token for the PRE-BROADCAST tech check. Same channel
  // and uid as `getMasterAgoraToken`, but the BE performs none of the go-live
  // side effects: no BroadcastStartedAt, no cloud recording, no roster fan-out,
  // and no «not earlier than 5 minutes before start» refusal. Use this to put
  // the master in the channel while `state === 'preview'` so a guest who opens
  // the `?preview=` invite is actually seen and heard; `getMasterAgoraToken`
  // remains the one and only way to genuinely start the broadcast.
  async getMasterTechCheckToken(liveId: number): Promise<AgoraTokenResponse> {
    const response = await fetch(`${BASE_URL}/api/master/lives/${liveId}/agora-token/tech-check`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<AgoraTokenResponse>(response);
  }

  // Agora token for the master's screen-share publisher (separate UID).
  // Backend refuses if cloud recording (i.e. the camera broadcast) hasn't
  // started yet — so call this AFTER getMasterAgoraToken + publish succeeds.
  async getMasterScreenAgoraToken(liveId: number): Promise<AgoraTokenResponse> {
    const response = await fetch(`${BASE_URL}/api/master/lives/${liveId}/agora-token/screen`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<AgoraTokenResponse>(response);
  }

  // Toggle screen-share state for a live the master owns. Call AFTER the
  // FE has successfully (un)published the screen track — this is what
  // flips Live.IsScreenSharing and updates the recording layout server-side.
  async setLiveScreenShareState(liveId: number, active: boolean): Promise<{ isScreenSharing: boolean }> {
    const response = await fetch(`${BASE_URL}/api/master/lives/${liveId}/screen-share`, {
      method: 'POST',
      headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
      body: JSON.stringify({ active }),
    });
    return this.handleResponse<{ isScreenSharing: boolean }>(response);
  }

  // CORE-736 — Agora token for the publisher that carries the library practice
  // the master plays during the live (separate UID, like the screen share).
  // Backend refuses before the camera broadcast is running: without a recording
  // there is nothing for this publisher to feed.
  async getMasterPracticeAgoraToken(liveId: number): Promise<AgoraTokenResponse> {
    const response = await fetchWithTimeout(`${BASE_URL}/api/master/lives/${liveId}/agora-token/practice`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    }, 20_000);
    return this.handleResponse<AgoraTokenResponse>(response);
  }

  // CORE-736 — flip Live.IsPracticeStreaming and re-render the recording layout.
  // Call AFTER the FE has (un)published the captured clip. Both calls run on the
  // publisher's hot path (play/pause), so they carry a timeout — a stalled
  // request here must never sit on the master's UI (the CORE-518/522 lesson).
  async setLivePracticeStreamState(liveId: number, active: boolean): Promise<{ isPracticeStreaming: boolean }> {
    const response = await fetchWithTimeout(`${BASE_URL}/api/master/lives/${liveId}/practice-stream`, {
      method: 'POST',
      headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
      body: JSON.stringify({ active }),
    }, 20_000);
    return this.handleResponse<{ isPracticeStreaming: boolean }>(response);
  }

  // Chat history (REST fallback)
  // CORE-1253 — веб догружает отсюда историю с начала. С токеном: история
  // закрытой Live принадлежит её аудитории. 401 здесь — не конец сессии.
  // CORE-1525 — Live «по приглашению» без кода отвечает 404 даже аудитории:
  // коды доступа те же, что у `getLiveById`.
  async getChatHistory(
    liveId: number,
    limit: number = 50,
    previewToken?: string | null,
    inviteCode?: string | null,
    // CORE-1820 — сообщения старше этого id (по возрастанию, не больше `limit`).
    beforeId?: number | null,
  ): Promise<ChatMessage[]> {
    const qs = buildLiveAccessQuery(previewToken, inviteCode);
    const before = beforeId != null ? `&beforeId=${beforeId}` : '';
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/chat${qs}${qs ? '&' : '?'}limit=${limit}${before}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<ChatMessage[]>(response, true);
  }

  // EP-07: Get live by ID. `previewToken` is the co-host invite (publisher
  // rights); `inviteCode` is CORE-721's audience invite — the only way anyone
  // but the owner can open a «Только по приглашению» live.
  async getLiveById(
    id: number,
    previewToken?: string | null,
    inviteCode?: string | null,
  ): Promise<LiveDetailResponse> {
    const qs = buildLiveAccessQuery(previewToken, inviteCode);
    const response = await fetch(`${BASE_URL}/api/lives/${id}${qs}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    // Live detail is publicly readable — a 401 here means a stale token,
    // not «session expired». Don't reset the nav stack to /login (owner
    // reported tapping an announcement card opened LiveDetail and then
    // bounced back). Let the screen surface the error inline instead.
    return this.handleResponse<LiveDetailResponse>(response, true);
  }

  /**
   * CORE-879 — откроется ли ссылка на Live-сессию у того, кому её перешлют.
   *
   * Публичный ответ уровня видимости не несёт, а купивший программу видит
   * закрытую сессию наравне с открытой: сервер пускает его по покупке. Чтобы
   * не раздавать ссылку, которая у получателя откроет «Live-сессия не
   * найдена», спрашиваем сервер так, как спросит получатель: без токена и без
   * анонимного id, с тем же `?invite=`, что уйдёт в ссылке. Решает
   * LiveAudienceAccess, а не догадка клиента.
   *
   * Сбой сети — «не откроется»: вызывают это только для сессий, которые у
   * человека по программе, и лишняя кнопка там хуже недостающей.
   */
  async liveOpensForGuest(
    id: number,
    previewToken?: string | null,
    inviteCode?: string | null,
  ): Promise<boolean> {
    return (await this.liveGuestAccess(id, previewToken, inviteCode)) === 'open';
  }

  /**
   * То же, но с третьим ответом: `unknown` — сеть упала или сервер ответил
   * 5xx, то есть про доступ он ничего не сказал. Кеш в `useLiveShareGate`
   * такой ответ не держит, иначе обрыв связи прятал кнопку на минуту.
   */
  async liveGuestAccess(
    id: number,
    previewToken?: string | null,
    inviteCode?: string | null,
  ): Promise<'open' | 'closed' | 'unknown'> {
    const qs = buildLiveAccessQuery(previewToken, inviteCode);
    try {
      const response = await fetchWithTimeout(`${BASE_URL}/api/lives/${id}${qs}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, 15_000);
      if (response.ok) return 'open';
      return response.status >= 500 ? 'unknown' : 'closed';
    } catch {
      return 'unknown';
    }
  }

  // ── Privacy controls (Master) ────────────────────────────────────────────
  // Mints a new cohost preview-token (BE rev 4 — no longer flips isPrivate;
  // the live stays public, the token only gates publisher access). Multi-
  // token-per-live is supported, so calling this repeatedly produces fresh
  // links without revoking old ones. Old single-token rotation behaviour is
  // emulated by calling this and then DELETE on the previous token.
  async createLivePreviewToken(liveId: number): Promise<LivePreviewTokenResponse> {
    // CORE-518 — timeout so a stalled mobile request can't hang the invite-link
    // loader forever. This mint runs on the create-Live «С гостями» success/
    // publish step (cohosts ON on dev): LiveStreamModal shows a spinner until it
    // settles, and LiveWizard.native's publish awaits it — a bare fetch that
    // never resolves left the «3-й шаг планирования» stuck on an infinite loader.
    // Same fix the sibling createLive got under CORE-431; on timeout the callers'
    // catch surfaces an error / proceeds instead of spinning.
    const response = await fetchWithTimeout(`${BASE_URL}/api/master/content/lives/${liveId}/preview-token`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    }, 30_000);
    return this.handleResponse<LivePreviewTokenResponse>(response);
  }

  /**
   * Lists all currently-active cohost preview tokens for a live (BE rev 4).
   * Owner-only. Used by the host UI panel that surfaces outstanding invite
   * links + how many cohosts each one is supporting.
   */
  async listLivePreviewTokens(liveId: number): Promise<LivePreviewTokenInfo[]> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${liveId}/preview-tokens`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<LivePreviewTokenInfo[]>(response);
  }

  /**
   * Revokes a specific cohost preview token (BE rev 4). Owner-only.
   * Active cohost sessions that joined via this token are NOT disconnected
   * — they keep their slot until they leave or the host explicitly calls
   * `endCohostSession`. New attempts to join via the revoked token return
   * 410 Gone.
   */
  async revokeLivePreviewToken(liveId: number, token: string): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/${liveId}/preview-token/${encodeURIComponent(token)}`,
      { method: 'DELETE', headers: await this.getHeaders(true) },
    );
    await this.handleResponse<void>(response);
  }

  /**
   * Force-disconnects a specific cohost by their Agora UID (BE rev 4).
   * Owner-only. Frees the cohost slot immediately. The kicked guest can
   * re-join through the same multi-use share-link unless the master also
   * calls `revokeLivePreviewToken` to invalidate the underlying token.
   */
  async endCohostSession(liveId: number, uid: number): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/${liveId}/end-cohost-session`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      },
    );
    await this.handleResponse<void>(response);
  }

  /**
   * CORE-1201 — модерация участника на сцене эфира: микрофон и камера по
   * отдельности. `enabled: false` ставит запрет, `true` снимает; снятие ничего
   * не включает — оно возвращает человеку право включить самому.
   *
   * Владелец эфира — единственный, кому бэкенд это разрешает (проверка
   * `live.MasterProfileId != profile.Id`). 404 означает, что такого активного
   * гостя на сцене нет: показать это ведущему, а не проглотить.
   */
  async setLiveCohostMic(liveId: number, uid: number, enabled: boolean): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/${liveId}/cohosts/${uid}/mic`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      },
    );
    await this.handleResponse<void>(response);
  }

  /** CORE-1201 — см. `setLiveCohostMic`, только камера. */
  async setLiveCohostCamera(liveId: number, uid: number, enabled: boolean): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/${liveId}/cohosts/${uid}/camera`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      },
    );
    await this.handleResponse<void>(response);
  }

  /**
   * CORE-1201 — снять участника со сцены по-настоящему.
   *
   * Отличается от `endCohostSession`, который закрывает только строку сессии в
   * БД: по собственному описанию бэкенда «a guest left that way keeps
   * publishing until they self-leave or their token expires». Этот зовёт
   * SCRUM-30/CORE-1047 eject — удаление из медиасессии у вендора (Agora
   * kicking-rule или LiveKit RemoveParticipant) и закрытие строки.
   */
  async ejectLiveCohost(liveId: number, uid: number): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/master/content/lives/${liveId}/cohosts/${uid}/eject`,
      {
        method: 'POST',
        headers: await this.getHeaders(true),
      },
    );
    await this.handleResponse<void>(response);
  }

  async getLiveCohosts(liveId: number): Promise<import('../types/auth').LiveCohostInfo[]> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/cohosts`);
    return this.handleResponse<import('../types/auth').LiveCohostInfo[]>(response);
  }

  async getLiveViewers(liveId: number): Promise<import('../types/auth').LiveViewerInfo[]> {
    const response = await fetch(
      `${BASE_URL}/api/lives/${liveId}/viewers`,
      { headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/auth').LiveViewerInfo[]>(response);
  }

  // CORE-137 — reason/description уходят в лог user_kicked_from_room и в
  // письмо на moderation@agapella.com на стороне БЭ. Ошибка письма кик не
  // отменяет — БЭ отвечает 200 и логирует сбой у себя.
  async kickLiveViewer(
    liveId: number,
    userId: string,
    opts: { durationSeconds?: number; reason?: string; description?: string } = {},
  ): Promise<void> {
    const params = new URLSearchParams({ durationSeconds: String(opts.durationSeconds ?? 600) });
    if (opts.reason) params.set('reason', opts.reason);
    if (opts.description) params.set('description', opts.description);
    const response = await fetch(
      `${BASE_URL}/api/lives/${liveId}/viewers/${encodeURIComponent(userId)}/kick?${params.toString()}`,
      { method: 'POST', headers: await this.getHeaders(true) },
    );
    await this.handleResponse<void>(response);
  }

  // Per-live engagement stats — owner only. Returns subscribers/reminders/attendees/comments/watched counts.
  async getLiveStats(liveId: number): Promise<LiveStatsResponse> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${liveId}/stats`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<LiveStatsResponse>(response);
  }

  /**
   * AMB-215 — batch variant. Returns a map keyed by liveId for every id
   * owned by the calling master. Ids that don't exist or aren't owned by
   * the caller are silently omitted (the server does the ownership gate
   * per id). Caller treats a missing key the same as zero stats.
   *
   * Up to 200 ids per call; longer lists should be chunked client-side.
   * Replaces N×HTTP × 4 SQL COUNT fan-out from /dashboard#feed with one
   * HTTP × 4 grouped queries.
   */
  async getLiveStatsBatch(liveIds: number[]): Promise<Record<number, LiveStatsResponse>> {
    if (liveIds.length === 0) return {};
    const ids = liveIds.join(',');
    const response = await fetch(`${BASE_URL}/api/master/content/lives/stats?ids=${ids}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<Record<number, LiveStatsResponse>>(response);
  }

  // Live moderators — owner only. Moderators get publisher Agora tokens so they
  // can help test the broadcast (camera/mic/screen) before the master goes live.
  async getLiveModerators(liveId: number): Promise<LiveModerator[]> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${liveId}/moderators`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<LiveModerator[]>(response);
  }

  // CORE-1149 — `isHost` promotes the grant to «ведущий этого эфира»: on a live
  // there is exactly one, and re-posting an existing grant just moves the flag.
  async addLiveModerator(liveId: number, userId: string, isHost = false): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${liveId}/moderators`, {
      method: 'POST',
      headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, isHost }),
    });
    await this.handleResponse<void>(response);
  }

  async removeLiveModerator(liveId: number, userId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${liveId}/moderators/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // Clears isPrivate + previewToken — the live becomes visible to everyone.
  async publishLive(liveId: number): Promise<MasterLive> {
    const response = await fetch(`${BASE_URL}/api/master/content/lives/${liveId}/publish`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterLive>(response);
  }

  // Get video practice detail by ID
  async getVideoPracticeDetail(id: number): Promise<VideoPracticeDetail> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${id}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<VideoPracticeDetail>(response);
  }

  // EP-01: Current Live (returns array of all current lives)
  async getCurrentLives(): Promise<CurrentLiveResponse[]> {
    // CORE-720 — identity matters here: the BE gates «только для подписчиков»
    // lives and rooms on the caller (LiveVisibilityFilters.WhereVisibleTo,
    // CORE-676). This call went out with NO headers at all, so every calendar
    // surface asked anonymously and a subscriber saw only public sessions —
    // the whole audience model was unreachable from the client. getHeaders
    // attaches the token only when there is one, so anonymous callers are
    // unaffected.
    const response = await fetch(`${BASE_URL}/api/lives/current`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    if (response.status === 204) {
      return [];
    }

    return this.handleResponse<CurrentLiveResponse[]>(response);
  }

  // UC-07.04: Mark live as watched
  async markAsWatched(liveId: number, durationWatchedSeconds?: number): Promise<WatchedStateResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/watched`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: durationWatchedSeconds != null
        ? JSON.stringify({ durationWatchedSeconds })
        : undefined,
    });
    return this.handleResponse<WatchedStateResponse>(response);
  }

  // UC-07.04: Get watched history
  async getWatchedHistory(): Promise<WatchedItemResponse[]> {
    const response = await fetch(`${BASE_URL}/api/user/watched`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<WatchedItemResponse[]>(response);
  }

  // === Practice Reflection ===

  /** Mark a video practice as completed. Backend decides whether the
   *  threshold was met and whether to trigger an Agapella reflection. */
  async markPracticeCompleted(
    videoPracticeId: number, durationWatchedSeconds?: number,
  ): Promise<PracticeCompletionResponse> {
    const response = await fetch(`${BASE_URL}/api/videopractices/${videoPracticeId}/completed`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ durationWatchedSeconds: durationWatchedSeconds ?? null }),
    });
    return this.handleResponse<PracticeCompletionResponse>(response);
  }

  /** Mark a live as completed (separate from /watched — completion is
   *  the trigger for reflection). */
  async markLiveCompleted(
    liveId: number, durationWatchedSeconds?: number,
  ): Promise<PracticeCompletionResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/completed`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ durationWatchedSeconds: durationWatchedSeconds ?? null }),
    });
    return this.handleResponse<PracticeCompletionResponse>(response);
  }

  /** Confirmed long-term facts surfaced in the "Что я о тебе помню" UI. */
  async getMemoryFacts(): Promise<MemoryFactDto[]> {
    const response = await fetch(`${BASE_URL}/api/user/memory-facts`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MemoryFactDto[]>(response);
  }

  /** Pending candidates awaiting user opt-in. Hidden until confirmed. */
  async getPendingMemoryFacts(): Promise<MemoryFactDto[]> {
    const response = await fetch(`${BASE_URL}/api/user/memory-facts/pending`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MemoryFactDto[]>(response);
  }

  async confirmMemoryFact(id: number): Promise<MemoryFactDto> {
    const response = await fetch(`${BASE_URL}/api/user/memory-facts/${id}/confirm`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MemoryFactDto>(response);
  }

  async dismissMemoryFact(id: number): Promise<MemoryFactDto> {
    const response = await fetch(`${BASE_URL}/api/user/memory-facts/${id}/dismiss`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MemoryFactDto>(response);
  }

  async deleteMemoryFact(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/user/memory-facts/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete memory fact ${id}: ${response.status}`);
    }
  }

  // Cloud Recording (master only)
  async startRecording(liveId: number): Promise<RecordingStatusResponse | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/master/lives/${liveId}/recording/start`, {
        method: 'POST',
        headers: await this.getHeaders(true),
      });
      if (!response.ok) {
        console.warn('Failed to start recording:', response.status);
        return null;
      }
      return response.json();
    } catch {
      console.warn('Recording start failed silently');
      return null;
    }
  }

  async stopRecording(liveId: number): Promise<RecordingStatusResponse | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/master/lives/${liveId}/recording/stop`, {
        method: 'POST',
        headers: await this.getHeaders(true),
      });
      if (!response.ok) {
        console.warn('Failed to stop recording:', response.status);
        return null;
      }
      return response.json();
    } catch {
      console.warn('Recording stop failed silently');
      return null;
    }
  }

  /**
   * Ping the BE while the master is actively publishing audio/video. The BE
   * tracks `BroadcasterLastSeenAt` from these pings; absent pings for >10min
   * trip the LiveAutoEnd watchdog. Fire-and-forget — failures are non-fatal,
   * the next tick retries.
   */
  async broadcastHeartbeat(liveId: number): Promise<void> {
    try {
      await fetch(`${BASE_URL}/api/master/lives/${liveId}/broadcast-heartbeat`, {
        method: 'POST',
        headers: await this.getHeaders(true),
      });
    } catch {
      // ignore — next interval tick will retry
    }
  }

  // ============ Community Feed (Posts) ============

  /**
   * CORE-717 — the feed request had NO timeout, so a single stalled request
   * left the profile on a spinner for the rest of the session: nothing ever
   * settled, nothing retried, and the only way out was a hard refresh.
   *
   * Measured on prod (03.08, guest on /u/:id#feed): this exact call hung past
   * 30 s on a cold load, while an identical request issued by hand from the
   * same page came back in 300-500 ms, and the endpoint itself answers in
   * ~0.7 s by curl (103 KB). Whatever stalls that first attempt, a fresh one
   * succeeds — which is also why leaving the tab and coming back "fixed" it.
   *
   * So: bound the attempt and replay it once. A GET is idempotent, so the
   * retry is free of side effects. The ceiling is deliberately well above a
   * healthy round-trip (and above the RU edge's slower path) — it exists to
   * break a stall, not to police latency.
   */
  async getMasterPosts(masterProfileId: number, limit = 20, offset = 0): Promise<MasterPost[]> {
    const url = `${BASE_URL}/api/masterprofile/${masterProfileId}/posts?limit=${limit}&offset=${offset}`;
    const attempt = async () => fetchWithTimeout(url, {
      method: 'GET',
      headers: await this.getHeaders(true),
    }, POSTS_ATTEMPT_TIMEOUT_MS);

    let response: Response;
    try {
      response = await attempt();
    } catch (err) {
      // Only a stall earns the replay. A real transport failure (offline,
      // DNS, CORS) would fail identically the second time, so let it through
      // to the caller's error handling instead of doubling the wait.
      if ((err as { name?: string })?.name !== 'TimeoutError') throw err;
      response = await attempt();
    }
    const data = await this.handleResponse<{ items: MasterPost[]; totalCount: number }>(response);
    return data.items;
  }

  /**
   * CORE-1220 — поставить реакцию на публикацию.
   *
   * Возвращает пересчитанный сервером расклад целиком, а не дельту: одну и ту
   * же публикацию человек может держать открытой в двух местах, и складывать
   * счётчики на клиенте значило бы расходиться с правдой при каждом таком
   * случае.
   *
   * Своих реакций может быть НЕСКОЛЬКО — уникальность на сервере по тройке
   * (публикация, человек, тип), а не по паре. Это не лайк-переключатель.
   */
  async addPostReaction(postId: number, type: string): Promise<PostReactionsResponse> {
    const response = await fetch(`${BASE_URL}/api/posts/${postId}/reactions`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ type }),
    });
    return this.handleResponse<PostReactionsResponse>(response);
  }

  /** CORE-1685 — реакция комплекта на комментарий; ответ — расклад целиком. */
  async addCommentReaction(postId: number, commentId: number, type: string): Promise<PostReactionsResponse> {
    const response = await fetch(`${BASE_URL}/api/posts/${postId}/comments/${commentId}/reactions`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ type }),
    });
    return this.handleResponse<PostReactionsResponse>(response);
  }

  /** CORE-1685 — снять свою реакцию с комментария. */
  async removeCommentReaction(postId: number, commentId: number, type: string): Promise<PostReactionsResponse> {
    const response = await fetch(
      `${BASE_URL}/api/posts/${postId}/comments/${commentId}/reactions/${encodeURIComponent(type)}`,
      { method: 'DELETE', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<PostReactionsResponse>(response);
  }

  /** CORE-1220 — снять свою реакцию. Ответ тот же, что у постановки. */
  async removePostReaction(postId: number, type: string): Promise<PostReactionsResponse> {
    const response = await fetch(`${BASE_URL}/api/posts/${postId}/reactions/${encodeURIComponent(type)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PostReactionsResponse>(response);
  }

  async getPostComments(postId: number, limit = 50, offset = 0): Promise<PostComment[]> {
    const response = await fetch(`${BASE_URL}/api/posts/${postId}/comments?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    const data = await this.handleResponse<{ items: PostComment[]; totalCount: number }>(response);
    return data.items;
  }

  async createPost(data: CreatePostRequest): Promise<MasterPost> {
    // CORE-204 — the feed-post multipart (photos) had NO upload telemetry, so a
    // failed «Опубликовать» left only a Sentry line and no account-attributed,
    // status-tagged breadcrumb. Wire it into the same CORE-486 stream as the
    // video presign path (kind='post', transport='multipart') so 413 (Vercel's
    // ~4.5 MB proxy cap on web / oversized photo), timeouts, and 5xx are
    // queryable per master in the admin panel + MCP/SQL, without asking the
    // master to open DevTools.
    const photos = (data.images && data.images.length > 0)
      ? data.images
      : (data.image ? [data.image] : []);
    const photoBytes = photos.reduce(
      (sum, p: any) => sum + (typeof p?.size === 'number' ? p.size : 0),
      0,
    );
    const ctx: UploadContext = {
      uploadSessionId: newUploadSessionId(),
      kind: 'post',
      transport: 'multipart',
      fileName: null,
      // Web Blobs carry .size; native {uri} assets don't, so leave it null there.
      fileSizeBytes: photoBytes > 0 ? photoBytes : null,
      contentType: null,
    };
    const startedAt = Date.now();
    trackUpload('upload_started', ctx, { photoCount: photos.length, hasVideo: !!data.videoUrl });

    // CORE-450 — upload photos straight to S3 via presign (Yandex primary for RU,
    // AWS fallback) so they never ride the createPost multipart body, which the RU
    // origin's ~128 KB upload-body shaping stalls (prod: a lone photo hung 60 s on
    // api + api-ru while a 24 MB video presigned in ~5 s from the same device). The
    // post then carries only the resulting URLs as string fields. On ANY presign/S3
    // failure we fall back to the legacy multipart-file path, so a presign outage
    // can't block posting — worst case is the old (shaped) behaviour, not worse.
    let imageUrls: string[] | null = null;
    if (photos.length > 0) {
      try {
        const urls: string[] = [];
        for (const p of photos) {
          // CORE-1477 — снимки уходят по одному, поэтому и ход считается ими:
          // байтового прогресса у этого PUT нет, а молчащий спиннер на
          // несколько фотографий читается как зависание.
          urls.push(await presignAndPutPostImage(p, { signal: data.signal }));
          data.onPhotoProgress?.(urls.length, photos.length);
        }
        imageUrls = urls;
        ctx.transport = 'presigned';
      } catch (presignErr: any) {
        // CORE-722 — «this file cannot be published» is a verdict, not a
        // transport failure. Falling back re-sent the very file we had just
        // refused down the legacy multipart path: the author waited out a
        // second upload (on a shaped RU link, up to two minutes of it) only to
        // be told «проверьте введённые данные» by the API's own 400. The
        // reason we already had — «сохрани как JPEG» — never reached them.
        if (presignErr instanceof PhotoFormatError) {
          trackUploadError('upload_failed', ctx, presignErr, {
            durationMs: Date.now() - startedAt,
            errorKind: 'unsupported_photo_format',
            photoCount: photos.length,
            hasVideo: !!data.videoUrl,
          });
          throw presignErr;
        }
        imageUrls = null;
        trackUpload('upload_retry', ctx, {
          errorKind: presignErr?.name || 'presign_failed',
          host: 'multipart-fallback',
          photoCount: photos.length,
          hasVideo: !!data.videoUrl,
        });
      }
    }

    // A FormData carrying file blobs can't be safely re-sent after a failed
    // fetch (its stream may be consumed), so build a fresh one per attempt.
    const buildForm = () => {
      const fd = new FormData();
      fd.append('Content', data.content);
      if (imageUrls) {
        // CORE-450 — photos already on S3; send only their URLs (repeated
        // `ImageUrls` string fields → BE List<string>). Body stays a few KB, so
        // the origin upload-body shaping that stalls file uploads never bites.
        imageUrls.forEach((u) => fd.append('ImageUrls', u));
      } else if (data.images && data.images.length > 0) {
        // CORE-338 — multi-photo: repeated `Images` fields (BE reads a List).
        // Falls back to the legacy single `Image` when only one is given.
        data.images.forEach((img) => fd.append('Images', img as any));
      } else if (data.image) {
        fd.append('Image', data.image as any);
      }
      // CORE-338 — video is presign-uploaded to S3; only its URL rides the post.
      if (data.videoUrl) fd.append('VideoUrl', data.videoUrl);
      if (data.isPinned) fd.append('IsPinned', 'true');
      // CORE-1366 — the response calls this `audience`, while the multipart
      // create contract calls the same value `Visibility`.
      if (data.audience) fd.append('Visibility', data.audience);
      // CORE-1697 — связанное обсуждение создаётся вместе с публикацией.
      if (data.withDiscussion) fd.append('WithDiscussion', 'true');
      // CORE-1230 — отчёт о прошедшем офлайн-событии (BE CORE-1247, int32).
      if (data.eventReportForId != null) fd.append('EventReportForId', String(data.eventReportForId));
      appendRepostOf(fd, data.repostOf);
      return fd;
    };

    const postTo = async (url: string) => {
      const token = await storage.getToken();
      // CORE-431 — was a plain fetch with no timeout: on a stuck RU connection
      // the promise never settled, so the composer's `finally` never ran and the
      // «Опубликовать» spinner span forever. fetchWithTimeout aborts with a
      // friendly TimeoutError after 60 s so the composer clears + surfaces it.
      // CORE-450 — the SAME Idempotency-Key rides the primary request and the
      // api-ru retry (uploadSessionId is stable across both attempts of this
      // call), so a timed-out POST that actually reached the server can't be
      // duplicated by the retry — the BE returns the already-created post.
      return fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Idempotency-Key': data.idempotencyKey ?? ctx.uploadSessionId,
        },
        body: buildForm(),
      });
    };

    // CORE-450 — fall back to api-ru (Moscow nginx, 500 MB body, no Vercel edge)
    // when the primary path fails in a way api-ru can fix. ruFallbackUrl only
    // rewrites a prod-api URL (api.agapella.io → api-ru) and returns a
    // dev/relative URL unchanged, so this never cross-posts a dev session to the
    // prod upstream. Web already retries timeout/network to api-ru via the
    // installed window.fetch patch; native has no such patch, so this closes
    // that gap there too.
    const primaryUrl = `${BASE_URL}/api/master/content/posts`;
    const fallbackUrl = ruFallbackUrl(primaryUrl);
    const canFallback = fallbackUrl !== primaryUrl;

    try {
      let response: Response;
      try {
        response = await postTo(primaryUrl);
      } catch (netErr) {
        // A TimeoutError (fetchWithTimeout's 60 s ceiling) or a TypeError
        // (DNS/TCP/TLS «Failed to fetch») means the primary connection failed.
        // NOTE: retrying a POST after a timeout CAN duplicate the post if the
        // server actually received it but the response was lost — accepted risk
        // per owner (CORE-450); an idempotency key on createPost is the durable
        // fix. A user-initiated AbortError is intentional and never retried.
        const name = (netErr as any)?.name;
        const retriable = name === 'TimeoutError' || netErr instanceof TypeError;
        if (!canFallback || !retriable) throw netErr;
        trackUpload('upload_retry', ctx, {
          errorKind: name || 'NetworkError',
          host: 'api-ru',
          photoCount: photos.length,
          hasVideo: !!data.videoUrl,
        });
        response = await postTo(fallbackUrl);
      }
      // A 413 is ALWAYS an edge rejection (Vercel's ~4.5 MB
      // FUNCTION_PAYLOAD_TOO_LARGE on the *.vercel.app /api rewrite, or nginx's
      // client_max_body_size). The app itself returns 400 for an oversize photo
      // (ValidateImage), never 413 — so on a 413 the bytes never reached the
      // backend and NO post was created: safe to retry with no duplicate risk.
      if (response.status === 413 && canFallback) {
        trackUpload('upload_retry', ctx, {
          httpStatus: 413,
          errorKind: 'payload_too_large',
          host: 'api-ru',
          photoCount: photos.length,
          hasVideo: !!data.videoUrl,
        });
        response = await postTo(fallbackUrl);
      }
      const post = await this.handleResponse<MasterPost>(response);
      trackUpload('upload_completed', ctx, {
        durationMs: Date.now() - startedAt,
        photoCount: photos.length,
        hasVideo: !!data.videoUrl,
      });
      return post;
    } catch (err) {
      // trackUploadError extracts httpStatus (413/5xx — handleResponse attaches
      // it) and errorKind ('AbortError'/'TimeoutError' for the 60 s timeout).
      trackUploadError('upload_failed', ctx, err, {
        durationMs: Date.now() - startedAt,
        photoCount: photos.length,
        hasVideo: !!data.videoUrl,
      });
      throw err;
    }
  }

  async updatePost(postId: number, data: UpdatePostRequest): Promise<MasterPost> {
    const formData = new FormData();
    if (data.content !== undefined) formData.append('Content', data.content);
    // CORE-526 — `Images` (new files) + `ImageUrls` (kept originals) together
    // REPLACE the post's photo set; omitting both leaves it untouched.
    if (data.images && data.images.length > 0) {
      data.images.forEach((img) => formData.append('Images', img as any));
    } else if (data.image) {
      formData.append('Image', data.image as any);
    }
    if (data.imageUrls) {
      data.imageUrls.forEach((url) => formData.append('ImageUrls', url));
    }
    // Tri-state video: '' removes, a URL sets/replaces, undefined = untouched.
    if (data.videoUrl !== undefined) formData.append('VideoUrl', data.videoUrl);
    if (data.removeImage) formData.append('RemoveImage', 'true');
    if (data.isPinned !== undefined) formData.append('IsPinned', String(data.isPinned));

    const token = await storage.getToken();
    const url = `${BASE_URL}/api/master/content/posts/${postId}`;
    const headers = { 'Authorization': `Bearer ${token}` };
    // CORE-1477 — правка с фотографиями уезжает телом запроса, и на телефоне
    // это те же минуты ожидания, что у публикации: с `onProgress`/`signal`
    // видно ход и есть чем прервать. Без них — прежний путь, как у веба.
    if (data.onProgress || data.signal) {
      const response = await uploadFormWithProgress(url, formData, headers, {
        method: 'PUT',
        onProgress: data.onProgress,
        signal: data.signal,
      });
      return this.handleResponse<MasterPost>(response);
    }
    // Mirror createPost: a media edit may PUT a fresh video URL over a slow RU
    // link — fetchWithTimeout aborts with a friendly error so the editor's
    // spinner can't span forever (CORE-431).
    const response = await fetchWithTimeout(url, {
      method: 'PUT',
      headers,
      body: formData,
    });
    return this.handleResponse<MasterPost>(response);
  }

  async deletePost(postId: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/posts/${postId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async likePost(postId: number): Promise<LikeResponse> {
    const response = await fetch(`${BASE_URL}/api/posts/${postId}/like`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<LikeResponse>(response);
  }

  async createComment(postId: number, data: CreateCommentRequest): Promise<PostComment> {
    const response = await fetch(`${BASE_URL}/api/posts/${postId}/comments`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<PostComment>(response);
  }

  // Pin a post in the master's feed. Single-pin invariant: the BE unpins any
  // previously-pinned post on the same master. Master role + ownership required.
  async pinPost(postId: number): Promise<MasterPost> {
    const response = await fetch(`${BASE_URL}/api/master/content/posts/${postId}/pin`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterPost>(response);
  }

  // Clear the pin on a post. Idempotent — returns the post state regardless of
  // whether it was previously pinned.
  async unpinPost(postId: number): Promise<MasterPost> {
    const response = await fetch(`${BASE_URL}/api/master/content/posts/${postId}/pin`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterPost>(response);
  }

  // ============ Manifesto Video ============

  async getManifestoVideo(masterProfileId: number): Promise<ManifestoVideo | null> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/manifesto-video`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    if (response.status === 204) return null;
    return this.handleResponse<ManifestoVideo>(response);
  }

  async saveManifestoVideo(
    data: ManifestoVideoRequest,
    onProgress?: (percent: number) => void,
  ): Promise<ManifestoVideo> {
    const token = await storage.getToken();

    // CORE-232 — push the video straight to S3 via a presigned PUT. RU uploads
    // to the prod origin choke at ~128 KB (origin-IP bandwidth shaping); S3 is
    // not shaped, so the bytes go direct and the manifesto PUT below carries
    // only metadata + the small cover frame (fast on any link). Thumbnail-only
    // saves skip the upload entirely. (Superseded the chunked-to-origin path.)
    let videoFileUrl: string | undefined;
    if (data.videoFile) {
      const fileName = (data.videoFile as File).name || 'manifesto.mp4';
      videoFileUrl = await presignAndPutVideo(data.videoFile, {
        kind: 'manifesto',
        fileName,
        onProgress: onProgress ? (info) => onProgress(info.percent) : undefined,
      });
    }

    // CORE-291 — the captured cover frame is a native-res JPEG (~150 KB). It
    // used to ride this multipart PUT to the origin, but that exceeds the RU
    // ~128 KB upload shaping and — because the PUT below had no timeout — hung
    // forever at 100% (only when a frame was picked; a frameless save carries
    // no image and went through). Push the frame straight to S3 via a presigned
    // PUT (unshaped, like the video in CORE-232 and the practice cover in
    // CORE-315) and reference its URL, so the PUT stays a few KB of text.
    let thumbnailFileUrl: string | undefined;
    let inlineThumbnail: any;
    if (data.thumbnailFile) {
      if (Platform.OS === 'web') {
        try {
          thumbnailFileUrl = await presignAndPutCover(data.thumbnailFile as Blob);
        } catch (e) {
          // Fail open — fall back to the inline multipart path (small enough on
          // non-shaped links) rather than block the save on a presign hiccup.
          console.warn('[manifesto] presigned thumbnail upload failed, falling back to inline', e);
          inlineThumbnail = data.thumbnailFile;
        }
      } else {
        inlineThumbnail = data.thumbnailFile;
      }
    }

    const formData = new FormData();
    formData.append('Title', data.title);
    formData.append('IsPublished', String(data.isPublished));
    if (data.videoUrl) formData.append('VideoUrl', data.videoUrl);
    // Reference the already-uploaded file instead of re-sending its bytes.
    if (videoFileUrl) formData.append('VideoFileUrl', videoFileUrl);
    // CORE-291 — prefer the presigned S3 URL (tiny string); fall back to inline
    // bytes only when presign wasn't available/failed.
    if (thumbnailFileUrl) formData.append('ThumbnailFileUrl', thumbnailFileUrl);
    else if (inlineThumbnail) formData.append('ThumbnailFile', inlineThumbnail);

    // CORE-291 — bound the metadata PUT so a stalled body can never hang the
    // save at 100% again; surfaces as a TimeoutError the caller reports.
    const response = await fetchWithTimeout(`${BASE_URL}/api/master/content/manifesto-video`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }, 90_000);
    return this.handleResponse<ManifestoVideo>(response);
  }

  async deleteManifestoVideo(): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/manifesto-video`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // ============ Agape Video (CORE-185) ============

  async getAgapeVideo(masterProfileId: number): Promise<AgapeVideo | null> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/agape-video`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    if (response.status === 204 || response.status === 404) return null;
    return this.handleResponse<AgapeVideo>(response);
  }

  async saveAgapeVideo(
    data: AgapeVideoRequest,
    onProgress?: (percent: number) => void,
  ): Promise<AgapeVideo> {
    const token = await storage.getToken();
    let videoFileUrl: string | undefined;
    if (data.videoFile) {
      const fileName = (data.videoFile as File).name || 'agape.mp4';
      videoFileUrl = await presignAndPutVideo(data.videoFile, {
        kind: 'agape',
        fileName,
        onProgress: onProgress ? (info) => onProgress(info.percent) : undefined,
      });
    }
    const formData = new FormData();
    formData.append('IsPublished', String(data.isPublished));
    if (videoFileUrl) formData.append('VideoFileUrl', videoFileUrl);
    const response = await fetch(`${BASE_URL}/api/master/content/agape-video`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    return this.handleResponse<AgapeVideo>(response);
  }

  async deleteAgapeVideo(): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/agape-video`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // ============ Master Events ============

  async getMasterEvents(masterProfileId: number, limit = 20, offset = 0, upcoming = true): Promise<{ items: MasterEvent[]; totalCount: number }> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/events?limit=${limit}&offset=${offset}&upcoming=${upcoming}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ items: MasterEvent[]; totalCount: number }>(response);
  }

  async getMasterEvent(masterProfileId: number, eventId: number): Promise<MasterEvent> {
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/events/${eventId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<MasterEvent>(response);
  }

  async createEvent(data: CreateEventRequest): Promise<MasterEvent> {
    // CORE-975 — the cover goes to S3 first; the multipart body then carries
    // only strings, which the shaped RU path passes in a fraction of a second.
    const coverUrl = data.coverImage ? await presignEventCoverOrNull(data.coverImage) : null;

    const formData = new FormData();
    formData.append('Title', data.title);
    formData.append('EventType', data.eventType);
    formData.append('StartDateUtc', data.startDateUtc);
    if (data.subtitle) formData.append('Subtitle', data.subtitle);
    if (data.description) formData.append('Description', data.description);
    if (coverUrl) formData.append('CoverImageUrl', coverUrl);
    else if (data.coverImage) formData.append('CoverImage', data.coverImage as any);
    if (data.endDateUtc) formData.append('EndDateUtc', data.endDateUtc);
    if (data.location) formData.append('Location', data.location);
    if (data.address) formData.append('Address', data.address);
    if (data.isOnline !== undefined) formData.append('IsOnline', String(data.isOnline));
    if (data.price) formData.append('Price', data.price);
    if (data.totalSpots !== undefined) formData.append('TotalSpots', String(data.totalSpots));
    if (data.contactName) formData.append('ContactName', data.contactName);
    if (data.contactEmail) formData.append('ContactEmail', data.contactEmail);
    if (data.contactPhone) formData.append('ContactPhone', data.contactPhone);
    if (data.tags) formData.append('Tags', data.tags);
    // CORE-89 — true tells the BE to keep the event off every public
    // listing (calendar, dots, profile list, friends feed). Default false
    // mirrors the existing «Публичное» FE default.
    if (data.isPrivate !== undefined) formData.append('IsPrivate', String(data.isPrivate));
    // CORE-227 — master's personal feed invitation + opt-out of the feed
    // announcement post. BE skips PostMasterEventMessage + the feed post
    // when PublishAnnouncement is false.
    if (data.feedMessage) formData.append('FeedMessage', data.feedMessage);
    if (data.publishAnnouncement !== undefined) formData.append('PublishAnnouncement', String(data.publishAnnouncement));

    // CORE-431 — a plain fetch with no timeout never settled on a stuck RU/slow
    // connection, so OfflineEventModal's `finally` never ran and the «Опубликовать»
    // button stayed disabled forever («событие не отправляется»). Same fix as
    // createPost: fetchWithTimeout aborts with a friendly error so the modal
    // clears + surfaces it.
    const fetcher = async () => {
      const requestToken = await storage.getToken();
      return fetchWithTimeout(`${BASE_URL}/api/master/content/events`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${requestToken}` },
        body: formData,
      });
    };
    let response = await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<MasterEvent>(response);
  }

  async updateEvent(id: number, data: UpdateEventRequest): Promise<MasterEvent> {
    // CORE-975 — same presigned cover path as createEvent above.
    const coverUrl = data.coverImage ? await presignEventCoverOrNull(data.coverImage) : null;

    const formData = new FormData();
    if (data.title) formData.append('Title', data.title);
    if (data.eventType) formData.append('EventType', data.eventType);
    if (data.startDateUtc) formData.append('StartDateUtc', data.startDateUtc);
    // Empty strings are meaningful on edit: they clear previously saved copy.
    if (data.subtitle !== undefined) formData.append('Subtitle', data.subtitle);
    if (data.description !== undefined) formData.append('Description', data.description);
    if (coverUrl) formData.append('CoverImageUrl', coverUrl);
    else if (data.coverImage) formData.append('CoverImage', data.coverImage as any);
    if (data.removeCoverImage) formData.append('RemoveCoverImage', 'true');
    if (data.endDateUtc) formData.append('EndDateUtc', data.endDateUtc);
    if (data.location) formData.append('Location', data.location);
    if (data.address) formData.append('Address', data.address);
    if (data.isOnline !== undefined) formData.append('IsOnline', String(data.isOnline));
    if (data.price) formData.append('Price', data.price);
    if (data.totalSpots !== undefined) formData.append('TotalSpots', String(data.totalSpots));
    if (data.spotsLeft !== undefined) formData.append('SpotsLeft', String(data.spotsLeft));
    if (data.contactName) formData.append('ContactName', data.contactName);
    if (data.contactEmail) formData.append('ContactEmail', data.contactEmail);
    if (data.contactPhone) formData.append('ContactPhone', data.contactPhone);
    if (data.tags) formData.append('Tags', data.tags);
    // CORE-89 — omitted/undefined leaves visibility alone; explicit
    // boolean flips it. Matches BE's null-means-keep-existing semantics
    // on AdminUpdateEventJsonRequest / UpdateEventRequest.
    if (data.isPrivate !== undefined) formData.append('IsPrivate', String(data.isPrivate));

    // CORE-431 — timeout so a stuck upload can't hang the edit «Опубликовать»
    // button forever (same rationale as createEvent above).
    const fetcher = async () => {
      const requestToken = await storage.getToken();
      return fetchWithTimeout(`${BASE_URL}/api/master/content/events/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${requestToken}` },
        body: formData,
      });
    };
    let response = await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<MasterEvent>(response);
  }

  async deleteEvent(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/content/events/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // ─── Platform Events (unified master + agape) ───

  /**
   * LIST endpoint — `source` is the collection filter, NOT the per-event
   * discriminator. Accepts `'all' | 'masters' | 'agape'`; passing the
   * singular `'master'` (which is what `UnifiedEvent['source']` would
   * suggest) silently returns nothing because BE branches on
   * `source == "all" || source == "masters"`. See EventListSource jsdoc.
   */
  async getEvents(params?: {
    source?: EventListSource; tradition?: string; tags?: string; eventType?: string;
    upcoming?: boolean; from?: string; to?: string; isOnline?: boolean;
    limit?: number; offset?: number;
  }): Promise<{ items: UnifiedEvent[]; totalCount: number }> {
    const q = new URLSearchParams();
    if (params?.source) q.set('source', params.source);
    if (params?.tradition) q.set('tradition', params.tradition);
    if (params?.tags) q.set('tags', params.tags);
    if (params?.eventType) q.set('eventType', params.eventType);
    if (params?.upcoming !== undefined) q.set('upcoming', String(params.upcoming));
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    if (params?.isOnline !== undefined) q.set('isOnline', String(params.isOnline));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const response = await fetch(`${BASE_URL}/api/events?${q.toString()}`, {
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ items: UnifiedEvent[]; totalCount: number }>(response);
  }

  /** Same LIST-filter vocabulary as `getEvents` — see EventListSource jsdoc. */
  async getEventTags(params?: { source?: EventListSource; tradition?: string }): Promise<{ tags: string[] }> {
    const q = new URLSearchParams();
    if (params?.source) q.set('source', params.source);
    if (params?.tradition) q.set('tradition', params.tradition);
    const response = await fetch(`${BASE_URL}/api/events/tags?${q.toString()}`);
    return this.handleResponse<{ tags: string[] }>(response);
  }

  /** Same LIST-filter vocabulary as `getEvents` — see EventListSource jsdoc. */
  async getPlatformCalendarEvents(params?: {
    month?: number; year?: number; source?: EventListSource; tradition?: string; tags?: string;
  }): Promise<{ events: CalendarEventDto[] }> {
    const q = new URLSearchParams();
    if (params?.month) q.set('month', String(params.month));
    if (params?.year) q.set('year', String(params.year));
    if (params?.source) q.set('source', params.source);
    if (params?.tradition) q.set('tradition', params.tradition);
    if (params?.tags) q.set('tags', params.tags);
    // CORE-720 — identity matters here: the BE gates «только для подписчиков»
    // lives and rooms on the caller (LiveVisibilityFilters.WhereVisibleTo,
    // CORE-676). This call went out with NO headers at all, so every calendar
    // surface asked anonymously and a subscriber saw only public sessions —
    // the whole audience model was unreachable from the client. getHeaders
    // attaches the token only when there is one, so anonymous callers are
    // unaffected.
    const response = await fetch(`${BASE_URL}/api/events/calendar?${q.toString()}`, {
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ events: CalendarEventDto[] }>(response);
  }

  /**
   * Per-event routes — `source` here is the SINGULAR discriminator.
   * For the BE controller (`EventsController.cs:141`) the strict
   * vocabulary is `'master' | 'agape'` (UnifiedEventSource), but the
   * FE `SavedEventsContext.feedKindToSource()` also threads `'live' |
   * 'room' | 'offline' | 'practice'` into the same routes for the
   * unified «save» flow — see jsdoc there. Kept loosely typed
   * (`string`) until that vocabulary gets aligned with BE; the LIST
   * endpoint above is the strict path that AMB-250 needed fixing.
   */
  async getEvent(source: string, id: number): Promise<UnifiedEvent> {
    const response = await fetch(`${BASE_URL}/api/events/${source}/${id}`, {
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<UnifiedEvent>(response);
  }

  getEventIcsUrl(source: string, id: number): string {
    return `${BASE_URL}/api/events/${source}/${id}/calendar.ics`;
  }

  async registerForEvent(source: string, id: number, data?: EventRegistrationRequest): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/events/${source}/${id}/register`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: data ? JSON.stringify(data) : undefined,
    });
    await this.handleResponse<void>(response);
  }

  async unregisterFromEvent(source: string, id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/events/${source}/${id}/register`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async saveEvent(source: string, id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/events/${source}/${id}/save`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async unsaveEvent(source: string, id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/events/${source}/${id}/save`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  /**
   * SCRUM-42 — everything the caller has ever saved (bookmarked
   * lives/rooms/events + hearted practices; legacy favorites backfilled).
   * `source` filters server-side: 'live' | 'room' | 'practice' | 'master'
   * | 'offline' | 'agape'. Cursor-paginated, savedAtUtc DESC.
   */
  async getMySaved(params?: { limit?: number; cursor?: string; source?: string }): Promise<SavedListResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.append('limit', String(params.limit));
    if (params?.cursor) query.append('cursor', params.cursor);
    if (params?.source) query.append('source', params.source);
    const qs = query.toString();
    return this.authedRequest<SavedListResponse>(`${BASE_URL}/api/user/me/saved${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async sendEventMessage(source: string, id: number, data: SendEventMessageRequest): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/events/${source}/${id}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await this.handleResponse<void>(response);
  }

  async getUserCalendar(params?: { month?: string }): Promise<CalendarItem[]> {
    const q = new URLSearchParams();
    if (params?.month) q.set('month', params.month);
    const qs = q.toString();
    const response = await fetch(`${BASE_URL}/api/user/my-calendar${qs ? `?${qs}` : ''}`, {
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<CalendarItem[]>(response);
  }

  async removeLiveFromCalendar(liveId: number): Promise<SubscriptionState> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/calendar`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<SubscriptionState>(response);
  }

  async getCalendarBadge(): Promise<CalendarBadgeResponse> {
    const response = await fetch(`${BASE_URL}/api/user/my-calendar/badge`, {
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<CalendarBadgeResponse>(response);
  }

  async markCalendarViewed(): Promise<CalendarBadgeResponse> {
    const response = await fetch(`${BASE_URL}/api/user/my-calendar/mark-viewed`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<CalendarBadgeResponse>(response);
  }

  async getUserRegistrations(params?: { limit?: number }): Promise<{ items: any[] }> {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    const response = await fetch(`${BASE_URL}/api/user/registrations${qs ? `?${qs}` : ''}`, {
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ items: any[] }>(response);
  }

  // ============ Activity feed ("Что у моих") ============

  /**
   * Combined activity feed of accounts the caller follows. Six event
   * kinds (live_scheduled, live_recorded, room_scheduled, room_recorded,
   * event_saved, practice_uploaded). Cursor pagination; `nextCursor` is
   * null when there's nothing more.
   *
   * Pass `kinds` as a comma-separated subset of `FeedItemKind` to filter.
   */
  async getFeed(params?: { limit?: number; cursor?: string; kinds?: FeedItemKind[] | string }): Promise<FeedResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.cursor) qs.append('cursor', params.cursor);
    if (params?.kinds) {
      const csv = Array.isArray(params.kinds) ? params.kinds.join(',') : params.kinds;
      if (csv) qs.append('kinds', csv);
    }
    const tail = qs.toString();
    const response = await fetch(`${BASE_URL}/api/user/me/feed${tail ? `?${tail}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<FeedResponse>(response);
  }

  // ============ Direct Messages (DM) ============

  async getDmConversations(): Promise<DmConversationUserView[]> {
    const response = await fetch(`${BASE_URL}/api/dm/conversations`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<DmConversationUserView[]>(response);
  }

  /**
   * Unified conversation list — Phase 2. Returns ALL conversations the
   * caller participates in (user↔user and user↔master) with an
   * `otherIsMaster` flag. New code should use this; legacy
   * `getDmConversations` filters to master-only.
   */
  async getAllDmConversations(): Promise<DmConversationView[]> {
    const response = await fetch(`${BASE_URL}/api/dm/all-conversations`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<DmConversationView[]>(response);
  }

  /**
   * Start a conversation. Pass either `masterProfileId` (legacy master
   * flow) or `otherUserId` (Phase 2 — works for both user↔user and
   * user↔master).
   */
  async createDmConversation(target: { masterProfileId: number } | { otherUserId: string }): Promise<CreateConversationResponse> {
    const response = await fetch(`${BASE_URL}/api/dm/conversations`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(target),
    });
    return this.handleResponse<CreateConversationResponse>(response);
  }

  async getDmMessages(conversationId: number, params?: { limit?: number; before?: number }): Promise<DmMessagesResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.before) query.append('before', String(params.before));
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/dm/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<DmMessagesResponse>(response);
  }

  async sendDmMessage(conversationId: number, content: string): Promise<{ id: number; senderRole: string; content: string; sentAt: string }> {
    const response = await fetch(`${BASE_URL}/api/dm/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ content }),
    });
    return this.handleResponse<{ id: number; senderRole: string; content: string; sentAt: string }>(response);
  }

  async markDmConversationRead(conversationId: number): Promise<void> {
    await fetch(`${BASE_URL}/api/dm/conversations/${conversationId}/read`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
    });
  }

  async getMasterDmConversations(): Promise<DmConversationMasterView[]> {
    const response = await fetch(`${BASE_URL}/api/master/dm/conversations`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<DmConversationMasterView[]>(response);
  }

  async getOnlineMasters(): Promise<MastersOnlineResponse> {
    const response = await fetch(`${BASE_URL}/api/masters/online`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<MastersOnlineResponse>(response);
  }

  // ============ Video Translation ============

  async getSupportedLanguages(): Promise<SupportedLanguage[]> {
    const response = await fetch(`${BASE_URL}/api/languages`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<SupportedLanguage[]>(response);
  }

  async getVideoTranslations(videoId: number): Promise<TranslationInfo[]> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${videoId}/translations`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<TranslationInfo[]>(response);
  }

  async getVideoTranslation(videoId: number, language: string): Promise<TranslationResponse> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${videoId}/translations/${language}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<TranslationResponse>(response);
  }

  async requestVideoTranslation(videoId: number, targetLanguage: string): Promise<TranslationResponse> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${videoId}/translate`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ targetLanguage }),
    });
    return this.handleResponse<TranslationResponse>(response);
  }

  // ============ Live Translation (real-time) ============

  async getLiveTranslations(liveId: number): Promise<LiveTranslationInfo[]> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/translations`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<LiveTranslationInfo[]>(response);
  }

  async getLiveTranslation(liveId: number, language: string): Promise<LiveTranslationResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/translations/${language}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<LiveTranslationResponse>(response);
  }

  async requestLiveTranslation(liveId: number, language: string): Promise<LiveTranslationResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/translations`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ targetLanguage: language }),
    });
    return this.handleResponse<LiveTranslationResponse>(response);
  }

  // ============ Subtitles ============

  async getVideoSubtitles(videoId: number): Promise<SubtitleResponse[]> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${videoId}/subtitles`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<SubtitleResponse[]>(response);
  }

  async getLiveSubtitles(liveId: number): Promise<SubtitleResponse[]> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/subtitles`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<SubtitleResponse[]>(response);
  }

  async requestVideoSubtitle(videoId: number, language: string): Promise<SubtitleResponse> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${videoId}/subtitles`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ language }),
    });
    return this.handleResponse<SubtitleResponse>(response);
  }

  async requestLiveSubtitle(liveId: number, language: string): Promise<SubtitleResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/subtitles`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ language }),
    });
    return this.handleResponse<SubtitleResponse>(response);
  }

  // ─── Anonymous-guest identity (SCRUM-31/36) ───

  /**
   * Mint or echo the caller's stable anon-{uuid} id. Idempotent: a 2nd call
   * from the same browser (with the cached id sent back in X-Anon-User-Id)
   * receives `minted: false` + the original id.
   *
   * CORE-1197 (возврат QA 08.09.2026) — личность берётся из ТЕЛА ответа, а не
   * из заголовка.
   *
   * Заголовок `X-Anon-User-Id` сервер шлёт, но в вебе его нельзя прочитать:
   * ответ приходит с другого домена (`app.*` → `api.*`), а
   * `Access-Control-Expose-Headers` в нём нет, и браузер по правилам CORS
   * отдаёт скрипту только `content-type`. Замер на стенде 10.09.2026:
   * `res.headers.get('X-Anon-User-Id')` возвращает `null`, хотя в curl
   * заголовок на месте. То есть на вебе не «запись не успела» — записывать было
   * НЕЧЕГО, и так с самого начала (на нативе CORS нет, и там всё работало —
   * поэтому дефект и прожил так долго).
   *
   * Цена ровно та, ради которой id и заводился: без сохранённой личности каждый
   * следующий запрос гостя чеканит новую, а `spectatorId` выводится из неё. Тот
   * же гость объявляется хабу под одной личностью, слово ведущий выдаёт ей, а
   * токен просит уже другая — сервер отвечает `FloorNotGranted` и subscriber'ом
   * (33 отказа на препроде 08.09, 33 разных spectatorId — разбор бэкенда).
   *
   * Тело же читается всегда: тот же id лежит в `userId` (сверено с заголовком в
   * одном ответе). Заголовок оставлен запасным путём — на нативе он есть, и
   * терять его незачем.
   *
   * Запись ЖДЁМ (`await`) до возврата: вызывающий обычно сразу идёт в
   * `/spectate`, и незавершённая запись означала бы ту же смену личности.
   */
  async getAnonIdentity(): Promise<{ userId: string; displayName: string; minted: boolean }> {
    const response = await fetch(`${BASE_URL}/api/anon/identity`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    if (!response.ok) {
      throw new Error(`anon/identity failed: ${response.status}`);
    }
    const parsed = await this.handleResponse<{ userId: string; displayName: string; minted: boolean }>(response);
    const resolvedId = parsed?.userId || response.headers.get('X-Anon-User-Id');
    if (resolvedId) {
      try { await storage.setAnonUserId(resolvedId); } catch { /* non-fatal */ }
    }
    return parsed;
  }

  // ─── Master circles ───

  async joinMasterCircle(masterProfileId: number): Promise<{ joined: boolean; channelId: string }> {
    const response = await fetch(`${BASE_URL}/api/master-circles/${masterProfileId}/join`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ joined: boolean; channelId: string }>(response);
  }

  // Members of a circle with their role ("owner" | "admin" | "member").
  // `circleId` is the Circle GUID (the Stream channel id is `circle-{circleId}`).
  async getCircleMembers(circleId: string): Promise<CircleMemberDto[]> {
    const response = await fetch(`${BASE_URL}/api/circles/${circleId}/members`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<CircleMemberDto[]>(response);
  }

  // Owner/admin removes a member from their circle (AMB-60). 403 if the caller
  // isn't owner/admin, 400 if the target is the owner, 404 if not a member.
  async kickCircleMember(circleId: string, userId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/circles/${circleId}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // Owner/admin mutes or unmutes a circle member (AMB-61).
  // isMuted=true + untilUtc → muted until then; isMuted=true without untilUtc →
  // muted indefinitely; isMuted=false → unmute. 403 if caller isn't owner/admin,
  // 400 if the target is the owner or untilUtc is in the past, 404 if not a member.
  async muteCircleMember(
    circleId: string,
    userId: string,
    opts: { isMuted: boolean; untilUtc?: string | null; reason?: string },
  ): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/circles/${circleId}/members/${encodeURIComponent(userId)}/mute`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({
        isMuted: opts.isMuted,
        untilUtc: opts.untilUtc ?? null,
        reason: opts.reason ?? null,
      }),
    });
    await this.handleResponse<void>(response);
  }

  // AMB-62 — circle OWNER permanently bans a member (admins cannot). 403 if the
  // caller isn't the owner, 400 if the target is the owner, 404 if not a member.
  async banCircleMember(circleId: string, userId: string, opts?: { reason?: string }): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/circles/${circleId}/members/${encodeURIComponent(userId)}/ban`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ reason: opts?.reason ?? null }),
    });
    await this.handleResponse<void>(response);
  }

  // AMB-62 — owner lifts a ban so the user can join again.
  async unbanCircleMember(circleId: string, userId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/circles/${circleId}/bans/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  // AMB-62 — owner-only ban list for a circle.
  async getCircleBans(circleId: string): Promise<CircleBanDto[]> {
    const response = await fetch(`${BASE_URL}/api/circles/${circleId}/bans`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<CircleBanDto[]>(response);
  }

  /**
   * CORE-371 — ensure (idempotent) the 1:1 AI companion channel exists in the
   * ChatApi backend and return its deterministic key. Called before opening a
   * companion over the ChatApi SignalR hub when `ai_chat_via_chat_api` is on.
   */
  async ensureAiCompanionChannel(companionId: string): Promise<{ channelKey: string }> {
    // fetchWithTimeout, not bare fetch: this call gates the whole chat. Nothing
    // downstream can recover from it never settling — `useChatThreadSource`
    // retries a REJECTION, and a promise that simply hangs leaves the thread on
    // its spinner forever, with no error and no telemetry.
    const response = await fetchWithTimeout(
      `${BASE_URL}/api/chat/ai-companions/${encodeURIComponent(companionId)}/channel`,
      { method: 'POST', headers: await this.getHeaders(true) },
      CHANNEL_PROVISION_TIMEOUT_MS,
    );
    return this.handleResponse<{ channelKey: string }>(response);
  }

  /**
   * CORE-371 — ensure (idempotent) the caller's Wisdom Council ChatApi channel
   * exists (with all active companion bots as members) and return its key.
   */
  async ensureWisdomCouncilChannel(): Promise<{ channelKey: string }> {
    // See ensureAiCompanionChannel — same gate, same reason for the deadline.
    const response = await fetchWithTimeout(`${BASE_URL}/api/chat/wisdom-council/channel`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    }, CHANNEL_PROVISION_TIMEOUT_MS);
    return this.handleResponse<{ channelKey: string }>(response);
  }

  /**
   * CORE-356 (B5/T5) — ensure (idempotent) the caller's 1:1 DM ChatApi channel
   * with `otherUserId` (the friend's app user id) exists and return its
   * deterministic key (`dm-{min}-{max}`). Called before opening a friend DM over
   * the ChatApi hub when `dm_chat_via_chat_api` is on.
   */
  async ensureDmChannel(otherUserId: string, streamChannelId?: string): Promise<{ channelKey: string }> {
    // `streamChannelId` (the current GetStream DM channel) lets the backend
    // backfill recent history into the dm-* channel on first open (CORE-356).
    const qs = streamChannelId ? `?streamChannelId=${encodeURIComponent(streamChannelId)}` : '';
    const response = await fetch(
      `${BASE_URL}/api/chat/dm/${encodeURIComponent(otherUserId)}/channel${qs}`,
      { method: 'POST', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<{ channelKey: string }>(response);
  }

  /**
   * CORE-408 — the caller's DM conversation list (peer + last message + unread)
   * from Agape.ChatApi, proxied through the main API so it rides api.agapella.io
   * + its api-ru RU fallback. Lets the FE build the DM list without GetStream
   * (toward retiring Stream). Newest-first; empty when chat isn't configured.
   */
  /**
   * DM-беседы для списка «Сообщения».
   *
   * Спрашиваем чат-сервис напрямую, а основной API оставляем запасным путём.
   * Причина: счётчик непрочитанных в строке приходил нулевым, тогда как бейдж
   * на вкладке — той же беседы, но из `/api/chat/channels/unread-summary` на
   * CHAT_HUB_BASE_URL — считал верно. Превью и время при этом были свежие, то
   * есть ответ основного API приходил, просто без unread. Оба числа должны
   * браться с одного хоста, иначе они расходятся у пользователя на глазах.
   */
  async getChatApiDmConversations(): Promise<DmConversationSummary[]> {
    if (CHAT_HUB_BASE_URL && CHAT_HUB_BASE_URL !== BASE_URL) {
      try {
        // Путь именно `/api/chat/channels/dm-conversations` — так этот список
        // называется НА ХОСТЕ ЧАТА (ChannelsController). `/api/chat/dm/
        // conversations` — имя того же списка у прокси основного API, и здесь
        // оно попадало в `DmController` с `[HttpDelete("{conversation}")]`, где
        // «conversations» садилось в параметр маршрута. Отсюда 405 с
        // `Allow: DELETE` в консоли на каждый опрос — и, что хуже, прямой путь
        // не срабатывал НИ РАЗУ: список всегда приезжал запасным маршрутом, то
        // есть расхождение счётчиков, ради которого всё это писалось, никуда не
        // девалось. Соседний `channels/unread-summary` живёт на этом же хосте —
        // теперь оба числа действительно с одного источника.
        const direct = await fetch(`${CHAT_HUB_BASE_URL}/api/chat/channels/dm-conversations`, {
          method: 'GET',
          headers: await this.getHeaders(true),
        });
        if (direct.ok) return await direct.json();
        // 404/501 — старый ChatApi без этого маршрута; идём прежним путём.
      } catch {
        // Сеть/CORS/RU-эдж — не повод остаться без списка вовсе.
      }
    }
    const response = await fetch(`${BASE_URL}/api/chat/dm/conversations`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<DmConversationSummary[]>(response);
  }

  /**
   * CORE-356 (B3/T6) — upload a chat attachment (image/video/voice/file) to
   * storage and get back a descriptor the ChatApi composer stores in a message's
   * attachmentsJson. Multipart (field `file`); the browser sets the boundary, so
   * only the Authorization header is passed (no Content-Type).
   */
  /**
   * CORE-806 (QA FAIL 09.09) — `timeoutMs` для тех, кому нужен отказ, а не
   * ожидание.
   *
   * Голый `fetch` без потолка на мёртвой или «зашторенной» сети не отвечает и
   * не падает — ровно то, о чём написано у `getChatContacts` ниже. Для
   * голосового это худший исход: человек отпустил кнопку, запись ушла в
   * никуда, и сказать ему нечего, потому что отказ так и не наступил.
   *
   * Параметр необязательный намеренно: загрузка видео на медленной мобильной
   * сети идёт долго и законно, и общий потолок оборвал бы её на середине.
   * Ставит его тот, кто знает размер своего вложения.
   */
  async uploadChatAttachment(
    file: Blob,
    fileName?: string,
    timeoutMs?: number,
  ): Promise<ChatAttachmentDescriptor> {
    const token = await this.getAuthToken();
    const form = new FormData();
    form.append('file', file as any, fileName || (file as any)?.name || 'file');
    const init: RequestInit = {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form as any,
    };
    const url = `${BASE_URL}/api/chat/attachments`;
    const response = timeoutMs
      ? await fetchWithTimeout(url, init, timeoutMs)
      : await fetch(url, init);
    return this.handleResponse<ChatAttachmentDescriptor>(response);
  }

  /**
   * CORE-1682 — правила вложений для канала чата мастера (CORE-1703):
   * лимиты, форматы и может ли текущий пользователь прикреплять. Токен не
   * обязателен. `null` — политику прочитать не удалось: вызывающий берёт
   * запасную, сервер всё равно проверит сам.
   */
  async getChatAttachmentPolicy(channelKey: string): Promise<ChatAttachmentPolicy | null> {
    if (!channelKey) return null;
    try {
      const response = await fetchWithTimeout(
        `${BASE_URL}/api/chat/attachments/policy?channelKey=${encodeURIComponent(channelKey)}`,
        { method: 'GET', headers: await this.getHeaders(true) },
        STREAM_TOKEN_TIMEOUT_MS,
      );
      if (!response.ok) return null;
      return parseAttachmentPolicy(await response.json());
    } catch {
      return null;
    }
  }

  /**
   * CORE-1682 — загрузка вложения в канал чата мастера. С `channelKey` сервер
   * проверяет формат по содержимому и размер по политике, отвечает `id` и
   * исходным именем без ссылки: файл никому не выдаётся, пока не отправлен
   * (`attachmentIds` в `SendMessageV2`). Отказ — ProblemDetails с `code`
   * (`unsupported_format`, `too_large` + `maxBytes`, …); `handleResponse`
   * бросает его тело со `status`, слова подбирает `uploadRefusalText`.
   */
  async uploadCommunityChatAttachment(
    channelKey: string,
    file: Blob,
    fileName?: string,
    timeoutMs?: number,
  ): Promise<CommunityAttachmentUpload> {
    const token = await this.getAuthToken();
    const form = new FormData();
    form.append('file', file as any, fileName || (file as any)?.name || 'file');
    const init: RequestInit = {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form as any,
    };
    const url = `${BASE_URL}/api/chat/attachments?channelKey=${encodeURIComponent(channelKey)}`;
    const response = timeoutMs
      ? await fetchWithTimeout(url, init, timeoutMs)
      : await fetch(url, init);
    const d = await this.handleResponse<Record<string, any>>(response);
    return {
      id: String(d?.id ?? d?.Id ?? ''),
      type: (d?.type ?? d?.Type) === 'image' ? 'image' : 'file',
      fileName: String(d?.fileName ?? d?.FileName ?? fileName ?? 'file'),
      mimeType: String(d?.mimeType ?? d?.MimeType ?? (file as any)?.type ?? ''),
      fileSizeBytes: Number(d?.fileSizeBytes ?? d?.FileSizeBytes ?? (file as any)?.size ?? 0),
    };
  }

  /**
   * CORE-1684 — свободен ли ник (`GET /api/user/nickname/check`). `reason`
   * сервера: `invalid`, `reserved`, `taken`; на отказе сети — `unknown`, чтобы
   * форма не врала ни «свободен», ни «занят».
   */
  async checkNickname(nickname: string): Promise<{ available: boolean; reason: string | null }> {
    try {
      const response = await fetchWithTimeout(
        `${BASE_URL}/api/user/nickname/check?nickname=${encodeURIComponent(nickname)}`,
        { method: 'GET', headers: await this.getHeaders(true) },
        STREAM_TOKEN_TIMEOUT_MS,
      );
      if (!response.ok) return { available: false, reason: 'unknown' };
      const d = await response.json();
      const reason = d?.reason ?? d?.Reason ?? null;
      return { available: !!(d?.available ?? d?.Available), reason: reason == null ? null : String(reason) };
    } catch {
      return { available: false, reason: 'unknown' };
    }
  }

  /**
   * CORE-1684 — кого можно упомянуть: поиск по всей платформе по нику и имени
   * (`GET /api/users/mention-search`). Закрытые профили — только по точному
   * нику; себя, заблокированных и удаляемых сервер не отдаёт; сначала точный
   * ник, потом участники сообщества мастера. Пусто на любой неудаче — подсказки
   * из участников канала остаются.
   */
  async searchMentionCandidates(
    query: string,
    masterProfileId?: number | null,
    limit = 8,
  ): Promise<MentionCandidate[]> {
    const q = query.trim();
    if (!q) return [];
    try {
      const qs = new URLSearchParams({ q, limit: String(limit) });
      if (masterProfileId != null && masterProfileId > 0) qs.set('masterProfileId', String(masterProfileId));
      const response = await fetchWithTimeout(
        `${BASE_URL}/api/users/mention-search?${qs.toString()}`,
        { method: 'GET', headers: await this.getHeaders(true) },
        STREAM_TOKEN_TIMEOUT_MS,
      );
      if (!response.ok) return [];
      const data = await response.json();
      const rows: any[] = Array.isArray(data) ? data : (data?.items ?? data?.Items ?? []);
      return rows
        .map((r): MentionCandidate => ({
          userId: String(r?.userId ?? r?.UserId ?? ''),
          name: String(r?.displayName ?? r?.DisplayName ?? '').trim(),
          nickname: (r?.nickname ?? r?.Nickname) ? String(r.nickname ?? r.Nickname) : undefined,
          avatar: getFileUrl(r?.avatarThumbUrl ?? r?.AvatarThumbUrl ?? r?.avatarUrl ?? r?.AvatarUrl) ?? undefined,
          isHost: !!(r?.isMaster ?? r?.IsMaster),
        }))
        .filter((c) => c.userId && (c.name || c.nickname))
        .map((c) => (c.name ? c : { ...c, name: `@${c.nickname}` }));
    } catch {
      return [];
    }
  }

  async getChatContacts(folder?: string): Promise<ChatContactsResponse> {
    const params = folder ? `?folder=${encodeURIComponent(folder)}` : '';
    // CORE-119 — bound like the Stream token (CORE-400). This runs inside the
    // chat-connect effect and `isConnecting` only clears once it settles; a bare
    // fetch to a blocked/shaped RU origin hangs forever (never errors, so the
    // api-ru fallback never engages) → eternal «Connecting…» loader with no
    // network error. The timeout aborts fast so api-ru kicks in / the caller's
    // .catch fires and the UI proceeds.
    const response = await fetchWithTimeout(`${BASE_URL}/api/chat/contacts${params}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    }, STREAM_TOKEN_TIMEOUT_MS);
    return this.handleResponse<ChatContactsResponse>(response);
  }

  /**
   * CORE-611 — seed global online status for friends / DM peers off Agape.ChatApi
   * instead of Stream's `queryUsers({presence:true})`. `userIds` are BARE platform
   * guids (strip the `user_` stream prefix first); the endpoint mirrors that shape
   * back in `onlineUserIds`. Best-effort: any failure (RU-blocked, chat-api down,
   * non-2xx) resolves to `[]` so a presence hiccup shows an empty dot set, never
   * throws into the chat UI. Live updates ride the hub's `UserPresenceChanged`
   * event (see `presenceHub.ts`); this is only the initial snapshot.
   */
  async getChatPresence(userIds: string[]): Promise<string[]> {
    const ids = Array.from(new Set(userIds.filter(Boolean)));
    if (ids.length === 0) return [];
    try {
      const response = await fetch(
        `${BASE_URL}/api/chat/presence?userIds=${encodeURIComponent(ids.join(','))}`,
        { method: 'GET', headers: await this.getHeaders(true) },
      );
      if (!response.ok) return [];
      const data = await response.json();
      return (data?.onlineUserIds ?? data?.OnlineUserIds ?? []) as string[];
    } catch {
      return [];
    }
  }

  /** Online user-ids (bare guids) currently watching a chat channel, from
   *  Agape.ChatApi's Redis presence. Key-based, so circle / wisdom-council
   *  «About» presence can come off Stream's `channel.state.members[].online`
   *  once the surface rides ChatApi (CORE-612). Hosted on the chat service, not
   *  the main API. Best-effort: [] on any failure (same shape as getChatPresence). */
  async getChatChannelOnline(channelKey: string): Promise<string[]> {
    if (!channelKey) return [];
    try {
      const response = await fetch(
        `${CHAT_HUB_BASE_URL}/api/chat/channels/${encodeURIComponent(channelKey)}/online`,
        { method: 'GET', headers: await this.getHeaders(true) },
      );
      if (!response.ok) return [];
      const data = await response.json();
      return (data?.userIds ?? data?.UserIds ?? []) as string[];
    } catch {
      return [];
    }
  }

  /**
   * CORE-1653 — участники канала чата (`GET api/chat/channels/{key}/members`,
   * живёт на чат-сервисе). Сервер отдаёт страницами до 100 и без поиска, так что
   * поиск по имени и подсказки упоминаний фильтруют уже загруженное. Читать
   * может только тот, кому канал открыт; на отказе — пустой список: чат без
   * списка участников остаётся чатом.
   */
  async getChatChannelMembers(
    channelKey: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<ChatChannelMember[]> {
    if (!channelKey) return [];
    try {
      const qs = `limit=${opts.limit ?? 100}&offset=${opts.offset ?? 0}`;
      const response = await fetch(
        `${CHAT_HUB_BASE_URL}/api/chat/channels/${encodeURIComponent(channelKey)}/members?${qs}`,
        { method: 'GET', headers: await this.getHeaders(true) },
      );
      if (!response.ok) return [];
      const data = await response.json();
      const rows: any[] = Array.isArray(data) ? data : (data?.items ?? data?.members ?? []);
      return rows
        .map((r) => ({
          userId: String(r?.userId ?? r?.UserId ?? ''),
          name: String(r?.name ?? r?.Name ?? ''),
          avatarUrl: r?.avatarUrl ?? r?.AvatarUrl ?? null,
          avatarThumbUrl: r?.avatarThumbUrl ?? r?.AvatarThumbUrl ?? null,
          role: String(r?.role ?? r?.Role ?? 'member'),
          isBanned: !!(r?.isBanned ?? r?.IsBanned),
        }))
        .filter((m) => !!m.userId);
    } catch {
      return [];
    }
  }

  /**
   * CORE-1683 — страница истории канала вокруг одного сообщения
   * (`GET /api/chat/channels/{key}/messages?around=`, чат-сервис). Нужна, когда
   * цитата или уведомление ведут к сообщению старше тех 50, что приходят из
   * `WatchChannel`. Для сообщения внутри обсуждения сервер отдаёт только это
   * обсуждение. Ответ — сырые `MessageDto`: их кладёт к себе `ChatApiChannel`,
   * а маппит общий маппер. Нет сообщения (404, скрыто) или связи — пустой
   * список: лента остаётся как была, просто без перехода.
   */
  async getChatChannelMessagesAround(
    channelKey: string,
    messageId: string,
    limit = 50,
  ): Promise<Record<string, any>[]> {
    if (!channelKey || !messageId) return [];
    return (await this.getChatChannelMessagesPage(channelKey, { around: messageId, limit })).items;
  }

  /**
   * CORE-1683 (QA 16.10) — страница истории канала с курсорами
   * (`GET /api/chat/channels/{key}/messages`, чат-сервис, CORE-1702). Не
   * больше одного из `before` / `after` / `around`; `threadRootId` — листать
   * одно обсуждение (корень и ответы). Пустая страница при любой ошибке:
   * лента остаётся как была. CORE-620 (возврат QA 30.10) — с пометкой
   * `failed`: иначе листание читало сбой как конец истории.
   */
  async getChatChannelMessagesPage(
    channelKey: string,
    query: { before?: string | number; after?: string | number; around?: string | number; threadRootId?: string | number; limit?: number },
  ): Promise<ChatMessagesPage> {
    const empty: ChatMessagesPage = { items: [], nextCursor: null, hasMore: false, newerCursor: null, hasNewer: false, threadRootId: null };
    if (!channelKey) return empty;
    const failed: ChatMessagesPage = { ...empty, failed: true };
    try {
      const params = new URLSearchParams();
      params.set('limit', String(query.limit ?? 50));
      if (query.before != null) params.set('before', String(query.before));
      if (query.after != null) params.set('after', String(query.after));
      if (query.around != null) params.set('around', String(query.around));
      if (query.threadRootId != null) params.set('threadRootId', String(query.threadRootId));
      const response = await fetch(
        `${CHAT_HUB_BASE_URL}/api/chat/channels/${encodeURIComponent(channelKey)}/messages?${params}`,
        { method: 'GET', headers: await this.getHeaders(true) },
      );
      if (!response.ok) return failed;
      const data = await response.json();
      const rows = Array.isArray(data) ? data : (data?.items ?? data?.Items ?? []);
      const str = (v: unknown) => (v == null || v === '' ? null : String(v));
      return {
        items: Array.isArray(rows) ? rows : [],
        nextCursor: str(data?.nextCursor ?? data?.NextCursor),
        hasMore: !!(data?.hasMore ?? data?.HasMore),
        newerCursor: str(data?.newerCursor ?? data?.NewerCursor),
        hasNewer: !!(data?.hasNewer ?? data?.HasNewer),
        threadRootId: str(data?.threadRootId ?? data?.ThreadRootId),
      };
    } catch {
      return failed;
    }
  }

  /** CORE-617 — aggregate unread across all the caller's channels (DM + AI +
   *  circle), categorized for the header + bottom-tab badges. Sources the badges
   *  off Agape.ChatApi so the app can drop the GetStream-client aggregation
   *  (`hooks/useUnreadCounts`) and stop connecting to Stream. Hosted on the chat
   *  service (not the main API). Best-effort: all-zero on any failure, so a chat
   *  hiccup blanks the badge rather than throwing. */
  async getChatUnreadSummary(): Promise<{
    totalUnread: number;
    unreadChannels: number;
    friendsUnread: number;
    conversationsUnread: number;
    aiUnread: number;
  }> {
    const empty = {
      totalUnread: 0, unreadChannels: 0, friendsUnread: 0, conversationsUnread: 0, aiUnread: 0,
    };
    try {
      const response = await fetch(
        `${CHAT_HUB_BASE_URL}/api/chat/channels/unread-summary`,
        { method: 'GET', headers: await this.getHeaders(true) },
      );
      if (!response.ok) return empty;
      const d = await response.json();
      return {
        totalUnread: d?.totalUnread ?? d?.TotalUnread ?? 0,
        unreadChannels: d?.unreadChannels ?? d?.UnreadChannels ?? 0,
        friendsUnread: d?.friendsUnread ?? d?.FriendsUnread ?? 0,
        conversationsUnread: d?.conversationsUnread ?? d?.ConversationsUnread ?? 0,
        // Absent until the chat service ships AiUnread — 0 just means "no badge".
        aiUnread: d?.aiUnread ?? d?.AiUnread ?? 0,
      };
    } catch {
      return empty;
    }
  }

  // ─── Block / unblock / blocked list + delete DM (Agape.ChatApi) ───
  //
  // These four live on the self-hosted chat service (CHAT_HUB_BASE_URL /
  // agape-chat.*), NOT the main API — they're part of the GetStream→ChatApi
  // strangler migration (CORE-356/478) and the main API doesn't proxy them, so
  // hitting BASE_URL returns a genuine 404. The same JWT authenticates both
  // hosts. Legacy chat routes (contacts, dm/{id}/channel) still answer on
  // BASE_URL, so those stay put.

  /** Block a user — the BE stops delivering their messages server-side. */
  async blockChatUser(userId: string): Promise<void> {
    const res = await fetch(`${CHAT_HUB_BASE_URL}/api/chat/block/${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(res);
  }

  /** Unblock a previously blocked user. */
  async unblockChatUser(userId: string): Promise<void> {
    const res = await fetch(`${CHAT_HUB_BASE_URL}/api/chat/block/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(res);
  }

  /** The «Чёрный список» — users the current user has blocked. Tolerates a bare
   *  array or a wrapped `{ items }` / `{ blocked }` and camel/Pascal field names. */
  async getBlockedUsers(): Promise<PublicUserSummary[]> {
    const res = await fetch(`${CHAT_HUB_BASE_URL}/api/chat/blocked`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    const data = await this.handleResponse<any>(res);
    const rows: any[] = Array.isArray(data) ? data : (data?.items ?? data?.blocked ?? []);
    return rows.map((r: any): PublicUserSummary => ({
      id: String(r?.id ?? r?.userId ?? r?.Id ?? ''),
      displayName: r?.displayName ?? r?.name ?? r?.DisplayName ?? '',
      avatarUrl: r?.avatarUrl ?? r?.avatar ?? r?.AvatarUrl ?? null,
      bio: r?.bio ?? null,
      intention: r?.intention ?? null,
      location: r?.location ?? null,
      isMaster: !!(r?.isMaster ?? r?.IsMaster),
      isOnline: !!(r?.isOnline ?? r?.IsOnline),
      joinedAt: r?.joinedAt ?? '',
    }));
  }

  /** Delete a DM conversation for the current user (removes it from the list).
   *  The DmController keys the conversation by the DM *channel key*, not the
   *  peer's userId — so resolve the key first (ensureDmChannel, on the main API)
   *  and delete on the chat service. */
  async deleteDmConversation(otherUserId: string): Promise<void> {
    const { channelKey } = await this.ensureDmChannel(otherUserId.replace(/^user_/, ''));
    const res = await fetch(`${CHAT_HUB_BASE_URL}/api/chat/dm/${encodeURIComponent(channelKey)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(res);
  }

  /** «Удалить у всех» — hard-truncate a 1:1 DM for BOTH participants (agape-backend
   *  7cf9bf3). Keyed by the DM channel key like deleteDmConversation; the BE also
   *  emits a `ConversationCleared` hub event so the peer's open thread resets live.
   *  Idempotent (repeat → removed=0). 403 if the caller isn't a participant. */
  async clearDmConversation(otherUserId: string): Promise<void> {
    const { channelKey } = await this.ensureDmChannel(otherUserId.replace(/^user_/, ''));
    const res = await fetch(`${CHAT_HUB_BASE_URL}/api/chat/dm/${encodeURIComponent(channelKey)}/clear`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(res);
  }

  async getAiCompanions(): Promise<ChatAiCompanionDto[]> {
    // CORE-510 — имена/подзаголовки/описания компаньонов локализованы на бэке,
    // и язык он берёт из `?lang=`, иначе из Accept-Language, иначе «ru».
    // Заголовок нам не годится: на вебе его ставит браузер по своей локали, а не
    // по языку, выбранному в приложении, а в RN его нет вовсе — тогда весь
    // каталог приезжал бы русским. Поэтому язык передаём явно, как во всех
    // остальных локализованных вызовах (`getAboutPlatform`,
    // `getPreferenceOptions`, `getPracticeDirectionCatalog`).
    let language: string | undefined;
    try {
      const { getCurrentLanguage } = await import('../i18n');
      language = getCurrentLanguage();
    } catch {
      // i18n недоступен (юнит-тесты) — бэк ответит на языке платформы.
    }

    const query = language ? `?lang=${encodeURIComponent(language)}` : '';
    // CORE-119 — bounded like getChatContacts: same chat-connect effect, same
    // eternal-loader risk on a blocked/shaped RU origin. See CORE-400/CORE-119.
    const response = await fetchWithTimeout(`${BASE_URL}/api/chat/ai-companions${query}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    }, STREAM_TOKEN_TIMEOUT_MS);
    return this.handleResponse<ChatAiCompanionDto[]>(response);
  }

  async getChatMasters(): Promise<ChatMasterConfigDto[]> {
    const response = await fetch(`${BASE_URL}/api/chat/masters`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<ChatMasterConfigDto[]>(response);
  }

  async getChatFriends(): Promise<UserFriendDto[]> {
    const response = await fetch(`${BASE_URL}/api/chat/friends`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<UserFriendDto[]>(response);
  }

  async addChatFriend(friendUserId: string): Promise<UserFriendDto> {
    const response = await fetch(`${BASE_URL}/api/chat/friends`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ friendUserId }),
    });
    return this.handleResponse<UserFriendDto>(response);
  }

  // ─── Реферальная программа (CORE-962 / CORE-964) ───

  /**
   * CORE-1156 — засчитать переход по реферальной ссылке `/r/{code}`.
   *
   * Анонимный вызов, и это единственное место, откуда он делается: пока его не
   * было, `ReferralAttribution` не создавалась ни разу, и любая регистрация
   * после перехода по ссылке привязывалась к «никому» (`NoAttribution`) — сама
   * ссылка при этом выглядела рабочей, а счётчики стояли на нуле.
   *
   * `getHeaders(false)` шлёт сохранённый `X-Anon-User-Id`, если он уже есть, и
   * тем самым продолжает историю того же посетителя, а не заводит второго.
   * Токен из ответа сохраняем ЯВНО и с ожиданием: `handleResponse` подхватывает
   * заголовок фоновым промисом, а здесь между переходом и регистрацией стоит
   * ровно то, ради чего всё делается, — терять его на гонке нечем.
   *
   * CORE-956 — contextual-ссылка (пост, эфир, кампания) выдаётся как
   * `/r/{code}?l={id}`, и `linkId` уходит сюда: сервер засчитывает переход
   * именно на неё, если она принадлежит владельцу кода. Без `linkId` (общая
   * ссылка, старый адрес) — переход на общую ссылку, как и раньше.
   */
  async recordReferralVisit(code: string, linkId?: number): Promise<ReferralVisitResponse> {
    const response = await fetch(`${BASE_URL}/api/referral/visit`, {
      method: 'POST',
      headers: await this.getHeaders(false),
      body: JSON.stringify(linkId ? { code, linkId } : { code }),
    });
    const result = await this.handleResponse<ReferralVisitResponse>(response, true);
    if (result?.visitorToken) {
      await storage.setAnonUserId(result.visitorToken);
    }
    return result;
  }

  /**
   * Своя реферальная личность: код, ссылка и факт подключения к правилам.
   * Доступно с этапа A (флаг `referral_tracking`); при выключенном этапе
   * бэкенд отвечает 404 — это норма, а не ошибка.
   */
  async getReferralIdentity(): Promise<ReferralIdentity> {
    return this.authedRequest<ReferralIdentity>(
      `${BASE_URL}/api/referral/me`,
      { method: 'GET' },
    );
  }

  /**
   * Итоги по валютам и дата ближайшей выплаты. Суммы приходят в целых
   * минорных единицах — считать что-либо от них на клиенте нельзя,
   * только показывать (см. utils/referralMoney).
   */
  async getReferralSummary(): Promise<ReferralSummary> {
    return this.authedRequest<ReferralSummary>(
      `${BASE_URL}/api/referral/summary`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-2067 — баланс баллов (модель «points»): доступно, ожидает hold,
   * начислено. Баллы сервер уже пересчитал по своему курсу.
   */
  async getReferralPoints(): Promise<ReferralPoints> {
    return this.authedRequest<ReferralPoints>(
      `${BASE_URL}/api/referral/points`,
      { method: 'GET' },
    );
  }

  /** Воронка: переходы → регистрации → покупатели. Только счётчики, без PII. */
  async getReferralFunnel(): Promise<ReferralFunnel> {
    return this.authedRequest<ReferralFunnel>(
      `${BASE_URL}/api/referral/funnel`,
      { method: 'GET' },
    );
  }

  /** История начислений. Ни покупателя, ни названия продукта в ответе нет. */
  async getReferralAccruals(limit = 20, offset = 0): Promise<ReferralAccrual[]> {
    return this.authedRequest<ReferralAccrual[]>(
      `${BASE_URL}/api/referral/accruals?limit=${limit}&offset=${offset}`,
      { method: 'GET' },
    );
  }

  /** История выплат по месяцам. */
  async getReferralPayouts(): Promise<ReferralPayout[]> {
    return this.authedRequest<ReferralPayout[]>(
      `${BASE_URL}/api/referral/payouts`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-1035 — пропуск на заполнение налоговых данных в российском контуре.
   *
   * Отдаёт адрес контура и подписанный пропуск; саму форму принимает уже
   * контур на машине в РФ. Отправлять реквизиты сюда нельзя: этот бэкенд
   * стоит в Финляндии, и первичная запись персональных данных россиянина
   * оказалась бы там — то, что запрещает 152-ФЗ ст. 18 ч. 5.
   */
  async getPayoutDetailsTicket(): Promise<PayoutDetailsTicket> {
    return this.authedRequest<PayoutDetailsTicket>(
      `${BASE_URL}/api/referral/payout-details/ticket`,
      { method: 'POST' },
    );
  }

  /**
   * Принять правила программы. Идемпотентно по версии.
   * CORE-1071 — `document`: принятая галочкой редакция «Условий реферальной
   * программы», когда сервер её требует (`termsDocumentRequired`).
   */
  async enrollInReferralProgram(
    termsVersion: string,
    document?: Omit<import('../types/referral').ReferralTermsDocument, 'effectiveFrom'>,
  ): Promise<ReferralIdentity> {
    return this.authedRequest<ReferralIdentity>(
      `${BASE_URL}/api/referral/enroll`,
      { method: 'POST', body: JSON.stringify(document ? { termsVersion, document } : { termsVersion }) },
    );
  }

  // ─── Feature Flags ───

  async getFeatures(): Promise<Record<string, boolean>> {
    const response = await fetch(`${BASE_URL}/api/features`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    const data = await this.handleResponse<{ features: Record<string, boolean> }>(response);
    return data.features;
  }

  async removeChatFriend(friendUserId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/chat/friends/${friendUserId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.title || 'Failed to remove friend');
    }
  }

  // ============ Sound Tracks ============

  async getSoundTracks(params?: { category?: string; search?: string; limit?: number; offset?: number }): Promise<PaginatedResponse<SoundTrack>> {
    const query = new URLSearchParams();
    if (params?.category) query.append('category', params.category);
    if (params?.search) query.append('search', params.search);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/soundtracks${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<PaginatedResponse<SoundTrack>>(response);
  }

  async getSoundTrackCategories(): Promise<string[]> {
    const response = await fetch(`${BASE_URL}/api/soundtracks/categories`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<string[]>(response);
  }

  // ============ Practice Rooms (Watch Parties) ============

  // CORE-1587 — `authedRequest`: протухший JWT обновляется и запрос
  // повторяется. Повтор не создаст вторую комнату — 401/403 сервер отдаёт до
  // создания. `skipAuthRedirect` — визард сам предлагает войти снова, не теряя
  // заполненное (сброс стека на /login стирал его).
  //
  // CORE-1910 — каждый вызов = одно действие пользователя = один
  // Idempotency-Key. Тот же ключ едет в повторе после refresh и в транспортном
  // повторе/фолбэке на api-ru (apiFallback/netResilience повторяют POST только
  // с ключом), а сервер на повтор отдаёт уже созданную комнату.
  async createRoom(
    data: CreateRoomRequest,
    opts?: { skipAuthRedirect?: boolean; idempotencyKey?: string },
  ): Promise<RoomResponse> {
    return this.authedRequest<RoomResponse>(
      `${BASE_URL}/api/watch-parties`,
      {
        method: 'POST',
        headers: { [IDEMPOTENCY_KEY_HEADER]: opts?.idempotencyKey ?? newIdempotencyKey() },
        body: JSON.stringify(data),
      },
      opts?.skipAuthRedirect,
    );
  }

  async getRoomByCode(code: string): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${code}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomResponse>(response);
  }

  // ── CORE-851 — co-hosts inside an active unified Live-session ──────────
  //
  // These are the WatchParty (system B) co-host controls: appoint / withdraw
  // a co-host and hand over the host role, all while the session runs. They
  // live on `WatchPartyController` (`/api/watch-parties/{id}/...`), keyed by
  // the NUMERIC room id — distinct from the legacy эфир co-host preview family
  // (`getLiveCohosts`, `/api/lives/{id}/cohosts`, system A) which we leave
  // untouched. The gate is `session.cohosts`, host-only (not delegatable), so
  // a co-host can never reach these.
  //
  // `authedRequest` (silent 401-refresh) because they run mid-session, exactly
  // like `kickRoomParticipant` / `joinRoom`.

  /** Current co-hosts of a room, each with their delegated capabilities. */
  async getRoomCohosts(id: number): Promise<import('../types/auth').RoomCohost[]> {
    return this.authedRequest<import('../types/auth').RoomCohost[]>(
      `${BASE_URL}/api/watch-parties/${id}/cohosts`,
      { method: 'GET' },
    );
  }

  /**
   * Promote a participant to co-host. `capabilities` omitted (undefined)
   * delegates everything this session allows to delegate; pass a subset to
   * narrow it. Returns the created grant.
   */
  async assignRoomCohost(
    id: number,
    userId: string,
    capabilities?: string[],
  ): Promise<import('../types/auth').RoomCohost> {
    return this.authedRequest<import('../types/auth').RoomCohost>(
      `${BASE_URL}/api/watch-parties/${id}/cohosts`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, capabilities }),
      },
    );
  }

  /** Withdraw a co-host's powers. 204 → resolves. */
  async revokeRoomCohost(id: number, targetUserId: string): Promise<void> {
    await this.authedRequest<void>(
      `${BASE_URL}/api/watch-parties/${id}/cohosts/${encodeURIComponent(targetUserId)}`,
      { method: 'DELETE' },
    );
  }

  // ── CORE-1717 — несколько равноправных ведущих ───────────────────────────
  //
  // Назначение ведущим — предложение с принятием: права появляются только
  // когда адресат нажмёт «Принять». Звать и снимать может любой ведущий.

  /** Позвать присутствующего участника в ведущие. 201 — новое, 200 — уже ожидающее. */
  async inviteRoomHost(id: number, userId: string): Promise<import('../types/auth').HostInvite> {
    return this.authedRequest<import('../types/auth').HostInvite>(
      `${BASE_URL}/api/watch-parties/${id}/hosts/invites`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      },
    );
  }

  /** Принять предложение (только адресат). Повтор после успеха — тот же ответ. */
  async acceptRoomHostInvite(id: number, inviteId: number): Promise<import('../types/auth').RoomCohost> {
    return this.authedRequest<import('../types/auth').RoomCohost>(
      `${BASE_URL}/api/watch-parties/${id}/hosts/invites/${inviteId}/accept`,
      { method: 'POST' },
    );
  }

  /** Отказаться (только адресат). */
  async declineRoomHostInvite(id: number, inviteId: number): Promise<import('../types/auth').HostInvite> {
    return this.authedRequest<import('../types/auth').HostInvite>(
      `${BASE_URL}/api/watch-parties/${id}/hosts/invites/${inviteId}/decline`,
      { method: 'POST' },
    );
  }

  /** Отозвать неотвеченное предложение (любой ведущий). 204 → resolves. */
  async cancelRoomHostInvite(id: number, inviteId: number): Promise<void> {
    await this.authedRequest<void>(
      `${BASE_URL}/api/watch-parties/${id}/hosts/invites/${inviteId}`,
      { method: 'DELETE' },
    );
  }

  /** Hand the host role to another participant. 204 → resolves. */
  async transferRoomHost(id: number, userId: string): Promise<void> {
    await this.authedRequest<void>(
      `${BASE_URL}/api/watch-parties/${id}/transfer-host`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      },
    );
  }

  /**
   * CORE-771 — the caller's effective Live capabilities **before** any session
   * exists (the dynamic preview for building the create flow). Distinct from the
   * frozen `RoomResponse.capabilities` snapshot: this recomputes server-side and
   * `dynamic: true` — it may widen after a level-up or narrow when the flag
   * flips, so callers treat it as «what you can do if you create now», never a
   * fixed grant. On an older backend without this route the request 404s; the
   * capability client maps any failure to a safe baseline (CORE-817).
   */
  async getCreatorCapabilities(): Promise<LiveCapabilitiesResponse> {
    // CORE-1587 — refresh + повтор; без сброса на /login: это фоновый опрос
    // (каждые 45 с), и его отказ не должен выбрасывать из открытого визарда.
    // Сбой клиент прав и так сводит к безопасному минимуму.
    return this.authedRequest<LiveCapabilitiesResponse>(
      `${BASE_URL}/api/watch-parties/capabilities`,
      { method: 'GET' },
      true,
    );
  }

  /**
   * CORE-115 — fetch a single room/archive by numeric id for editing.
   * Hits the plain `GET /api/watch-parties/{id}` route (distinct from
   * `getRoomById`, which uses the `/id/{id}` variant).
   */
  // Room participation history — everyone who actually entered (incl. left /
  // kicked), one aggregated row per user. Distinct from the RSVP-intent
  // `/participants` endpoint. Server sorts: active first, then by last join.
  async getRoomParticipantsHistory(
    id: number,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<import('../types/auth').RoomParticipantHistory>> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const q = qs.toString();
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/participants/history${q ? `?${q}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PaginatedResponse<import('../types/auth').RoomParticipantHistory>>(response);
  }

  async getRoomForEdit(id: number): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomResponse>(response);
  }

  async getRoomById(id: number): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/id/${id}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    // Room detail is publicly readable — a 401 here shouldn't reset the
    // nav stack to /login. Surface the error inline. (Same fix pattern
    // as getLiveById for the same «card opens & bounces back» bug.)
    return this.handleResponse<RoomResponse>(response, true);
  }

  async joinRoom(code: string): Promise<RoomResponse> {
    // CORE-1417 — гостевая личность едет и вместе с JWT, но только сюда. Гость,
    // которому дали слово, нажимает «Войти» и возвращается в комнату этим
    // вызовом; слово записано на его `spectatorId`, и без личности сервер не
    // узнаёт в вошедшем того же человека — слово терялось (прод 15.09.2026,
    // комнаты 251/252). Остальные запросы по-прежнему шлют её только без JWT.
    const anonId = await storage.getAnonUserId().catch(() => null);
    // CORE-1782 — веб умеет публиковать практику в канал для записи и говорит
    // об этом явно; по признаку веб выбирает публикующего из ведущих. Нативная
    // сборка тела не шлёт — сервер определяет её по клиенту (не умеет).
    // CORE-1565 — веб называет версию текста предупреждения о записи, который
    // показывает: по ней сервер (с CORE-1565) не пускает браузер без
    // подтверждения этой версии и отвечает `recording_consent_required` — его
    // комната ведёт на экран входа. Прежний сервер поле не знает и пропускает.
    const body = Platform.OS === 'web'
      ? JSON.stringify({ canPublishPractice: true, recordingNoticeVersion: RECORDING_NOTICE_VERSION })
      : undefined;
    // AMB-198 — silent-refresh on 401. Joining a room mid-session must
    // not trip the JWT-expired kick-to-login flow.
    return this.authedRequest<RoomResponse>(
      `${BASE_URL}/api/watch-parties/${code}/join`,
      {
        method: 'POST',
        ...(body ? { body } : {}),
        ...(anonId ? { headers: { 'X-Anon-User-Id': anonId } } : {}),
      },
    );
  }

  async startRoom(id: number): Promise<RoomResponse> {
    // AMB-198 — silent-refresh on 401.
    return this.authedRequest<RoomResponse>(
      `${BASE_URL}/api/watch-parties/${id}/start`,
      { method: 'POST' },
    );
  }

  async endRoom(id: number): Promise<{ endedAtUtc: string }> {
    // AMB-198 — silent-refresh on 401.
    return this.authedRequest<{ endedAtUtc: string }>(
      `${BASE_URL}/api/watch-parties/${id}/end`,
      { method: 'POST' },
    );
  }

  /**
   * Group A — triggers calendar cleanup for a room that auto-closed via R9a
   * (nobody ever joined within 60s of T_start). BE endpoint may not exist yet
   * — this method swallows 4xx so it's safe to call before BE migration.
   */
  async cancelRoomFromCalendars(id: number): Promise<void> {
    try {
      const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/cancel`, {
        method: 'POST',
        headers: await this.getHeaders(true),
      });
      if (!response.ok && response.status !== 404) {
        // 404 = endpoint doesn't exist yet; any other failure is unexpected
        // but non-fatal — log and move on.
        console.info('[room-lifecycle] cancelRoomFromCalendars non-OK', response.status);
      }
    } catch (err) {
      console.info('[room-lifecycle] cancelRoomFromCalendars failed (graceful)', err);
    }
  }

  /**
   * CORE-115 — edit a finished room's published archive (recording).
   * Replaces the old PUT /api/watch-parties/{id}. Partial update: null /
   * omitted field = leave as-is, so send only what changed.
   * `languageCode: ''` clears the language tag; a `visibility` change
   * propagates to the whole recurring series (handled BE-side).
   */
  async updateArchive(id: number, data: import('../types/auth').UpdateArchiveRequest): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/archive`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<RoomResponse>(response);
  }

  /**
   * CORE-115 — upload a room cover image and get back its public URL.
   * Host-only. Allowed: image/jpeg, image/png, image/webp (≤ 5 MB). The
   * returned `url` is what you pass as `coverImageUrl` when creating /
   * updating / archiving a room. No Content-Type header — fetch sets the
   * multipart boundary itself.
   */
  async uploadRoomCover(
    file: File | { uri: string; name: string; type: string },
    opts?: { skipAuthRedirect?: boolean },
  ): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file as any);
    // CORE-1587 — multipart, поэтому не `authedRequest` (тот ставит JSON
    // Content-Type), но тот же refresh + повтор, что у createLive. Токен
    // читается в каждой попытке — повтор уходит уже с обновлённым.
    const fetcher = async () => {
      const token = await storage.getToken();
      return fetch(`${BASE_URL}/api/watch-parties/cover`, {
        method: 'POST',
        // Без токена заголовка нет — раньше уходило «Bearer null».
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
    };
    let response = await fetcher();
    response = await this.retryWithRefresh(fetcher, response);
    return this.handleResponse<{ url: string }>(response, opts?.skipAuthRedirect);
  }

  /**
   * CORE-833/838 — soft-cancel a scheduled room. The row is kept and flipped to
   * `Cancelled`: subscribers get a push, pending reminders are dropped, and the
   * invite link resolves to «Отменена» instead of a 404. Host only, Scheduled
   * only (BadRequest for Active/Ended). Returns the updated room.
   *
   * This is the user-facing cancel from the widget card menu, so — unlike the
   * fire-and-forget `cancelRoomFromCalendars` — it THROWS on failure, letting
   * the UI surface the error. `deleteRoom` (DELETE) stays the hard-removal path
   * in «Мои комнаты».
   */
  async cancelRoom(id: number): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/cancel`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomResponse>(response);
  }

  async deleteRoom(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.Message || err.message || 'Failed to delete room');
    }
  }

  /**
   * SCRUM-49 — Cancel this room and every future iteration of the
   * recurring series via parentRoomId. Past iterations stay intact.
   * Active rooms are ended gracefully (PartyEnded + cloud recording
   * stop) and kept as Ended rows so the recording can finalize.
   */
  async deleteRoomSeries(id: number): Promise<{ cancelledCount: number; anchorSurvived: boolean }> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/series`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.Message || err.message || 'Failed to cancel series');
    }
    return response.json();
  }

  /**
   * CORE-259 — the current user's gamification progress (Пламя Любви streak,
   * Свет Души XP, Уровень духовного пути). Auth-only; the BE resolves the
   * caller. New users get a zeroed profile rather than a 404.
   */
  async getGamificationProfile(): Promise<GamificationProfile> {
    return this.authedRequest<GamificationProfile>(
      `${BASE_URL}/api/gamification/profile`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-255 / CORE-663 — the authenticated achievement catalogue together
   * with the caller's binary earned state.  Per-achievement progress is not a
   * field of this DTO; callers compose it with the server-owned streak fields
   * returned by getGamificationProfile().
   */
  async getGamificationAchievements(): Promise<GamificationAchievementsResponse> {
    return this.authedRequest<GamificationAchievementsResponse>(
      `${BASE_URL}/api/gamification/achievements`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-274 — another user's Путь Света progress, for the «Поделиться» flow: a
   * shared link lands a guest on the sharer's public profile with `?tab=lightPath`
   * and shows their progress. Anonymous (works for guests, mirrors the public
   * badge lookup) — any id resolves; no progress → a zeroed profile, never a 404.
   * Plain fetch, no auth needed.
   */
  async getPublicGamificationProfile(userId: string): Promise<GamificationProfile> {
    const res = await fetch(`${BASE_URL}/api/gamification/profile/${encodeURIComponent(userId)}`, { method: 'GET' });
    return this.handleResponse<GamificationProfile>(res);
  }

  /**
   * CORE-690 / CORE-681 — privacy-safe, read-only progress for another user's
   * public profile. Unlike getPublicGamificationProfile (legacy share flow),
   * this endpoint exposes only streak, XP and the backend-computed level.
   */
  async getPublicGamificationSummary(userId: string): Promise<PublicGamificationSummary> {
    const response = await fetch(
      `${BASE_URL}/api/user/public/${encodeURIComponent(userId)}/gamification-summary`,
      {
        method: 'GET',
        // Attach auth when available so the backend can apply its normal
        // profile-visibility rules; anonymous visitors remain supported.
        headers: await this.getHeaders(true),
      },
    );
    return this.handleResponse<PublicGamificationSummary>(response, true);
  }

  /**
   * CORE-254 — the Уровни духовного пути ladder: current standing + every rung
   * (Искатель → Хранитель → Проводник света → Магистр света) with its XP floor
   * and reached/current flags. The FE renders names + thresholds from here so
   * they stay owned by the backend. Auth-only; a no-progress user gets Искатель.
   */
  async getGamificationLevelState(): Promise<GamificationLevelState> {
    return this.authedRequest<GamificationLevelState>(
      `${BASE_URL}/api/gamification/level`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-517 — the caller's XP history ledger (CORE-251), newest first: the
   * actual «последние начисления» behind the Свет Души total, so the Путь Света
   * block can show real earned entries rather than a static rewards catalogue.
   * Auth-only; a no-progress user gets an empty page.
   */
  async getGamificationXpHistory(limit = 10, offset = 0): Promise<PaginatedResponse<GamificationXpEntry>> {
    return this.authedRequest<PaginatedResponse<GamificationXpEntry>>(
      `${BASE_URL}/api/gamification/xp/history?limit=${limit}&offset=${offset}`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-269 — one user's level badge (shown next to their name across the app).
   * Anonymous (works for guests — master/user cards are public); any id resolves,
   * no progress → seeker. Plain fetch (no auth needed).
   */
  async getUserLevelBadge(userId: string): Promise<LevelBadge> {
    const res = await fetch(`${BASE_URL}/api/gamification/badge/${encodeURIComponent(userId)}`, { method: 'GET' });
    return this.handleResponse<LevelBadge>(res);
  }

  /**
   * CORE-269 — batch level-badge lookup for a list (comments, community messages,
   * cards). One request instead of N; every requested id is present in the map
   * (default seeker). Anonymous. Empty input short-circuits without a call.
   */
  async getLevelBadges(userIds: string[]): Promise<Record<string, LevelBadge>> {
    const ids = Array.from(new Set(userIds.filter(Boolean)));
    if (ids.length === 0) return {};
    const res = await fetch(`${BASE_URL}/api/gamification/badges`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds: ids }),
    });
    return this.handleResponse<Record<string, LevelBadge>>(res);
  }

  /**
   * CORE-256/272 — the caller's pending congratulation events (level-up,
   * achievement, milestone, record), newest-first. The FE pops a «поздравительное
   * окно» per event, then dismisses it via acknowledgeGamificationEvents. Reading
   * never provisions state.
   */
  async getGamificationEvents(): Promise<GamificationEvents> {
    return this.authedRequest<GamificationEvents>(
      `${BASE_URL}/api/gamification/events`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-509 — Agapella's own wording for one pending celebration.
   *
   * `text: null` is a normal answer, not a failure: generation may be off, slow
   * or refused by the wording rules, and the caller then shows its static
   * fallback. Never let this decide whether the celebration appears.
   */
  async getCelebrationLine(eventId: number): Promise<{ text: string | null }> {
    return this.authedRequest<{ text: string | null }>(
      `${BASE_URL}/api/gamification/events/${eventId}/line`,
      { method: 'GET' },
    );
  }

  /** Dismiss congratulation events — specific ids, or all pending when omitted. */
  async acknowledgeGamificationEvents(ids?: number[]): Promise<{ acknowledged: number }> {
    return this.authedRequest<{ acknowledged: number }>(
      `${BASE_URL}/api/gamification/events/acknowledge`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids && ids.length ? { ids } : {}),
      },
    );
  }

  /**
   * CORE-258/274 — the prepared share-card for one of the caller's own progress
   * items: type ∈ level|achievement|streak|milestone|record, id = the item key
   * (level code / achievement key / 'current'|'longest' / threshold days). 404s
   * when the user hasn't actually reached that item (callers treat as "nothing
   * to share" and fall back to a generic message).
   */
  async getGamificationShareCard(type: GamificationShareType, id: string): Promise<GamificationShareCard> {
    return this.authedRequest<GamificationShareCard>(
      `${BASE_URL}/api/gamification/share/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
  }

  /** CORE-710/709 — owner-only snapshot for the overall Light Path share card. */
  async getCurrentGamificationProgressShare(): Promise<GamificationShareCard> {
    return this.getGamificationShareCard('progress', 'current');
  }

  /**
   * CORE-966 — the anonymous read behind a `/s/{token}` share link. Addressed by
   * the opaque token only, so it cannot be walked to ask what an account holds;
   * 404 covers every "nothing here" (unknown token, private profile, an item the
   * user no longer has). No auth header: this is the same read a link-preview
   * crawler performs, and attaching a session would make the app's answer differ
   * from the one Telegram sees.
   */
  async getPublicGamificationShareCard(token: string): Promise<PublicGamificationShareCard> {
    const response = await fetch(
      `${BASE_URL}/api/gamification/share/public/${encodeURIComponent(token)}`,
      { method: 'GET' },
    );
    return this.handleResponse<PublicGamificationShareCard>(response, true);
  }

  /**
   * CORE-497 — the caller's own accumulated interest profile (directions /
   * masters / formats / activities, each sorted by count). Zeroed for a user
   * with no signals yet. Read-only; no UI consumes it yet (personalization is
   * a later epic) — exposed so the client can build on it without a re-fetch.
   */
  async getMyInterestProfile(): Promise<InterestProfileResponse> {
    return this.authedRequest<InterestProfileResponse>(
      `${BASE_URL}/api/analytics/me/profile`,
      { method: 'GET' },
    );
  }

  /**
   * CORE-137 / CORE-674 — HTTP-кик участника комнаты. Хаб остаётся основным
   * путём (кик и рассылка ParticipantKicked идут одним вызовом), а этот —
   * запасной: без соединения с хабом ведущий раньше не мог исключить никого
   * вообще. Сервер делает то же самое, что и хаб: письмо модерации, событие
   * user_kicked_from_room, выброс из Agora и запрет входа по ссылке.
   */
  async kickRoomParticipant(
    id: number,
    userId: string,
    reason?: string,
    description?: string,
  ): Promise<{ userId: string; outcome: string; freshKick: boolean }> {
    return this.authedRequest(
      `${BASE_URL}/api/watch-parties/${id}/kick`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, reason, description }),
      },
    );
  }

  async leaveRoom(id: number): Promise<{ type: string }> {
    // AMB-198 — silent-refresh on 401. Critical: a user trying to leave
    // the room must succeed even with a stale JWT, otherwise the cleanup
    // (Agora teardown, BE participant.LeftAt) is left half-done.
    //
    // CORE-1511 — и переживает временный отказ. Вызывающие глотают ошибку и
    // уводят человека («Вы вышли»), а хаб при уходе шлёт `LeaveParty`, после
    // которого сервер уже не закрывает строку по льготному периоду: один
    // потерянный DELETE под нагрузкой (502/504 на ag1, CORE-1500) оставлял
    // ушедшего в счётчике и списке у всех. Повторяем сеть, 5xx и 429.
    const delaysMs = [0, 1000, 3000];
    for (let attempt = 0; ; attempt++) {
      if (delaysMs[attempt]) await new Promise(resolve => setTimeout(resolve, delaysMs[attempt]));
      try {
        return await this.authedRequest<{ type: string }>(
          `${BASE_URL}/api/watch-parties/${id}/leave`,
          { method: 'DELETE' },
        );
      } catch (e) {
        const status = (e as { status?: number })?.status;
        const transient = status === undefined || status >= 500 || status === 429;
        if (!transient || attempt >= delaysMs.length - 1) throw e;
      }
    }
  }

  async getMyRooms(params?: {
    status?: string;
    limit?: number;
    offset?: number;
    hostedOnly?: boolean;
    /**
     * CORE-1132 — только будущие встречи, отсортированные по дате начала.
     * Без него выдача идёт по дате СОЗДАНИЯ, и «двести последних, среди которых
     * есть будущие» — это не «двести ближайших встреч».
     */
    upcoming?: boolean;
  }): Promise<PaginatedResponse<RoomResponse>> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    // AMB-263 follow-up — BE-side narrow to rooms where caller is host.
    // Default off (omitted) preserves the legacy «host OR participant OR
    // subscriber» semantics for every callsite that hasn't opted in. The
    // Studio counter passes true so totalCount reflects only hosted rooms.
    if (params?.hostedOnly) query.append('hostedOnly', 'true');
    if (params?.upcoming) query.append('upcoming', 'true');
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/watch-parties/my${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PaginatedResponse<RoomResponse>>(response);
  }

  async getPublicRooms(params?: { status?: string; category?: string; limit?: number; offset?: number }): Promise<PaginatedResponse<RoomResponse>> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.category) query.append('category', params.category);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/watch-parties/public${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<PaginatedResponse<RoomResponse>>(response);
  }

  async getLiveNowRooms(limit = 10): Promise<RoomResponse[]> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/live-now?limit=${limit}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomResponse[]>(response);
  }

  async getStartingSoonRooms(limit = 10): Promise<RoomResponse[]> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/starting-soon?limit=${limit}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomResponse[]>(response);
  }

  async subscribeToRoom(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/subscribe`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async unsubscribeFromRoom(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/subscribe`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<void>(response);
  }

  async toggleRoomNotifications(id: number, enabled: boolean): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/subscribe/notifications`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ enabled }),
    });
    await this.handleResponse<void>(response);
  }

  /**
   * CORE-528/846 — public listener entry into a live. Mirrors
   * `spectateRoom` for the broadcast surface: a bodyless, unauthenticated GET
   * that hands an anonymous guest a spectatorId and a fallback name so they can
   * appear in the room's named roster instead of only bumping the anonymous
   * viewer aggregate.
   *
   * The id is STABLE per browser — the BE derives it from the persisted
   * `X-Anon-User-Id` that `getHeaders`/`handleResponse` already round-trip — so
   * a refresh or reconnect returns the same spectatorId and the roster dedupes
   * instead of growing a second row for the same person.
   */
  async spectateLive(id: number): Promise<SpectateLiveResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${id}/spectate`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<SpectateLiveResponse>(response);
  }

  /**
   * CORE-1420 — Agora fallback proxy config, or null when the backend does not
   * offer the proxy to this caller (204: flag off, or not deployed here).
   * `force` is the dev/staging console switch; prod ignores it.
   */
  async getRtcProxyConfig(force = false): Promise<RtcProxyConfig | null> {
    const response = await fetch(`${BASE_URL}/api/rtc/proxy-config${force ? '?force=true' : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    if (response.status === 204) return null;
    return this.handleResponse<RtcProxyConfig>(response);
  }

  /**
   * CORE-1513 — ростер для сторожа комнаты: участники и гости, сцена, пауза,
   * политика камер, заморозка чата, фон. Сервер собирает его раз в 2 с на
   * комнату для всех сразу, а полный `getRoomByCode` строил комнату на каждый
   * вызов — при 1000 зрителях ~33 сборки в секунду.
   */
  async getRoomRosterSnapshot(id: number): Promise<RoomRosterSnapshotResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/roster-snapshot`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomRosterSnapshotResponse>(response);
  }

  /** CORE-1859 — `intent`: занять место на сцене или освободить его (см. `RtcTokenIntent`). */
  async getRoomRtcToken(id: number, intent?: RtcTokenIntent): Promise<RoomRtcToken> {
    const query = intent ? `?intent=${intent}` : '';
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/rtc-token${query}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomRtcToken>(response);
  }

  /**
   * CORE-1345 — занять экран комнаты и получить ключи второго потока. Один показ
   * на комнату: 409 `screen_share_busy` (с `holder`), 409 `practice_on_stage`,
   * 403 без права, 404 при выключенном флаге. Повтор своим же — идемпотентен.
   */
  async startRoomScreenShare(id: number): Promise<RoomScreenShareStartResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/screen-share`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomScreenShareStartResponse>(response);
  }

  /** CORE-1345 — закончить показ: свой — всегда, чужой — ведущий/модератор видео. Идемпотентно. */
  async stopRoomScreenShare(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/screen-share`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    await this.handleResponse<unknown>(response);
  }

  /**
   * CORE-1361 — publisher token for the library practice the host plays in a
   * room, so the captured clip can join the room's channel under its own uid and
   * be picked up by the recorder. Host-only, and refused until the recording is
   * actually running (the recorder's audio/video profile is fixed at its start).
   */
  async getRoomPracticeRtcToken(id: number): Promise<RoomRtcToken> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/rtc-token/practice`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomRtcToken>(response);
  }

  /**
   * Read-only chat history for an ended (or active) room. Used by the FE
   * archive layout's chat panel. Visibility-gated identically to recording.
   */
  async getRoomMessages(id: number): Promise<RoomMessageResponse[]> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/messages`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomMessageResponse[]>(response);
  }

  /**
   * CORE-1344 — что серверу позволено принять от этого зрителя в этой комнате
   * прямо сейчас: право, форматы, размеры, количество.
   *
   * Спрашивается при открытии чата и перезапрашивается по кадрам `ChatFrozen`
   * и `CohostsChanged` — отдельного события «политика изменилась» у хаба нет.
   * Ответ управляет только интерфейсом: право окончательно проверяют загрузка
   * и отправка, поэтому отказ этой ручки не должен ничего ломать — молчим и
   * не показываем скрепку.
   */
  async getRoomChatAttachmentPolicy(partyId: number): Promise<LiveChatAttachmentPolicy> {
    const response = await fetch(
      `${BASE_URL}/api/watch-parties/${partyId}/chat/attachments/policy`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<LiveChatAttachmentPolicy>(response);
  }

  /**
   * CORE-1344 — загрузить ОДИН файл в чат комнаты (multipart, поле `file`).
   *
   * Сервер проверяет права до чтения тела и отказывает раздельными кодами:
   * `chat_attachment_unsupported_type` (400), `chat_attachment_too_large`
   * (413), 403 без права, 404 если комнаты нет. Загруженный файл никому не
   * виден, пока его `id` не уйдёт в `SendMessageWithAttachments`;
   * неотправленный сервер убирает сам через сутки.
   *
   * `Content-Type` не ставим руками — границу multipart проставляет платформа.
   *
   * Потолок ожидания задаёт вызывающий: 20 МБ документа на мобильной сети
   * идут долго и законно, но без потолка «зашторенная» сеть не ответит и не
   * упадёт, и человек останется с вечным «загружается» (та же причина, что у
   * `uploadChatAttachment`).
   */
  async uploadRoomChatAttachment(
    partyId: number,
    file: Blob,
    fileName?: string,
    timeoutMs?: number,
    onProgress?: (loadedBytes: number, totalBytes: number) => void,
  ): Promise<LiveChatAttachment> {
    // CORE-2092 — свежий токен: загрузку шлёт XHR мимо `getHeaders`.
    const token = await this.getAuthToken();
    const form = new FormData();
    form.append('file', file as any, fileName || (file as any)?.name || 'file');
    const url = `${BASE_URL}/api/watch-parties/${partyId}/chat/attachments`;

    // XHR, а не fetch: у fetch нет прогресса отправки, а композер показывает
    // «1,4 из 2,4 МБ» и полосу. XHR есть и в браузере, и в React Native.
    const response = await new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      if (timeoutMs) xhr.timeout = timeoutMs;
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded, e.total);
        };
      }
      // Ответ заворачиваем в Response и отдаём общему handleResponse: у него
      // разбор тела, статус в ошибке и выход на 401 — вторую копию этих
      // правил здесь держать незачем.
      xhr.onload = () => resolve(new Response(xhr.responseText || null, { status: xhr.status }));
      // Без статуса: так ошибку и отличают от отказа сервера — «ответа не
      // было вовсе», и композер предлагает просто повторить.
      xhr.onerror = () => reject(new TypeError('Network request failed'));
      xhr.ontimeout = () => reject(new TypeError('Upload timed out'));
      xhr.send(form as any);
    });
    return this.handleResponse<LiveChatAttachment>(response);
  }

  /**
   * Polling endpoint for the host completion modal. Returns the recording
   * lifecycle state (None/Recording/Processing/Ready/Failed) plus playback
   * URL once Ready. Poll every 5s up to 30 min after End.
   */
  async getRoomRecordingStatus(id: number): Promise<RoomRecordingStatusResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/recording`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomRecordingStatusResponse>(response);
  }

  /**
   * CORE-1171 — правка настроек ещё не прошедшей Live-сессии. Host-only,
   * `PUT /api/watch-parties/{id}`.
   *
   * Поля необязательные по одному: `null`/отсутствие = «не трогать», значение =
   * заменить. Обложку и направления контракт не принимал вовсе — CORE-1175 их
   * добавил на тех же правах, но только пока сессия не началась: у начавшейся
   * сервер отвечает отказом, у завершённой они правятся эндпоинтом архивной
   * меты (CORE-116). Пустой список направлений отвергается (400).
   */
  async updateRoom(id: number, req: UpdateRoomRequest): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify(req),
    });
    return this.handleResponse<RoomResponse>(response);
  }

  /** Set room visibility (Public/Subscribers/Private). Host-only. */
  async updateRoomVisibility(id: number, visibility: WatchPartyVisibility): Promise<RoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/visibility`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ visibility }),
    });
    return this.handleResponse<RoomResponse>(response);
  }

  /**
   * Promote a finished room's recording into reusable VideoPractices in the
   * host master's library. Idempotent — repeat calls return the same items.
   * Host-only, master-only, room must have a Ready recording.
   *
   * CORE-760 — a resumed session recorded in several stretches publishes one
   * library item per stretch, so `items` holds them all (oldest first).
   * `videoPracticeId` / `alreadyPromoted` stay pointing at the first stretch
   * for callers that only need one id.
   */
  async promoteRoomRecordingToVideo(id: number): Promise<{
    videoPracticeId: number;
    alreadyPromoted: boolean;
    items?: Array<{ ordinal: number; videoPracticeId: number; alreadyPromoted: boolean; playbackUrl?: string | null }>;
  }> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/recording/promote-to-video`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{
      videoPracticeId: number;
      alreadyPromoted: boolean;
      items?: Array<{ ordinal: number; videoPracticeId: number; alreadyPromoted: boolean; playbackUrl?: string | null }>;
    }>(response);
  }

  /**
   * CORE-588 / CORE-554 — снять запись с публикации в «Медиа» без удаления
   * исходной Live-сессии. Un-hides → hides the promoted VideoPractice copy
   * (the room, its title/description/cover and chat archive стay intact), so
   * a later promote returns the same library item. Host-only, master-only.
   * Inverse of promoteRoomRecordingToVideo.
   */
  async unpublishRoomRecording(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/recording/unpublish`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.Message || err.message || 'Failed to unpublish recording');
    }
  }

  /**
   * CORE-588 / CORE-554 — удалить собственную запись Live-сессии, сохранив
   * саму комнату (title/description/cover/chat archive/series остаются). Also
   * un-publishes any promoted «Медиа» copy. Distinct from deleteRoom, which
   * removes the whole room. Host-only, «в рамках согласованных прав».
   *
   * CORE-1257 — bounded with `fetchWithTimeout`, not a bare `fetch`. A
   * stalled connection used to leave the caller's promise pending forever,
   * so the modal's «deleting» loader spun with no success, no error, and no
   * way to retry.
   */
  async deleteRoomRecording(id: number): Promise<void> {
    const response = await fetchWithTimeout(`${BASE_URL}/api/watch-parties/${id}/recording`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    }, DELETE_RECORDING_TIMEOUT_MS);
    if (!response.ok) {
      // CORE-1257 — the BE answers 403/404 with an empty body, and the old
      // English fallback went straight into the dialog. Keep `status` on the
      // error so the UI can map it with `friendlyApiError`, which replaces the
      // «Request failed with status N» template by status-keyed copy.
      const err = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(err.Message || err.message || `Request failed with status ${response.status}`),
        { status: response.status },
      );
    }
  }

  /**
   * CORE-2046 — «Скачать запись». Owner only. `ready` carries a short-lived
   * signed link that the browser saves as a file; `preparing` means the BE is
   * joining the recording's HLS fragments into one MP4/M4A — ask again later.
   * POST because the first call may queue that job; repeating it is safe.
   * Errors keep `status`: 404 means this BE has no such endpoint yet (the
   * caller hides the action), 403 — not the recording's author.
   */
  async requestRecordingDownload(source: 'room' | 'live', id: number): Promise<RecordingDownloadResponse> {
    const url = source === 'room'
      ? `${BASE_URL}/api/watch-parties/${id}/recording/download`
      : `${BASE_URL}/api/master/content/lives/${id}/recording/download`;
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: await this.getHeaders(true),
    }, 20_000);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(err.Message || err.message || `Request failed with status ${response.status}`),
        { status: response.status },
      );
    }
    return response.json();
  }

  /**
   * Pause / resume the active session (BE rev 4). Host-only. While paused,
   * the auto-end timer is on hold and the BE accumulates the pause duration
   * in `TotalPausedSeconds`. Both host and participants get the new state
   * via WS `RoomPlaybackChanged`.
   */
  async setRoomPlayback(id: number, action: 'pause' | 'resume'): Promise<RoomPlaybackState> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/playback`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ action }),
    });
    return this.handleResponse<RoomPlaybackState>(response);
  }

  /**
   * CORE-850 — turn the session chat off or back on. Host + master only.
   *
   * Freezing keeps the history readable and refuses new messages; it is not a
   * delete (CORE-550). The endpoint has existed since CORE-550 but nothing on
   * the client ever called it, so there was no way to freeze a chat from the
   * app at all. All participants get the new state via WS `ChatFrozen`.
   */
  async setChatFrozen(id: number, frozen: boolean): Promise<{ isChatFrozen: boolean }> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/chat/freeze`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ frozen }),
    });
    return this.handleResponse<{ isChatFrozen: boolean }>(response);
  }

  /**
   * Start cloud recording in-session (BE rev 4). Host-only. 503 if Agora
   * recording credentials aren't configured for the environment. WS event
   * `RoomRecordingChanged { state: 'Recording' }` notifies all participants.
   *
   * CORE-859 — `hasVideo` tells the server whether a live video track exists
   * in the RTC channel right now, so it can pick the Agora recording profile
   * (audio-only vs video canvas) that matches the channel. Only the client in
   * the channel knows this; the server otherwise falls back to the room type,
   * which is a poor proxy. Pass `undefined` (the default) when the caller
   * cannot answer honestly — the field is then omitted rather than guessed.
   */
  async startRoomRecording(id: number, hasVideo?: boolean): Promise<RoomRecordingChange> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/recording/start`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      ...(hasVideo === undefined ? {} : { body: JSON.stringify({ hasVideo }) }),
    });
    return this.handleResponse<RoomRecordingChange>(response);
  }

  /**
   * Stop cloud recording in-session (BE rev 4). Host-only. WS event
   * `RoomRecordingChanged { state: 'Processing' | 'Ready', playbackUrl? }`
   * arrives once Agora finalizes — typically 5–15 min after stop.
   */
  async stopRoomRecording(id: number): Promise<RoomRecordingChange> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/recording/stop`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<RoomRecordingChange>(response);
  }

  /**
   * Swap the media currently playing in an active room (BE rev 4). Host-only.
   * BE rebases `MediaStartedAtUtc` so every client restarts the new track
   * from 00:00 in sync. Use to pivot from a video practice to a soundtrack
   * mid-session, or vice versa.
   */
  /**
   * CORE-1371 — `restart` просит начать ТОТ ЖЕ материал сначала. Без флага повтор
   * тех же id не считается первичной сменой (CORE-912): сервер не двигает якорь,
   * не сбрасывает плейхед и не открывает стретч, то есть перезапуска не
   * происходит вовсе. Вывести намерение из тела нельзя — id у него прежние.
   */
  async swapRoomMedia(
    id: number,
    body: { mediaType: RoomMediaType; videoPracticeId?: number; soundTrackId?: number; restart?: boolean; resumeAtSeconds?: number },
  ): Promise<RoomMediaChange> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/media`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify(body),
    });
    return this.handleResponse<RoomMediaChange>(response);
  }

  /**
   * CORE-831 (возврат QA 16.10) — команда фона комнаты по REST, двойник
   * `Background*` хаба. Сервер рассылает `BackgroundAudioChanged` всей комнате
   * и отвечает новым состоянием фона.
   */
  async sendRoomBackgroundCommand(
    id: number,
    body: { command: 'play' | 'pause' | 'stop' | 'restart' | 'looping' | 'volume'; isLooping?: boolean; volume?: number },
  ): Promise<RoomBackgroundAudioSnapshot> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/background`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(body),
    });
    return this.handleResponse<RoomBackgroundAudioSnapshot>(response);
  }

  /**
   * CORE-1741 — свежая ссылка на файл чата комнаты или эфира по `linkUrl`
   * вложения. Личность — токен или, у гостя, `X-Anon-User-Id` (`getHeaders`).
   * В закрытой сессии ссылка одноразовая и живёт около двух минут.
   */
  async requestChatAttachmentLink(linkPath: string): Promise<{ url: string; expiresAtUtc: string }> {
    const response = await fetch(`${BASE_URL}${linkPath}`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ url: string; expiresAtUtc: string }>(response, true);
  }

  /**
   * CORE-1739/1741 — как взять файл чата по одноразовой ссылке: сервер отдаёт
   * его только тому, кому выдал ссылку, и только с его токеном входа или
   * гостевой личностью (`X-Anon-User-Id`) — ровно с тем, с чем просили ссылку.
   * Отдаёт абсолютный адрес и эти заголовки: веб берёт файл `fetch`, приложение —
   * скачиванием в кеш. Пересланная ссылка без заголовков — 404.
   */
  async chatAttachmentFileRequest(linkPath: string, download = false): Promise<{ url: string; headers: Record<string, string> }> {
    const { url } = await this.requestChatAttachmentLink(linkPath);
    const full = url.startsWith('/') ? `${BASE_URL}${url}` : url;
    const all = (await this.getHeaders(true)) as Record<string, string>;
    const headers: Record<string, string> = {};
    if (all['Authorization']) headers['Authorization'] = all['Authorization'];
    if (all['X-Anon-User-Id']) headers['X-Anon-User-Id'] = all['X-Anon-User-Id'];
    return { url: download ? `${full}${full.includes('?') ? '&' : '?'}download=true` : full, headers };
  }

  /**
   * CORE-1739 — файл чата по одноразовой ссылке целиком (веб). Не дали —
   * ошибка со статусом (403 `attachment_forbidden` на ссылке, 404 на файле).
   */
  async fetchChatAttachmentFile(linkPath: string, download = false): Promise<Blob> {
    const { url, headers } = await this.chatAttachmentFileRequest(linkPath, download);
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const error: ApiError = { message: 'Файл недоступен', status: response.status };
      throw error;
    }
    return response.blob();
  }

  /** CORE-1739 — файл чата сообщества (мастера); то же, что `fetchChatAttachmentFile`. */
  async fetchCommunityChatAttachment(linkPath: string, download = false): Promise<Blob> {
    return this.fetchChatAttachmentFile(linkPath, download);
  }

  async spectateRoom(code: string): Promise<SpectateRoomResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${code}/spectate`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<SpectateRoomResponse>(response);
  }

  /**
   * CORE-1658 — подтверждение на экране входа: человек видел предупреждение о
   * записи и согласился. Вошедший опознаётся токеном, гость — анонимной
   * личностью: её надо получить ДО запроса (`getAnonIdentity`), иначе сервер
   * начеканит новую, и подтверждение окажется не у того гостя, что войдёт.
   */
  async recordLiveRecordingConsent(id: number, textVersion: string, document?: AcceptedDocument): Promise<void> {
    const token = await storage.getToken();
    if (!token) await this.getAnonIdentity().catch(() => { /* не фатально: сервер начеканит личность сам */ });
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/recording-consent`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      // CORE-1660 — вместе с версией уходит редакция из реестра: вариант и хеш текста.
      body: JSON.stringify({ textVersion, textVariant: document?.variant, textSha256: document?.sha256 }),
    });
    await this.handleResponse<unknown>(response);
  }

  /**
   * CORE-1197 — токен гостя по ссылке. `inviteCode` не косметика: сервер
   * выдаёт право публиковать только тому, кто предъявил ДЕЙСТВУЮЩИЙ код
   * (CORE-1199). Числового id мало — он предсказуем и встречается в ссылках на
   * объявление сессии, поэтому пропуском считается именно код.
   *
   * Без кода ответ прежний, subscriber, — так что старые вызывающие ничего не
   * теряют, а гость без кода просто остаётся зрителем.
   */
  async getSpectatorRtcToken(id: number, inviteCode?: string, intent?: RtcTokenIntent): Promise<RoomRtcToken> {
    const params = new URLSearchParams();
    if (inviteCode) params.set('code', inviteCode);
    if (intent) params.set('intent', intent);
    // CORE-1565 — то же, что `recordingNoticeVersion` у `/join`: гость с веба
    // называет версию текста, и без её подтверждения токен звонка не выдают.
    if (Platform.OS === 'web') params.set('notice', RECORDING_NOTICE_VERSION);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/spectate/rtc-token${query}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomRtcToken>(response);
  }

  async inviteToRoom(id: number, userIds: string[]): Promise<{ invited: string[]; errors: Array<{ userId: string; error: string }> }> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${id}/invite`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ userIds }),
    });
    return this.handleResponse<{ invited: string[]; errors: Array<{ userId: string; error: string }> }>(response);
  }

  getRoomCalendarUrl(id: number): string {
    return `${BASE_URL}/api/watch-parties/${id}/calendar.ics`;
  }

  // Recurring room instances
  async getRoomInstances(parentId: number, params?: { limit?: number; offset?: number }): Promise<PaginatedResponse<RoomResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    const qs = query.toString();
    const response = await fetch(`${BASE_URL}/api/watch-parties/${parentId}/instances${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PaginatedResponse<RoomResponse>>(response);
  }

  // Home feed rooms (dedicated endpoints that include relevance scoring)
  async getHomeLiveNowRooms(limit = 10): Promise<RoomResponse[]> {
    const response = await fetch(`${BASE_URL}/api/home/rooms-live-now?limit=${limit}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomResponse[]>(response);
  }

  async getHomeStartingSoonRooms(limit = 10): Promise<RoomResponse[]> {
    const response = await fetch(`${BASE_URL}/api/home/rooms-starting-soon?limit=${limit}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomResponse[]>(response);
  }

  // Master profile rooms
  async getMasterRooms(masterProfileId: number, params?: { status?: string; limit?: number; offset?: number }): Promise<PaginatedResponse<RoomResponse>> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));
    const qs = query.toString();
    // CORE-1286 — с токеном, если он есть: сервер отдаёт владельцу его
    // приватные и подписочные сессии, подписчику — подписочные. Анонимный
    // запрос видел только публичные, и мастер не находил свою же сессию.
    const response = await fetch(`${BASE_URL}/api/masterprofile/${masterProfileId}/rooms${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<PaginatedResponse<RoomResponse>>(response);
  }

  // Practice detail — rooms using this practice
  async getPracticeRooms(videoPracticeId: number, limit = 10): Promise<RoomResponse[]> {
    const response = await fetch(`${BASE_URL}/api/content/videos/${videoPracticeId}/rooms?limit=${limit}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomResponse[]>(response);
  }

  // Practice rooms archive — ended public master live-events with recordings
  async fetchArchiveRooms(filters: { direction?: string; date?: string }): Promise<RoomResponse[]> {
    const params = new URLSearchParams({ tab: 'archive' });
    if (filters.direction) params.set('direction', filters.direction);
    if (filters.date) params.set('date', filters.date);
    const response = await fetch(`${BASE_URL}/api/practice-rooms?${params.toString()}`, {
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<RoomResponse[]>(response);
  }

  // Practice rooms calendar — month-range dot indicators (upcoming + archive)
  async fetchCalendarDots(from: string, to: string): Promise<CalendarDot[]> {
    const params = new URLSearchParams({ from, to });
    const response = await fetch(`${BASE_URL}/api/practice-rooms/calendar?${params.toString()}`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    return this.handleResponse<CalendarDot[]>(response);
  }

  // Practice rooms stats — peak/views/messages for the immersive ended layout.
  // Intentionally bypasses this.handleResponse: a missing-stats 404 must
  // degrade gracefully to nulls (so the stat-bar renders «—» instead of
  // crashing the screen), and stats aren't security-sensitive so we don't
  // want handleResponse's 401 auto-redirect either.
  async fetchRoomStats(roomId: number): Promise<RoomStats> {
    const response = await fetch(`${BASE_URL}/api/practice-rooms/${roomId}/stats`, {
      method: 'GET',
      headers: await this.getHeaders(false),
    });
    if (!response.ok) {
      return { peakParticipants: null, viewCount: null, messageCount: null };
    }
    const text = await response.text();
    return text ? JSON.parse(text) : { peakParticipants: null, viewCount: null, messageCount: null };
  }

  // ─── CORE-1760 / CORE-1764 — the Live ending page (read-only) ───
  // The token decides the viewer role (organizer / host / participant), so it goes
  // whenever there is one; a guest without a token gets the same answer as a
  // participant (the room is open by its invite code), so a 401 here must never
  // bounce anyone to /login.
  async getRoomEnding(code: string): Promise<SessionEndingResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${encodeURIComponent(code)}/ending`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    return this.handleResponse<SessionEndingResponse>(response, true);
  }

  async getRoomEndingParticipants(code: string, page: { limit: number; offset: number }): Promise<SessionEndingParticipantsResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${encodeURIComponent(code)}/ending/participants?limit=${page.limit}&offset=${page.offset}`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    return this.handleResponse<SessionEndingParticipantsResponse>(response, true);
  }

  async getLiveEnding(liveId: number): Promise<SessionEndingResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/ending`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    return this.handleResponse<SessionEndingResponse>(response, true);
  }

  async getLiveEndingParticipants(liveId: number, page: { limit: number; offset: number }): Promise<SessionEndingParticipantsResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/ending/participants?limit=${page.limit}&offset=${page.offset}`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    return this.handleResponse<SessionEndingParticipantsResponse>(response, true);
  }

  // ─── CORE-1761 — the rating of the experience (backend CORE-1841, on dev since 26.10.2026) ───
  // A 401 here is a guest (the page sends them to sign in with returnTo), never a reason
  // to bounce to /login; a 404 is a session this account has no access to (the same rule
  // as `/ending`) or a stand without the route — either way the card stays hidden.
  // PUT: 400 value outside 1..5, 403 did not attend, 409 another value already saved
  // (the body carries it) or the session is not finished yet; the same value again is 200.
  //
  // CORE-1761 (QA 28.10.2026) — a guest who attended by the link and signed in on the
  // ending page holds the account's token now, but the visit happened under the anonymous
  // identity (`X-Anon-User-Id` → the spectator row). `getHeaders` drops the anonymous id
  // once a token is attached; the rating routes send both, so the server can tie that
  // visit to the account (contract ask to the backend, see docs/results/CORE-1761-ending-rating.md).
  // The id is a browser-local token of the same person, never another user's.
  private async ratingHeaders(): Promise<Record<string, string>> {
    const headers = (await this.getHeaders()) as Record<string, string>;
    if (!headers['X-Anon-User-Id']) {
      const anonId = await storage.getAnonUserId().catch(() => null);
      if (anonId) headers['X-Anon-User-Id'] = anonId;
    }
    return headers;
  }

  async getRoomEndingRating(code: string): Promise<SessionRatingResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${encodeURIComponent(code)}/ending/rating`, {
      method: 'GET',
      headers: await this.ratingHeaders(),
    });
    return this.handleResponse<SessionRatingResponse>(response, true);
  }

  async putRoomEndingRating(code: string, value: SessionRatingValue): Promise<SessionRatingResponse> {
    const response = await fetch(`${BASE_URL}/api/watch-parties/${encodeURIComponent(code)}/ending/rating`, {
      method: 'PUT',
      headers: await this.ratingHeaders(),
      body: JSON.stringify({ value }),
    });
    return this.handleResponse<SessionRatingResponse>(response, true);
  }

  async getLiveEndingRating(liveId: number): Promise<SessionRatingResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/ending/rating`, {
      method: 'GET',
      headers: await this.ratingHeaders(),
    });
    return this.handleResponse<SessionRatingResponse>(response, true);
  }

  async putLiveEndingRating(liveId: number, value: SessionRatingValue): Promise<SessionRatingResponse> {
    const response = await fetch(`${BASE_URL}/api/lives/${liveId}/ending/rating`, {
      method: 'PUT',
      headers: await this.ratingHeaders(),
      body: JSON.stringify({ value }),
    });
    return this.handleResponse<SessionRatingResponse>(response, true);
  }

  // ─── Unsplash ───

  async getUnsplashLiveCovers(): Promise<UnsplashImage[]> {
    const response = await fetch(`${BASE_URL}/api/Unsplash/live-covers`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return this.handleResponse<UnsplashImage[]>(response);
  }

  async trackUnsplashDownload(downloadLocationUrl: string): Promise<void> {
    await fetch(`${BASE_URL}/api/Unsplash/track-download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ downloadLocationUrl }),
    });
  }

  // ─── Reports (CORE-26) ───
  // Abuse report on any entity («Пожаловаться»). Auth-only on the BE — guests
  // get a 401, so callers gate the entry point behind the auth CTA.
  async createReport(data: import('../types/reports').CreateReportRequest): Promise<import('../types/reports').ReportResponse> {
    const response = await fetch(`${BASE_URL}/api/reports`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<import('../types/reports').ReportResponse>(response);
  }

  // === CORE-871 — платные программы мастеров ===

  /**
   * CORE-889 — «Созданное» глазами владельца: страница по фильтру статуса плюс
   * каунтеры табов и текущее состояние тумблера видимости раздела.
   *
   * Каунтеры приходят вместе со страницей намеренно: «Черновики (3)» нельзя
   * вывести из выборки, которую сами же и постранично забрали.
   */
  async getOwnPrograms(params?: {
    status?: 'draft' | 'published' | 'archived' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<import('../types/programs').MasterProgramList> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    query.append('limit', String(params?.limit ?? 20));
    query.append('offset', String(params?.offset ?? 0));
    const response = await fetch(`${BASE_URL}/api/master/programs?${query.toString()}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').MasterProgramList>(response);
  }

  /**
   * CORE-889 — вкладка «Программы» в публичном профиле мастера (Макет 8).
   *
   * При выключенном тумблере посетителю приходит `sectionVisible: false` и
   * пустой список — вкладку рисовать не нужно вовсе. Владельцу его программы
   * приходят и в этом случае: он смотрит на витрину, которую пока видит один.
   */
  async getPublicPrograms(masterProfileId: number, limit = 20, offset = 0):
    Promise<import('../types/programs').PublicProgramList> {
    const response = await fetch(
      `${BASE_URL}/api/masterprofile/${masterProfileId}/programs?limit=${limit}&offset=${offset}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/programs').PublicProgramList>(response);
  }

  /**
   * CORE-1011 — программа до покупки (Макет 9): витрина, состав и — для
   * авторизованного — его собственное отношение к ней, одним запросом.
   *
   * Токен необязателен, поэтому 401 здесь не должен уводить на /login: страница
   * программы существует и для анонима, просто без раздела `access`.
   *
   * 404 приходит на три разных случая (нет такой, черновик, витрина мастера
   * закрыта) намеренно: разный ответ превратил бы перебор id в способ узнать,
   * какие черновики пишет мастер.
   */
  async getPublicProgramPage(programId: number):
    Promise<import('../types/programs').PublicProgramPage> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/public`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').PublicProgramPage>(response, true);
  }

  /**
   * CORE-877 / CORE-929 — начать покупку программы (§6 ТЗ).
   *
   * Второе нажатие «Купить» при уже начатой покупке возвращает ТУ ЖЕ строку, а
   * не заводит вторую: человек, кликнувший дважды, не должен получить два счёта.
   * Поэтому повтор — обычный успех, и отличать его на фронте незачем.
   *
   * Отказы приходят исключениями: 404 — программы нет; 409 — снята с продажи,
   * продажи на паузе, скрыта модерацией, своя собственная, без цены, либо
   * доступ уже есть. Последний случай единственный, у которого в теле лежит
   * `purchase`, — по нему он и опознаётся: свободного текста сообщения на
   * ветвление не хватает, а кода бэк не отдаёт.
   */
  async startProgramPurchase(programId: number):
    Promise<import('../types/programs').ProgramPurchase> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/purchase`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramPurchase>(response);
  }

  /**
   * CORE-877 — своя покупка. Чужая отвечает 404: чей это платёж и на что,
   * посторонним знать незачем.
   *
   * Это же и опрос исхода: платёжный контур сообщает его бэкенду, а не
   * вкладке, из которой человек ушёл платить, поэтому единственный способ
   * узнать «оплатилось ли» — спросить свою покупку.
   */
  async getProgramPurchase(purchaseId: number):
    Promise<import('../types/programs').ProgramPurchase> {
    const response = await fetch(`${BASE_URL}/api/programs/purchases/${purchaseId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramPurchase>(response);
  }

  /** CORE-1533 — состояние подписочного доступа Agapēlla. */
  async getAgapellaSubscription():
    Promise<import('../types/agapellaSubscription').AgapellaAccessState> {
    const response = await fetch(`${BASE_URL}/api/agapella-subscription`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/agapellaSubscription').AgapellaAccessState>(response);
  }

  /**
   * CORE-1538 — начать пробный доступ: 7 дней, один раз на аккаунт, без
   * платёжных данных. 409 `trial_already_used`, 404 `subscription_disabled`.
   */
  async startAgapellaTrial():
    Promise<import('../types/agapellaSubscription').AgapellaAccessState> {
    const response = await fetch(`${BASE_URL}/api/agapella-subscription/trial`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/agapellaSubscription').AgapellaAccessState>(response);
  }

  /** CORE-1534 — продаётся ли подписка, цена и период. */
  async getAgapellaSubscriptionOffer():
    Promise<import('../types/agapellaSubscription').AgapellaSubscriptionOffer> {
    const response = await fetch(`${BASE_URL}/api/agapella-subscription/offer`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/agapellaSubscription').AgapellaSubscriptionOffer>(response);
  }

  /**
   * CORE-1534 — оформить оплату подписки. Повторный вызов при неоплаченной
   * покупке отдаёт её же. 404 `subscription_disabled`, 409 `not_priced`.
   * CORE-1951 — `receiptContact`: почта или телефон для чека. Без него у
   * аккаунта без почты — 400 `RECEIPT_CONTACT_REQUIRED`; не почта и не
   * телефон — 400 `RECEIPT_CONTACT_INVALID`.
   * CORE-2075 — `usePoints`: списать баллы (флаг `points_payment`). Хватило на
   * всю цену — покупка сразу `paid` и без `paymentUrl`.
   */
  async startAgapellaSubscriptionPurchase(receiptContact?: string | null, usePoints = false):
    Promise<import('../types/agapellaSubscription').AgapellaSubscriptionPurchase> {
    const body = purchaseRequestBody(receiptContact ?? null, usePoints);
    const response = await fetch(`${BASE_URL}/api/agapella-subscription/purchase`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return this.handleResponse<import('../types/agapellaSubscription').AgapellaSubscriptionPurchase>(response);
  }

  /** CORE-1534 — своя покупка подписки; это же и опрос исхода оплаты. */
  async getAgapellaSubscriptionPurchase(purchaseId: number):
    Promise<import('../types/agapellaSubscription').AgapellaSubscriptionPurchase> {
    const response = await fetch(`${BASE_URL}/api/agapella-subscription/purchases/${purchaseId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/agapellaSubscription').AgapellaSubscriptionPurchase>(response);
  }

  /**
   * CORE-1534 — обменять одноразовый код из приложения на токен. Код живёт
   * минуты и срабатывает один раз; неудача — 401 `handoff_invalid`.
   */
  /**
   * CORE-1535 — одноразовый код входа для перехода из приложения на страницу
   * оплаты в браузере (живёт 2 минуты, срабатывает один раз). Из режима
   * «войти как» — 403.
   */
  async issueLoginHandoff(): Promise<{ code: string; expiresAtUtc: string }> {
    const response = await fetch(`${BASE_URL}/api/auth/handoff`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ code: string; expiresAtUtc: string }>(response);
  }

  async exchangeLoginHandoff(code: string): Promise<LoginResponse> {
    const response = await fetch(`${BASE_URL}/api/auth/handoff/exchange`, {
      method: 'POST',
      headers: await this.getHeaders(false),
      body: JSON.stringify({ code }),
    });
    return this.handleResponse<LoginResponse>(response, true);
  }

  /**
   * CORE-877 — есть ли у меня доступ к программе. Отвечает и тем, у кого он
   * закончился: «не покупал» и «срок вышел» — разные ответы (§7 ТЗ).
   */
  async getProgramAccess(programId: number):
    Promise<import('../types/programs').ProgramAccess> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/access`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramAccess>(response);
  }

  // === CORE-1002 / CORE-878 — купленная программа: библиотека и прохождение ===
  //
  // Все пять ручек закрыты entitlement'ом на сервере: без доступа `content`
  // отвечает 403, а `media` — 403 с телом `{ message, access }`. Фронт эти
  // отказы показывает, но не изображает: прямая ссылка на файл мимо интерфейса
  // не откроется точно так же.

  /**
   * CORE-1002 — что человек купил. Единица выдачи — ПРОГРАММА, а не платёж:
   * купленная дважды приходит одной строкой (в отличие от финансовой истории,
   * где две оплаты обязаны остаться двумя строками).
   *
   * `status` делит библиотеку на действующее и завершённое. Каунтеры в ответе —
   * по всей библиотеке, а не по странице, поэтому подписи сегментов не врут при
   * пагинации.
   */
  async getMyPrograms(params?: { status?: 'active' | 'expired'; limit?: number; offset?: number }):
    Promise<import('../types/programs').MyProgramList> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const response = await fetch(
      `${BASE_URL}/api/programs/my${qs.toString() ? `?${qs}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/programs').MyProgramList>(response);
  }

  /** Состав купленной программы с прогрессом по каждому материалу. 403 — доступа нет. */
  async getProgramRunContent(programId: number):
    Promise<import('../types/programs').ProgramRunContent> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/content`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramRunContent>(response);
  }

  /** Файлы программы — вкладка «Материалы». Видео и практики сюда не попадают. */
  async getProgramMaterials(programId: number):
    Promise<import('../types/programs').ProgramMaterialList> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/materials`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramMaterialList>(response);
  }

  /** Вкладка «О программе»: витрина плюс свой доступ и прогресс, одним запросом. */
  async getProgramRunAbout(programId: number):
    Promise<import('../types/programs').ProgramRunAbout> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/about`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramRunAbout>(response);
  }

  /**
   * Ссылка на закрытый материал — по одному за раз и только по требованию.
   *
   * Не запрашивается пачкой на весь состав: у ссылок бывает срок
   * (`urlsExpireAtUtc`), и набранные заранее протухают ровно к тому моменту,
   * когда человек до них дойдёт.
   */
  async getProgramItemMedia(programId: number, itemId: number):
    Promise<import('../types/programs').ProgramItemMedia> {
    const response = await fetch(
      `${BASE_URL}/api/programs/${programId}/items/${itemId}/media`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/programs').ProgramItemMedia>(response);
  }

  /**
   * Отчёт о просмотре либо ручная отметка (CORE-997).
   *
   * Что именно слать — решает сам материал: у файла `manualOnly`, и он ждёт
   * `completed`; у видео `completed` отвергается с 400 («отмечается просмотром,
   * а не вручную»), ему нужны секунды. Считает завершение сервер — ответ
   * возвращает пересчитанный прогресс и материала, и программы.
   */
  async reportProgramItemProgress(
    programId: number,
    itemId: number,
    input: import('../types/programs').ProgramProgressInput,
  ): Promise<import('../types/programs').ProgramProgressResult> {
    const response = await fetch(
      `${BASE_URL}/api/programs/${programId}/items/${itemId}/progress`,
      { method: 'POST', headers: await this.getHeaders(true), body: JSON.stringify(input) },
    );
    return this.handleResponse<import('../types/programs').ProgramProgressResult>(response);
  }

  /** CORE-889 — тумблер «Показывать раздел в профиле». Доступ уже купивших он не трогает. */
  async setProgramsSectionVisible(visible: boolean): Promise<{ visible: boolean }> {
    const response = await fetch(`${BASE_URL}/api/master/programs/section-visibility`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ visible }),
    });
    return this.handleResponse<{ visible: boolean }>(response);
  }

  /**
   * CORE-984 — создать черновик программы; поля шага 1 можно отправить сразу.
   *
   * CORE-993: тип продукта выбирается ровно здесь и больше нигде — сменить его
   * у заведённого черновика нечем, и это не упущение: у проданного продукта
   * тип менять уже нельзя, а у незаведённого его ещё не существует.
   * Неотправленный `productType` сервер понимает как «программа».
   */
  async createProgramDraft(
    patch?: import('../types/programs').ProgramDraftPatch,
    productType?: import('../types/programs').ProgramProductType,
  ): Promise<import('../types/programs').MasterProgram> {
    const response = await fetch(`${BASE_URL}/api/master/programs`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ ...(patch ?? {}), ...(productType ? { productType } : {}) }),
    });
    return this.handleResponse<import('../types/programs').MasterProgram>(response);
  }

  /** CORE-984 — программа глазами владельца, в любом её состоянии. */
  async getProgram(programId: number): Promise<import('../types/programs').MasterProgram> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').MasterProgram>(response);
  }

  /**
   * CORE-984 — сохранить один шаг визарда. Отправлять надо только то, чем шаг
   * владеет: отсутствующее поле сервер оставляет как есть.
   */
  async saveProgramDraft(programId: number, patch: import('../types/programs').ProgramDraftPatch):
    Promise<import('../types/programs').MasterProgram> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify(patch),
    });
    return this.handleResponse<import('../types/programs').MasterProgram>(response);
  }

  /**
   * CORE-874 — загрузить обложку программы и получить её url, который потом
   * уходит в `coverImageUrl` обычным PATCH'ем черновика.
   *
   * Отдельным вызовом, а не полем в PATCH: шаг 1 автосохраняется по ходу
   * набора, и картинка в каждом таком сохранении переливалась бы заново.
   * Заголовок Content-Type не ставим — fetch сам проставит multipart-границу.
   */
  async uploadProgramCover(file: File | { uri: string; name: string; type: string }): Promise<{ url: string }> {
    const token = await storage.getToken();
    const formData = new FormData();
    formData.append('file', file as any);
    const response = await fetch(`${BASE_URL}/api/master/programs/cover`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    return this.handleResponse<{ url: string }>(response);
  }

  /**
   * CORE-984 / CORE-1100 — удалить программу безвозвратно: и черновик, и
   * опубликованный продукт, который ни разу не покупали.
   *
   * Единственный отказ — 409 с `code: 'PROGRAM_HAS_PURCHASES'`: проданное не
   * удаляется ни в каком статусе, его убирают в архив (`archiveProgram`). Чтобы
   * не приводить мастера к этому отказу нажатием, спрашивают заранее —
   * `getProgramLifecycle().canHardDelete`.
   */
  async deleteProgram(programId: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  /**
   * CORE-879 / CORE-988 — завести Live-сессию прямо в разделе программы.
   *
   * Live-сессия создаётся обычная, в общей таблице: отдельного «программной Live-сессии»
   * в бэке нет. Программа получает на него ссылку тем же способом, что и на
   * любой другой материал, и в ответ приходит ВЕСЬ состав целиком — как у
   * остальных мутаций состава, чтобы дерево не пересобирать из кусков.
   *
   * `visibility` по умолчанию `program`: мастер, заводящий Live-сессию из редактора
   * платного продукта, продаёт её, а не публикует.
   */
  async createProgramLive(
    programId: number,
    sectionId: number,
    body: {
      title: string;
      startDateTimeUtc: string;
      durationMinutes?: number;
      description?: string;
      visibility?: string;
    },
  ): Promise<import('../types/programs').ProgramItemsAdded> {
    const response = await fetch(
      `${BASE_URL}/api/master/programs/${programId}/sections/${sectionId}/live`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    return this.handleResponse<import('../types/programs').ProgramItemsAdded>(response);
  }

  // === CORE-882 / CORE-991 — возвраты и жизнь программы после продаж ===

  /**
   * Попросить вернуть деньги за покупку. Причина необязательна.
   *
   * Заявку разбирает человек: автоматического возврата нет, и срока «через
   * столько-то вернём» тоже — правила возврата ждут юридического утверждения.
   * Поэтому ответ — заявка со статусом, а не решение.
   *
   * Повторный вызов при незакрытой заявке возвращает её же.
   */
  async requestProgramRefund(purchaseId: number, reason?: string):
    Promise<import('../types/programLifecycle').ProgramRefundRequest> {
    const response = await fetch(
      `${BASE_URL}/api/user/finance/purchases/${purchaseId}/refund-request`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason ?? null }),
      },
    );
    return this.handleResponse<import('../types/programLifecycle').ProgramRefundRequest>(response);
  }

  /**
   * CORE-1743 — пакет документов мастера вошедшего пользователя (CORE-1742):
   * статус оформления и точные редакции документов. Только свой: id в запросе нет.
   */
  async getMasterOnboarding(): Promise<import('../types/masterOnboarding').MasterOnboardingState> {
    const response = await fetch(`${BASE_URL}/api/master-onboarding`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/masterOnboarding').MasterOnboardingState>(response);
  }

  /**
   * CORE-1743 — подписать документ пакета ровно в выданной редакции. Ответ —
   * состояние пакета; после последней подписи `status` = `active`. 409
   * `master_document_edition_mismatch`, 404 `master_package_not_found` /
   * `master_document_not_in_package`.
   */
  async signMasterDocument(
    documentId: string,
    edition: { version: string; variant: string; sha256: string },
  ): Promise<import('../types/masterOnboarding').MasterOnboardingState> {
    const response = await fetch(
      `${BASE_URL}/api/master-onboarding/documents/${encodeURIComponent(documentId)}/sign`,
      {
        method: 'POST',
        headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
        body: JSON.stringify(edition),
      },
    );
    return this.handleResponse<import('../types/masterOnboarding').MasterOnboardingState>(response);
  }

  /**
   * CORE-1743 — запустить налоговую проверку по статусу и ИНН, которые кандидат
   * уже положил в контур (форма реквизитов CORE-1053). Ответ — состояние пакета.
   * 409 `master_onboarding_not_open` — пакета нет или он уже подписан.
   */
  async runMasterTaxCheck(): Promise<import('../types/masterOnboarding').MasterOnboardingState> {
    const response = await fetch(`${BASE_URL}/api/master-onboarding/tax-check`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/masterOnboarding').MasterOnboardingState>(response);
  }

  /** CORE-1926 — все редакции реестра CORE-1660 без текстов. Публичный. */
  async getLegalEditions(): Promise<import('../legal/editionHistory').LegalEditionSummary[]> {
    const response = await fetch(`${BASE_URL}/api/legal/editions`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../legal/editionHistory').LegalEditionSummary[]>(response);
  }

  /** CORE-1660 — полный текст одной редакции из реестра. Публичный. */
  async getLegalEdition(documentId: string, version: string, variant: string):
    Promise<import('../types/masterOnboarding').LegalEditionText> {
    const path = [documentId, version, variant].map(encodeURIComponent).join('/');
    const response = await fetch(`${BASE_URL}/api/legal/editions/${path}`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/masterOnboarding').LegalEditionText>(response);
  }

  /** Мои заявки на возврат, новые сверху. */
  async getMyRefundRequests(limit = 20, offset = 0):
    Promise<import('../types/programLifecycle').RefundRequestList> {
    const response = await fetch(
      `${BASE_URL}/api/user/finance/refund-requests?limit=${limit}&offset=${offset}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/programLifecycle').RefundRequestList>(response);
  }

  /**
   * CORE-991 — что стоит на кону: сколько человек купили программу, сколько
   * занимаются сейчас, есть ли незакрытые возвраты и что из действий вообще
   * доступно. Отдельным запросом намеренно: предупреждение, приходящее в ответ
   * на действие, приходит поздно.
   */
  async getProgramLifecycle(programId: number): Promise<import('../types/programs').ProgramLifecycle> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/lifecycle`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramLifecycle>(response);
  }

  /**
   * CORE-991 — убрать в архив: из публичной выдачи программа уходит и больше не
   * продаётся, но покупки, финансовая история и доступ купивших остаются
   * нетронутыми. Это то, что предлагается вместо удаления проданной программы.
   */
  async archiveProgram(programId: number): Promise<void> {
    return this.programLifecycleAction(programId, 'archive');
  }

  /**
   * Приостановить или вернуть продажи, архивировать или вернуть из архива.
   *
   * Четыре действия одним методом, потому что у них общий контракт: POST по
   * имени действия, тело не нужно, ответ пустой. `archiveProgram` выше оставлен
   * как имя, под которым архивацию уже зовут экраны из dev.
   */
  async programLifecycleAction(
    programId: number,
    action: 'pause-sales' | 'resume-sales' | 'archive' | 'unarchive',
  ): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/${action}`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  /**
   * Отменить программу — отдельный сценарий вместо запрещённого удаления.
   * Причина обязательна: она уходит покупателям в уведомлении.
   */
  async cancelProgram(programId: number, reason: string):
    Promise<import('../types/programLifecycle').ProgramCancelResult> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/cancel`, {
      method: 'POST',
      headers: { ...(await this.getHeaders(true)), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    return this.handleResponse<import('../types/programLifecycle').ProgramCancelResult>(response);
  }

  // === CORE-880 / CORE-989 — Финансы → Покупки ===

  /**
   * История покупок владельца токена: платежи всех статусов, новые сверху.
   *
   * Единица выдачи здесь — ПЛАТЁЖ, а не программа. Одна и та же программа,
   * купленная дважды, придёт двумя строками, и это верно: история денег не
   * схлопывается по товару. Библиотека купленного — другая ручка и другой
   * экран.
   *
   * Параметра «чей» нет вовсе: чужая финансовая история недоступна не потому,
   * что мы проверяем права, а потому, что спросить о ней нечем.
   */
  async getPurchaseHistory(limit = 20, offset = 0):
    Promise<import('../types/programs').PurchaseHistoryList> {
    const response = await fetch(
      `${BASE_URL}/api/user/finance/purchases?limit=${limit}&offset=${offset}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/programs').PurchaseHistoryList>(response);
  }

  // === CORE-881 / CORE-990 — «Финансы → Продажи» мастера ===
  //
  // Профиль берётся из токена, а не из запроса: подставить чужой нечем.

  /** Продажи по программам за период: `7d` | `30d` | `all`. */
  /**
   * CORE-1141 — продуктовая аналитика мастера: аудитория и контент.
   *
   * Соседний вызов к `getMasterSales`, и сознательно не он: там деньги, здесь
   * штуки. Ни одной суммы этот ответ не содержит — источник правды по деньгам
   * один, и он в финансовом разделе.
   */
  async getMasterAnalytics(params?: { period?: string }):
    Promise<import('../types/masterAnalytics').MasterAnalyticsReport> {
    const qs = new URLSearchParams();
    if (params?.period) qs.set('period', params.period);
    const response = await fetch(
      `${BASE_URL}/api/master/analytics${qs.toString() ? `?${qs}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/masterAnalytics').MasterAnalyticsReport>(response);
  }

  async getMasterSales(params?: { period?: string; limit?: number; offset?: number }):
    Promise<import('../types/masterSales').MasterSalesReport> {
    const qs = new URLSearchParams();
    if (params?.period) qs.set('period', params.period);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const response = await fetch(
      `${BASE_URL}/api/master/finance/sales${qs.toString() ? `?${qs}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/masterSales').MasterSalesReport>(response);
  }

  /**
   * Операции за период — продажи и возвраты, новые сверху.
   *
   * `programId` сужает список до одной программы: это тот же список, в который
   * ведёт переход «Продажи» из карточки в «Созданном».
   */
  async getMasterSalesOperations(params?: {
    period?: string; programId?: number; limit?: number; offset?: number;
  }): Promise<import('../types/masterSales').SalesOperationList> {
    const qs = new URLSearchParams();
    if (params?.period) qs.set('period', params.period);
    if (params?.programId != null) qs.set('programId', String(params.programId));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const response = await fetch(
      `${BASE_URL}/api/master/finance/operations${qs.toString() ? `?${qs}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/masterSales').SalesOperationList>(response);
  }

  // === CORE-883 / CORE-992 — лента уведомлений ===
  //
  // Лента общая для платформы, а не для программ: программы — первый её
  // поставщик. Поэтому методы названы по ленте, а не по программам, и новый
  // поставщик событий не потребует второго набора ручек.

  /** Лента, новые сверху. `unreadOnly` оставляет непрочитанные. */
  async getNotifications(params?: { limit?: number; offset?: number; unreadOnly?: boolean }):
    Promise<import('../types/notifications').NotificationFeed> {
    const qs = new URLSearchParams();
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    if (params?.unreadOnly) qs.set('unreadOnly', 'true');
    const response = await fetch(
      `${BASE_URL}/api/notifications${qs.toString() ? `?${qs}` : ''}`,
      { method: 'GET', headers: await this.getHeaders(true) },
    );
    return this.handleResponse<import('../types/notifications').NotificationFeed>(response);
  }

  /** Счётчик непрочитанного — для бейджа в шапке. */
  async getNotificationsUnreadCount(): Promise<{ count: number }> {
    const response = await fetch(`${BASE_URL}/api/notifications/unread-count`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ count: number }>(response);
  }

  /** Отметить прочитанным. 404 — уведомление не найдено или чужое. */
  async markNotificationRead(id: number): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/notifications/${id}/read`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  /** Отметить прочитанным всё. Возвращает, сколько отмечено. */
  async markAllNotificationsRead(): Promise<{ count: number }> {
    const response = await fetch(`${BASE_URL}/api/notifications/read-all`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<{ count: number }>(response);
  }

  // === CORE-985 / CORE-875 — состав программы ===
  //
  // Каждый мутирующий вызов возвращает ВЕСЬ состав целиком, а не дельту. Это
  // сделано ради фронта: дерево не нужно пересобирать из кусков и незачем
  // расходиться с сервером — ответ кладётся в стейт как есть.

  /** Весь состав программы: разделы и материалы по порядку. */
  async getProgramContent(programId: number): Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/content`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /** Добавить раздел в конец. Название необязательно — шаг 2 автосохраняется. */
  async addProgramSection(programId: number, title?: string | null):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/sections`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ title: title ?? null }),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  async renameProgramSection(programId: number, sectionId: number, title: string | null):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/sections/${sectionId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ title }),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /** Удалить раздел вместе со ссылками. Исходные практики и эфиры не трогаются. */
  async deleteProgramSection(programId: number, sectionId: number):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/sections/${sectionId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /**
   * Порядок разделов. Присылать нужно ВСЕ id целиком, в нужном порядке —
   * частичный список сервер отбивает, ничего не применяя.
   */
  async reorderProgramSections(programId: number, ids: number[]):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/sections/order`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ ids }),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /** «Добавить выбранное» — одним батчем. Уже добавленное вернётся в `skipped`. */
  async addProgramItems(
    programId: number,
    sectionId: number,
    items: import('../types/programs').ProgramItemInput[],
  ): Promise<import('../types/programs').ProgramItemsAdded> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/sections/${sectionId}/items`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ items }),
    });
    return this.handleResponse<import('../types/programs').ProgramItemsAdded>(response);
  }

  /** Убрать материал ИЗ ПРОГРАММЫ. Исходник остаётся в «Практиках» и «Медиа». */
  async removeProgramItem(programId: number, itemId: number):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/items/${itemId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /**
   * Порядок материалов раздела. Id из соседнего раздела той же программы
   * принимаются и переезжают сюда — перетаскивание между разделами это тот же
   * жест, и отдельного «переместить» нет намеренно.
   */
  async reorderProgramItems(programId: number, sectionId: number, ids: number[]):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(
      `${BASE_URL}/api/master/programs/${programId}/sections/${sectionId}/items/order`, {
        method: 'PUT',
        headers: await this.getHeaders(true),
        body: JSON.stringify({ ids }),
      });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /** Правая колонка: существующий контент мастера по типу, с поиском. */
  async getProgramContentCandidates(programId: number, params: {
    type: import('../types/programs').ProgramItemKind;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<import('../types/programs').ProgramContentCandidates> {
    const query = new URLSearchParams({ type: params.type });
    if (params.search) query.append('search', params.search);
    if (params.limit !== undefined) query.append('limit', String(params.limit));
    if (params.offset !== undefined) query.append('offset', String(params.offset));
    const response = await fetch(
      `${BASE_URL}/api/master/programs/${programId}/content/candidates?${query.toString()}`, {
        method: 'GET',
        headers: await this.getHeaders(true),
      });
    return this.handleResponse<import('../types/programs').ProgramContentCandidates>(response);
  }

  /**
   * CORE-994 — выбрать практику, которую продаёт платная практика.
   *
   * Единственная дверь к материалу этого продукта: списочное «Добавить
   * выбранное» он не принимает вовсе. Возвращается состав целиком — тот же,
   * что у программы: один служебный раздел с одним материалом внутри.
   *
   * Отказы приходят с кодом: `PROGRAM_NOT_PAID_PRACTICE` — это программа, а не
   * практика; `PROGRAM_HAS_BUYERS` — материал уже купили, и подменить его
   * задним числом нельзя.
   */
  async setProgramPaidPractice(programId: number, practiceId: number):
    Promise<import('../types/programs').ProgramContent> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/practice`, {
      method: 'PUT',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ practiceId }),
    });
    return this.handleResponse<import('../types/programs').ProgramContent>(response);
  }

  /**
   * CORE-875 — загрузить файл-материал. НЕ прикрепляет его к программе:
   * вернувшиеся поля кладутся в `addProgramItems`, поэтому загрузка и
   * добавление — один жест в интерфейсе и два факта в данных.
   *
   * Заголовок Content-Type не ставим — fetch сам проставит multipart-границу.
   */
  async uploadProgramFile(
    programId: number,
    file: File | { uri: string; name: string; type: string },
  ): Promise<import('../types/programs').ProgramFileUpload> {
    const token = await storage.getToken();
    const formData = new FormData();
    formData.append('file', file as any);
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/files`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    return this.handleResponse<import('../types/programs').ProgramFileUpload>(response);
  }

  // === CORE-996 / CORE-995 — чат программы (§9 ТЗ) ===

  /**
   * Войти в чат программы.
   *
   * Отказы разбираются здесь, а не отдаются в catch: у вкладки чата «мастер
   * выключил чат» и «доступ закончился» — не сбои, а состояния, которые она
   * обязана показать. Через исключение они были бы неотличимы от обрыва связи.
   *
   * 404 сервер отдаёт и на выключенный чат, и на несуществующую программу —
   * различать их снаружи незачем и нечем: для читателя это одна и та же
   * картинка «чата здесь нет», а подтверждать существование чужого черновика
   * бэк намеренно не хочет.
   */
  async getProgramChat(programId: number):
    Promise<import('../types/programs').ProgramChatOutcome> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/chat`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });

    if (response.status === 404) return { outcome: 'noChat' };

    if (response.status === 403) {
      let access: import('../types/programs').ProgramAccess | null = null;
      try {
        const body = await response.json();
        access = body?.access ?? null;
      } catch {
        // Тело отказа не разобралось — вкладка всё равно должна показать
        // «доступа нет», просто без уточнения, был ли он когда-то.
      }
      return { outcome: 'noAccess', access };
    }

    const chat = await this.handleResponse<import('../types/programs').ProgramChatEntry>(response);
    return { outcome: 'ready', chat };
  }

  /**
   * Мастер приглушает участника чата программы до указанного момента.
   * `mutedUntilUtc = null` снимает запрет.
   *
   * Именно приглушает, а не выгоняет: программа оплачена, и отбирать за резкое
   * сообщение доступ к материалам нельзя.
   */
  async muteProgramChatParticipant(
    programId: number,
    userId: string,
    mutedUntilUtc: string | null,
  ): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/programs/${programId}/chat/mute`, {
      method: 'POST',
      headers: await this.getHeaders(true),
      body: JSON.stringify({ userId, mutedUntilUtc }),
    });
    if (!response.ok) await this.handleResponse<void>(response);
  }

  // === CORE-986 / CORE-910 — готовность и публикация программы (шаг 5) ===

  /**
   * Чего не хватает программе до публикации. Перечень считает сервер — фронт
   * его только показывает и группирует по шагам.
   */
  async getProgramReadiness(programId: number): Promise<import('../types/programs').ProgramReadiness> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/readiness`, {
      method: 'GET',
      headers: await this.getHeaders(true),
    });
    return this.handleResponse<import('../types/programs').ProgramReadiness>(response);
  }

  /**
   * Опубликовать программу. Тела у запроса нет: отложенной публикации в
   * продукте не существует, и передавать дату некуда.
   *
   * Отказ по неготовности (422) разбирается здесь, а не отдаётся в catch:
   * это ожидаемый ответ с полезной нагрузкой, а не сбой. Заодно так обходится
   * ловушка — `handleResponse` дописывает в тело ошибки поле `status` с HTTP-кодом
   * и затирает им `status: 'draft'` из самого перечня готовности.
   */
  async publishProgram(programId: number): Promise<import('../types/programs').ProgramPublishOutcome> {
    const response = await fetch(`${BASE_URL}/api/master/programs/${programId}/publish`, {
      method: 'POST',
      headers: await this.getHeaders(true),
    });

    if (response.status === 422) {
      const readiness = await response.json() as import('../types/programs').ProgramReadiness;
      return { outcome: 'notReady', readiness };
    }

    const result = await this.handleResponse<import('../types/programs').ProgramPublishResult>(response);
    return { outcome: 'published', result };
  }
}

export const api = new ApiService();
